import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import axios from 'axios';
import {
  Users, Home, DollarSign, Calendar, Church, UserPlus, Menu, X, LogOut, Plus, Edit, Trash2, Search,
  CheckCircle, Clock, Building, CreditCard, FileText, BarChart3, Heart, User, Briefcase,
  Camera, Calculator, Receipt, Wallet, PiggyBank, TrendingUp, TrendingDown, Monitor,
  UserCheck, FileSpreadsheet, BookOpen, ArrowLeftRight, ArrowLeft, Building2, Scale, Link, ExternalLink,
  Clipboard, UserCog, FileCheck, Copy, Landmark, ArrowDownCircle, ArrowUpCircle,
  Gift, HelpCircle, ChevronRight, ChevronLeft, Sparkles, UsersRound, CalendarDays, MapPin, Book,
  Bell, Facebook, Twitter, Instagram, Linkedin, AlertCircle, RefreshCw, Repeat, Upload, Download,
  File, Eye, FolderOpen, ListTodo, CheckSquare, Square, Filter, Phone, Mail, Banknote, CircleDollarSign,
  Send, UserPlus2, LinkIcon, MailPlus, Settings, Shield, Tag, ToggleLeft, Palette, GripVertical, Key, Megaphone,
  Printer, BadgeCheck, QrCode, Share2, Verified, Target, Save
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Context
const AuthContext = createContext();
const useAuth = () => useContext(AuthContext);

// API Setup
const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Check if we're in kiosk mode
const isKioskMode = () => {
  return window.location.pathname === '/kiosk' || window.location.hash === '#/kiosk';
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab, sidebarOpen, setSidebarOpen }) => {
  const { logout, user } = useAuth();
  
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'mypaperwork', label: 'My Paperwork', icon: FileText, highlight: true },
    { id: 'businesscards', label: 'Digital ID Cards', icon: BadgeCheck, highlight: true },
    { id: 'churchdocs', label: 'Church Documents', icon: FolderOpen, highlight: true },
    { id: 'people', label: 'People', icon: Users },
    { id: 'families', label: 'Families', icon: Building },
    { id: 'groups', label: 'Groups', icon: UsersRound },
    { id: 'calendar', label: 'Calendar', icon: CalendarDays },
    { id: 'materials', label: 'Materials', icon: BookOpen },
    { id: 'newsletter', label: 'Newsletter', icon: Mail },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'tithe-reminders', label: 'Tithe Reminders', icon: Heart },
    { id: 'tasks', label: 'Tasks', icon: Clipboard },
    { id: 'loans', label: 'Church Loans', icon: Landmark },
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'donations', label: 'Deposits', icon: Heart },
    { id: 'giving', label: 'Online Giving', icon: Gift },
    { id: 'employees', label: 'Employees', icon: Briefcase },
    { id: 'hr', label: 'HR & Hiring', icon: UserCog },
    { id: 'payroll', label: 'Payroll & W2', icon: DollarSign },
    { id: 'contractors', label: 'Contractors & 1099', icon: Briefcase },
    { id: 'accounting', label: 'Accounting', icon: Calculator },
    { id: 'documents', label: 'HR Documents', icon: FileCheck },
    { id: 'kiosk', label: 'Self Check-In', icon: Monitor },
    { id: 'admin', label: 'Admin Tools', icon: Settings },
  ];

  return (
    <>
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-12 h-12 rounded-lg object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              <div className="w-12 h-12 bg-purple-600 rounded-lg items-center justify-center hidden">
                <Church className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-tight">Holy Light Ministry</h1>
                <p className="text-xs text-gray-500">Soul Center Headquarters</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`sidebar-link w-full ${activeTab === item.id ? 'active' : ''} ${item.highlight ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 text-green-700' : ''}`}
              >
                <item.icon className={`w-5 h-5 ${item.highlight ? 'text-green-600' : ''}`} />
                {item.label}
                {item.highlight && <span className="ml-auto text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">New</span>}
              </button>
            ))}
          </nav>
          <div className="p-4 border-t">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{user?.username}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium w-full">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Alerts/Notifications Component
const AlertsDropdown = ({ setActiveTab }) => {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);

  useEffect(() => { fetchAlerts(); const interval = setInterval(fetchAlerts, 30000); return () => clearInterval(interval); }, []);

  const fetchAlerts = async () => {
    try { const r = await api.get('/api/alerts'); setAlerts(r.data.alerts || []); setUnreadCount(r.data.unread_count || 0); } catch (e) { console.error(e); }
  };

  const markAsRead = async (id) => {
    try { await api.put(`/api/alerts/${id}/read`); fetchAlerts(); } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    try { await api.put('/api/alerts/mark-all-read'); fetchAlerts(); } catch (e) { console.error(e); }
  };

  const handleAlertClick = (alert) => {
    if (alert.status === 'unread') markAsRead(alert.id);
    setSelectedAlert(alert);
  };

  const handleNavigate = (alert) => {
    setIsOpen(false);
    setSelectedAlert(null);
    // Navigate based on alert type
    if (alert.type === 'event_reminder' || alert.type === 'calendar') {
      setActiveTab && setActiveTab('calendar');
    } else if (alert.type === 'birthday' || alert.type === 'new_member') {
      setActiveTab && setActiveTab('people');
    } else if (alert.type === 'new_hire' || alert.type === 'hr') {
      setActiveTab && setActiveTab('hr');
    } else if (alert.type === 'task') {
      setActiveTab && setActiveTab('tasks');
    } else if (alert.type === 'donation' || alert.type === 'giving') {
      setActiveTab && setActiveTab('donations');
    } else if (alert.type === 'document') {
      setActiveTab && setActiveTab('documents');
    } else {
      setActiveTab && setActiveTab('notifications');
    }
  };

  const getPriorityColor = (priority) => priority === 'high' ? 'text-red-500' : priority === 'normal' ? 'text-blue-500' : 'text-gray-500';
  const getPriorityBg = (priority) => priority === 'high' ? 'bg-red-100 text-red-700' : priority === 'normal' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700';
  const getTypeIcon = (type) => {
    if (type === 'welcome' || type === 'new_hire') return <UserPlus className="w-4 h-4" />;
    if (type === 'event_reminder' || type === 'calendar') return <Calendar className="w-4 h-4" />;
    if (type === 'birthday') return <Gift className="w-4 h-4" />;
    if (type === 'task') return <CheckSquare className="w-4 h-4" />;
    if (type === 'document') return <FileText className="w-4 h-4" />;
    return <Bell className="w-4 h-4" />;
  };

  return (
    <div className="relative">
      <button onClick={() => setIsOpen(!isOpen)} className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg" data-testid="alerts-button">
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border z-50">
          <div className="p-3 border-b flex items-center justify-between">
            <h3 className="font-semibold">Notifications</h3>
            <div className="flex gap-2">
              {unreadCount > 0 && <button onClick={markAllRead} className="text-sm text-purple-600 hover:underline">Mark all read</button>}
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
          </div>
          
          {/* Alert Detail View */}
          {selectedAlert ? (
            <div className="p-4">
              <button onClick={() => setSelectedAlert(null)} className="text-sm text-purple-600 hover:underline mb-3 flex items-center gap-1">
                ← Back to list
              </button>
              <div className={`p-4 rounded-lg ${selectedAlert.priority === 'high' ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={getPriorityColor(selectedAlert.priority)}>{getTypeIcon(selectedAlert.type)}</span>
                  <h4 className="font-semibold">{selectedAlert.title}</h4>
                </div>
                <p className="text-gray-700 mb-3">{selectedAlert.message}</p>
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <span>{new Date(selectedAlert.created_at).toLocaleString()}</span>
                  <span className={`px-2 py-1 rounded ${getPriorityBg(selectedAlert.priority)}`}>{selectedAlert.priority}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleNavigate(selectedAlert)} className="flex-1 bg-purple-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-purple-700">
                    Go to Related Section
                  </button>
                  <button onClick={() => { setSelectedAlert(null); setIsOpen(false); }} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">
                    Close
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {alerts.length === 0 ? (
                <p className="text-center py-6 text-gray-500">No notifications</p>
              ) : (
                alerts.slice(0, 10).map((alert) => (
                  <div 
                    key={alert.id} 
                    onClick={() => handleAlertClick(alert)} 
                    className={`p-3 border-b hover:bg-gray-50 cursor-pointer transition-colors ${alert.status === 'unread' ? 'bg-purple-50' : ''}`}
                    data-testid={`alert-item-${alert.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 p-2 rounded-full ${alert.status === 'unread' ? 'bg-purple-100' : 'bg-gray-100'} ${alert.type === 'event_reminder' ? 'text-purple-500' : getPriorityColor(alert.priority)}`}>
                        {getTypeIcon(alert.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{alert.title}</p>
                        <p className="text-xs text-gray-600 line-clamp-2">{alert.message}</p>
                        <p className="text-xs text-gray-400 mt-1">{new Date(alert.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {alert.status === 'unread' && <div className="w-2 h-2 bg-purple-500 rounded-full" />}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          
          {/* View All Button */}
          {!selectedAlert && alerts.length > 0 && (
            <div className="p-3 border-t">
              <button 
                onClick={() => { setActiveTab && setActiveTab('notifications'); setIsOpen(false); }} 
                className="w-full text-center text-sm text-purple-600 hover:underline font-medium"
              >
                View All Notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Header = ({ setSidebarOpen, title, setActiveTab }) => (
  <header className="bg-white shadow-sm px-6 py-4 flex items-center justify-between">
    <div className="flex items-center gap-4">
      <button onClick={() => setSidebarOpen(true)} className="lg:hidden" data-testid="menu-toggle">
        <Menu className="w-6 h-6 text-gray-600" />
      </button>
      <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
    </div>
    <div className="flex items-center gap-2">
      <AlertsDropdown setActiveTab={setActiveTab} />
    </div>
  </header>
);

const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizeClass = size === 'lg' ? 'max-w-2xl' : size === 'xl' ? 'max-w-4xl' : size === 'full' ? 'max-w-6xl' : 'max-w-lg';
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal-content ${sizeClass}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ setActiveTab, onQuickAction }) => {
  const { permissions } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [dailyVerse, setDailyVerse] = useState(null);

  // Collection of Bible verses for daily rotation
  const bibleVerses = [
    { verse: "For I know the plans I have for you, declares the LORD, plans to prosper you and not to harm you, plans to give you hope and a future.", reference: "Jeremiah 29:11" },
    { verse: "Trust in the LORD with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight.", reference: "Proverbs 3:5-6" },
    { verse: "I can do all this through him who gives me strength.", reference: "Philippians 4:13" },
    { verse: "The LORD is my shepherd, I lack nothing.", reference: "Psalm 23:1" },
    { verse: "Be strong and courageous. Do not be afraid; do not be discouraged, for the LORD your God will be with you wherever you go.", reference: "Joshua 1:9" },
    { verse: "And we know that in all things God works for the good of those who love him, who have been called according to his purpose.", reference: "Romans 8:28" },
    { verse: "Come to me, all you who are weary and burdened, and I will give you rest.", reference: "Matthew 11:28" },
    { verse: "But those who hope in the LORD will renew their strength. They will soar on wings like eagles; they will run and not grow weary, they will walk and not be faint.", reference: "Isaiah 40:31" },
    { verse: "The LORD is my light and my salvation—whom shall I fear? The LORD is the stronghold of my life—of whom shall I be afraid?", reference: "Psalm 27:1" },
    { verse: "Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God.", reference: "Philippians 4:6" },
    { verse: "For God so loved the world that he gave his one and only Son, that whoever believes in him shall not perish but have eternal life.", reference: "John 3:16" },
    { verse: "The LORD bless you and keep you; the LORD make his face shine on you and be gracious to you.", reference: "Numbers 6:24-25" },
    { verse: "This is the day the LORD has made; let us rejoice and be glad in it.", reference: "Psalm 118:24" },
    { verse: "Be still, and know that I am God.", reference: "Psalm 46:10" },
    { verse: "Love is patient, love is kind. It does not envy, it does not boast, it is not proud.", reference: "1 Corinthians 13:4" },
    { verse: "The fruit of the Spirit is love, joy, peace, forbearance, kindness, goodness, faithfulness, gentleness and self-control.", reference: "Galatians 5:22-23" },
    { verse: "For where two or three gather in my name, there am I with them.", reference: "Matthew 18:20" },
    { verse: "Cast all your anxiety on him because he cares for you.", reference: "1 Peter 5:7" },
    { verse: "Delight yourself in the LORD, and he will give you the desires of your heart.", reference: "Psalm 37:4" },
    { verse: "Have I not commanded you? Be strong and courageous. Do not be afraid; do not be discouraged, for the LORD your God will be with you wherever you go.", reference: "Joshua 1:9" },
    { verse: "The name of the LORD is a fortified tower; the righteous run to it and are safe.", reference: "Proverbs 18:10" },
    { verse: "But seek first his kingdom and his righteousness, and all these things will be given to you as well.", reference: "Matthew 6:33" },
    { verse: "Create in me a pure heart, O God, and renew a steadfast spirit within me.", reference: "Psalm 51:10" },
    { verse: "He heals the brokenhearted and binds up their wounds.", reference: "Psalm 147:3" },
    { verse: "Therefore, if anyone is in Christ, the new creation has come: The old has gone, the new is here!", reference: "2 Corinthians 5:17" },
    { verse: "Your word is a lamp for my feet, a light on my path.", reference: "Psalm 119:105" },
    { verse: "The LORD is close to the brokenhearted and saves those who are crushed in spirit.", reference: "Psalm 34:18" },
    { verse: "In the beginning was the Word, and the Word was with God, and the Word was God.", reference: "John 1:1" },
    { verse: "Let everything that has breath praise the LORD.", reference: "Psalm 150:6" },
    { verse: "Give thanks to the LORD, for he is good; his love endures forever.", reference: "Psalm 107:1" },
    { verse: "Greater love has no one than this: to lay down one's life for one's friends.", reference: "John 15:13" }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, eventsRes, birthdaysRes] = await Promise.all([
          api.get('/api/dashboard/stats'),
          api.get(`/api/calendar/events?month=${new Date().getMonth() + 1}&year=${new Date().getFullYear()}`),
          api.get('/api/members/birthdays/upcoming?days=30')
        ]);
        setStats(statsRes.data);
        setUpcomingBirthdays(birthdaysRes.data.birthdays || []);
        
        // Get upcoming events (next 7 days)
        const today = new Date();
        const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        const upcoming = (eventsRes.data.events || [])
          .filter(e => {
            const eventDate = new Date(e.start_date);
            return eventDate >= today && eventDate <= nextWeek;
          })
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
          .slice(0, 5);
        setUpcomingEvents(upcoming);
        
        // Get daily verse based on day of year
        const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
        setDailyVerse(bibleVerses[dayOfYear % bibleVerses.length]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getEventTypeColor = (type) => {
    const colors = {
      service: 'bg-purple-500',
      meeting: 'bg-blue-500',
      event: 'bg-green-500',
      holiday: 'bg-red-500',
      birthday: 'bg-pink-500',
      other: 'bg-gray-500'
    };
    return colors[type] || colors.other;
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading...</div>;

  const isAdmin = permissions?.is_admin;

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Daily Bible Verse - Featured at top for all users */}
      {dailyVerse && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white shadow-lg" data-testid="daily-verse">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-200 mb-2">Daily Verse</p>
              <p className="text-lg leading-relaxed mb-3">"{dailyVerse.verse}"</p>
              <p className="text-purple-200 font-semibold">— {dailyVerse.reference}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats - Show for admin */}
      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: 'Total Members', value: stats?.total_members || 0, icon: Users, color: 'bg-blue-500' },
            { label: 'Families', value: stats?.total_families || 0, icon: Building, color: 'bg-purple-500' },
            { label: 'Donations (Month)', value: `$${stats?.donations_this_month?.toLocaleString() || 0}`, icon: Heart, color: 'bg-pink-500' },
            { label: "Today's Check-ins", value: stats?.todays_checkins || 0, icon: UserCheck, color: 'bg-green-500' },
          ].map((stat, index) => (
            <div key={index} className="stat-card">
              <div className={`stat-icon ${stat.color}`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Events Feed - Main column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" /> Upcoming Events
              </h3>
              <button onClick={() => setActiveTab('calendar')} className="text-sm text-purple-600 hover:text-purple-800">
                View Calendar →
              </button>
            </div>
            
            {upcomingEvents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No upcoming events this week</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event, index) => (
                  <div key={event.id || index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors" data-testid={`event-feed-item-${index}`}>
                    <div className="flex flex-col items-center min-w-[60px]">
                      <span className="text-xs text-gray-500 uppercase">{formatEventDate(event.start_date)}</span>
                      <span className="text-2xl font-bold text-gray-900">{new Date(event.start_date).getDate()}</span>
                    </div>
                    <div className={`w-1 self-stretch rounded-full ${getEventTypeColor(event.event_type)}`}></div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{event.title}</h4>
                          <p className="text-sm text-gray-500">
                            {event.start_time && `${event.start_time}`}
                            {event.end_time && ` - ${event.end_time}`}
                          </p>
                        </div>
                        {event.is_recurring && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" /> Recurring
                          </span>
                        )}
                      </div>
                      {event.location && (
                        <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {event.location}
                        </p>
                      )}
                      {event.description && (
                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">{event.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Birthdays */}
          {upcomingBirthdays.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-pink-500" /> Upcoming Birthdays
                </h3>
                <span className="text-sm text-gray-500">{upcomingBirthdays.length} this month</span>
              </div>
              <div className="space-y-3">
                {upcomingBirthdays.slice(0, 5).map((birthday, index) => (
                  <div key={birthday.id} className={`flex items-center gap-4 p-3 rounded-xl ${birthday.is_today ? 'bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200' : 'bg-gray-50'}`} data-testid={`birthday-item-${index}`}>
                    {birthday.photo ? (
                      <img src={birthday.photo} alt={birthday.first_name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {birthday.first_name?.charAt(0)}{birthday.last_name?.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">{birthday.first_name} {birthday.last_name}</h4>
                        {birthday.is_today && (
                          <span className="px-2 py-0.5 bg-pink-500 text-white text-xs rounded-full animate-pulse">
                            🎂 Today!
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {birthday.is_today 
                          ? `Turning ${birthday.turning_age} today!` 
                          : `${birthday.days_until === 1 ? 'Tomorrow' : `In ${birthday.days_until} days`} • Turning ${birthday.turning_age}`
                        }
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(birthday.birthday_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions - For admin only */}
          {isAdmin && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button onClick={() => onQuickAction('addMember')} className="btn-primary flex items-center justify-center gap-2" data-testid="quick-add-member">
                  <UserPlus className="w-4 h-4" /> Add Member
                </button>
                <button onClick={() => onQuickAction('addDonation')} className="btn-secondary flex items-center justify-center gap-2" data-testid="quick-add-donation">
                  <Heart className="w-4 h-4" /> Record Donation
                </button>
                <button onClick={() => onQuickAction('processPayroll')} className="btn-secondary flex items-center justify-center gap-2" data-testid="quick-payroll">
                  <DollarSign className="w-4 h-4" /> Process Payroll
                </button>
                <button onClick={() => onQuickAction('openKiosk')} className="btn-secondary flex items-center justify-center gap-2" data-testid="quick-kiosk">
                  <Monitor className="w-4 h-4" /> Open Kiosk
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar - Financial Summary for Admin / Welcome for Members */}
        <div className="space-y-4">
          {isAdmin ? (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Monthly Donations</span>
                  <span className="font-semibold text-green-600">${stats?.donations_this_month?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Monthly Payroll</span>
                  <span className="font-semibold text-red-600">${stats?.total_payroll_this_month?.toLocaleString() || 0}</span>
                </div>
                <div className="flex items-center justify-between border-t pt-4">
                  <span className="text-gray-800 font-medium">Net</span>
                  <span className={`font-bold ${(stats?.donations_this_month || 0) - (stats?.total_payroll_this_month || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${((stats?.donations_this_month || 0) - (stats?.total_payroll_this_month || 0)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Welcome!</h3>
              <p className="text-gray-600 mb-4">Thank you for being part of our church family. Stay connected with upcoming events and activities.</p>
              <button onClick={() => setActiveTab('giving')} className="btn-primary w-full flex items-center justify-center gap-2">
                <Heart className="w-4 h-4" /> Give Online
              </button>
            </div>
          )}

          {/* Church Info Card */}
          <div className="card bg-gradient-to-br from-purple-50 to-indigo-50">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Church className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900">Holy Light Ministry</h3>
              <p className="text-sm text-gray-600">Soul Center Headquarters</p>
              <p className="text-xs text-purple-600 mt-2">"A Place You Can Call Home"</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Members Component with Photo Support
const Members = ({ autoOpenModal = false }) => {
  const { user, canEdit, canDelete } = useAuth();
  const [members, setMembers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(autoOpenModal);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('members');
  const [filterType, setFilterType] = useState('all'); // all, staff, members_only
  const [inviteStats, setInviteStats] = useState({});
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', address: '', city: '', state: '', zip_code: '',
    birth_date: '', gender: '', membership_date: '', family_id: '', status: 'active', photo: '',
    classification: 'member', facebook: '', twitter: '', instagram: '', linkedin: '',
    // Staff fields
    is_staff: false, position: '', department: '', hire_date: '', salary_type: 'monthly', salary_amount: '',
    work_phone: '', work_email: '', emergency_contact_name: '', emergency_contact_phone: ''
  });
  const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '', phone: '', message: '', invite_type: 'member' });
  const [copiedLink, setCopiedLink] = useState(null);
  const fileInputRef = useRef(null);

  // Check if user can edit this member (admin or own profile)
  const canEditMember = (memberId) => {
    if (canEdit('members')) return true;
    // Users can edit their own profile if linked
    if (user?.member_id === memberId) return true;
    return false;
  };

  useEffect(() => { fetchMembers(); fetchFamilies(); fetchInvites(); }, []);
  useEffect(() => { if (autoOpenModal) setShowModal(true); }, [autoOpenModal]);

  const fetchMembers = async () => {
    try { const r = await api.get('/api/members'); setMembers(r.data.members); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchFamilies = async () => {
    try { const r = await api.get('/api/families'); setFamilies(r.data.families); } catch (e) { console.error(e); }
  };

  const fetchInvites = async () => {
    try { 
      const r = await api.get('/api/invites'); 
      setInvites(r.data.invites);
      const stats = await api.get('/api/invites/stats');
      setInviteStats(stats.data);
    } catch (e) { console.error(e); }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData({ ...formData, photo: reader.result });
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      // Clean up empty strings to null for optional fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });
      // Convert salary to number if staff and has value
      if (submitData.is_staff && submitData.salary_amount) {
        submitData.salary_amount = parseFloat(submitData.salary_amount);
      } else {
        submitData.salary_amount = null;
      }
      if (editMember) await api.put(`/api/members/${editMember.id}`, submitData);
      else await api.post('/api/members', submitData);
      fetchMembers();
      closeModal();
    } catch (error) { console.error(error); alert('Error saving member'); }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post('/api/invites', inviteForm);
      alert(`Invite created! Share this link:\n${window.location.origin}/#/invite/${r.data.invite_code}`);
      fetchInvites();
      setShowInviteModal(false);
      setInviteForm({ email: '', first_name: '', last_name: '', phone: '', message: '', invite_type: 'member' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Error creating invite');
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const r = await api.post(`/api/invites/${inviteId}/resend`);
      alert(`Invite resent! New link:\n${window.location.origin}/#/invite/${r.data.new_code}`);
      fetchInvites();
    } catch (e) { alert('Error resending invite'); }
  };

  const handleCancelInvite = async (inviteId) => {
    if (window.confirm('Cancel this invite?')) {
      try { await api.delete(`/api/invites/${inviteId}`); fetchInvites(); } catch (e) { alert('Error cancelling invite'); }
    }
  };

  const copyInviteLink = (code) => {
    const link = `${window.location.origin}/#/invite/${code}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(code);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this member? This action cannot be undone.')) {
      try { 
        await api.delete(`/api/members/${id}`); 
        fetchMembers(); 
        alert('Member deleted successfully');
      } catch (e) { 
        console.error(e); 
        alert('Failed to delete member: ' + (e.response?.data?.detail || e.message));
      }
    }
  };

  const openModal = (member = null) => {
    if (member) { 
      setEditMember(member); 
      setFormData({
        ...member, 
        classification: member.classification || 'member', 
        facebook: member.facebook || '', 
        twitter: member.twitter || '', 
        instagram: member.instagram || '', 
        linkedin: member.linkedin || '',
        is_staff: member.is_staff || false,
        position: member.position || '',
        department: member.department || '',
        hire_date: member.hire_date || '',
        salary_type: member.salary_type || 'monthly',
        salary_amount: member.salary_amount?.toString() || '',
        work_phone: member.work_phone || '',
        work_email: member.work_email || '',
        emergency_contact_name: member.emergency_contact_name || '',
        emergency_contact_phone: member.emergency_contact_phone || ''
      }); 
    }
    else { 
      setEditMember(null); 
      setFormData({ 
        first_name: '', last_name: '', email: '', phone: '', address: '', city: '', state: '', zip_code: '', 
        birth_date: '', gender: '', membership_date: '', family_id: '', status: 'active', photo: '', 
        classification: 'member', facebook: '', twitter: '', instagram: '', linkedin: '',
        is_staff: false, position: '', department: '', hire_date: '', salary_type: 'monthly', salary_amount: '',
        work_phone: '', work_email: '', emergency_contact_name: '', emergency_contact_phone: ''
      }); 
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditMember(null); };

  // Filter members by search term and filter type
  const filteredMembers = members.filter(m => {
    const matchesSearch = `${m.first_name} ${m.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || 
      (filterType === 'staff' && m.is_staff) || 
      (filterType === 'members_only' && !m.is_staff);
    return matchesSearch && matchesFilter;
  });

  const staffCount = members.filter(m => m.is_staff).length;
  const membersOnlyCount = members.filter(m => !m.is_staff).length;

  const getInviteStatusColor = (status) => {
    const colors = { pending: 'bg-yellow-100 text-yellow-700', accepted: 'bg-green-100 text-green-700', expired: 'bg-gray-100 text-gray-500', cancelled: 'bg-red-100 text-red-700' };
    return colors[status] || colors.pending;
  };

  return (
    <div className="space-y-6" data-testid="members-page">
      {/* Tab Navigation */}
      <div className="flex items-center gap-4 border-b">
        <button onClick={() => setActiveTab('members')} className={`pb-3 px-1 border-b-2 font-medium transition-colors ${activeTab === 'members' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          <Users className="w-4 h-4 inline mr-2" />People ({members.length})
        </button>
        <button onClick={() => setActiveTab('invites')} className={`pb-3 px-1 border-b-2 font-medium transition-colors ${activeTab === 'invites' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          <MailPlus className="w-4 h-4 inline mr-2" />Invites ({inviteStats.pending || 0} pending)
        </button>
      </div>

      {activeTab === 'members' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4">
              <p className="text-purple-100 text-sm">All People</p>
              <p className="text-2xl font-bold">{members.length}</p>
            </div>
            <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4">
              <p className="text-blue-100 text-sm flex items-center gap-1"><Briefcase className="w-3 h-3" /> Staff</p>
              <p className="text-2xl font-bold">{staffCount}</p>
            </div>
            <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white p-4">
              <p className="text-green-100 text-sm">Members Only</p>
              <p className="text-2xl font-bold">{membersOnlyCount}</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input type="text" placeholder="Search people..." className="input-field pl-10 w-full sm:w-64" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <select className="select-field w-40" value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="filter-type">
                <option value="all">All People</option>
                <option value="staff">Staff Only</option>
                <option value="members_only">Members Only</option>
              </select>
            </div>
            <div className="flex gap-2">
              {canEdit('members') && (
                <>
                  <button onClick={() => setShowInviteModal(true)} className="btn-secondary flex items-center gap-2">
                    <Send className="w-4 h-4" /> Send Invite
                  </button>
                  <button onClick={() => openModal()} className="btn-primary flex items-center gap-2" data-testid="add-member-btn">
                    <Plus className="w-4 h-4" /> Add Person
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr><th className="table-header">Photo</th><th className="table-header">Name</th><th className="table-header">Role</th><th className="table-header">Email</th><th className="table-header">Phone</th><th className="table-header">Status</th><th className="table-header">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredMembers.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      {m.photo ? <img src={m.photo} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-purple-600" /></div>}
                    </td>
                    <td className="table-cell font-medium">{m.first_name} {m.last_name}</td>
                    <td className="table-cell">
                      {m.is_staff ? (
                        <div className="flex flex-col">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium w-fit" data-testid={`staff-badge-${m.id}`}><Briefcase className="w-3 h-3" /> Staff</span>
                          {m.position && <span className="text-xs text-gray-500 mt-0.5">{m.position}</span>}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 capitalize">{m.classification || 'Member'}</span>
                      )}
                    </td>
                    <td className="table-cell text-gray-500">{m.email || '-'}</td>
                    <td className="table-cell text-gray-500">{m.phone || '-'}</td>
                    <td className="table-cell"><span className={`badge ${m.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{m.status}</span></td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        {canEditMember(m.id) && (
                          <button onClick={() => openModal(m)} className="text-blue-600 hover:text-blue-800" data-testid={`edit-member-${m.id}`}><Edit className="w-4 h-4" /></button>
                        )}
                        {canDelete('members') && (
                          <button onClick={() => handleDelete(m.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-member-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredMembers.length === 0 && <div className="text-center py-8 text-gray-500">No members found</div>}
          </div>
        </>
      )}

      {activeTab === 'invites' && (
        <>
          {/* Invite Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card bg-gradient-to-br from-yellow-400 to-yellow-500 text-white">
              <p className="text-yellow-100 text-sm">Pending</p>
              <p className="text-2xl font-bold">{inviteStats.pending || 0}</p>
            </div>
            <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white">
              <p className="text-green-100 text-sm">Accepted</p>
              <p className="text-2xl font-bold">{inviteStats.accepted || 0}</p>
            </div>
            <div className="card bg-gradient-to-br from-gray-400 to-gray-500 text-white">
              <p className="text-gray-200 text-sm">Expired</p>
              <p className="text-2xl font-bold">{inviteStats.expired || 0}</p>
            </div>
            <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <p className="text-purple-100 text-sm">Total Sent</p>
              <p className="text-2xl font-bold">{inviteStats.total || 0}</p>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={() => setShowInviteModal(true)} className="btn-primary flex items-center gap-2">
              <Send className="w-4 h-4" /> Send New Invite
            </button>
          </div>

          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Invitee</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Type</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Expires</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invites.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{inv.first_name || '-'} {inv.last_name || ''}</td>
                    <td className="table-cell text-gray-500">{inv.email}</td>
                    <td className="table-cell capitalize">{inv.invite_type}</td>
                    <td className="table-cell"><span className={`px-2 py-1 rounded-full text-xs ${getInviteStatusColor(inv.status)}`}>{inv.status}</span></td>
                    <td className="table-cell text-sm text-gray-500">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : '-'}</td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        <button onClick={() => copyInviteLink(inv.code)} className={`p-1.5 rounded hover:bg-gray-100 ${copiedLink === inv.code ? 'text-green-600' : 'text-gray-400 hover:text-purple-600'}`} title="Copy Link">
                          {copiedLink === inv.code ? <CheckCircle className="w-4 h-4" /> : <LinkIcon className="w-4 h-4" />}
                        </button>
                        {inv.status === 'pending' && (
                          <>
                            <button onClick={() => handleResendInvite(inv.id)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Resend"><RefreshCw className="w-4 h-4" /></button>
                            <button onClick={() => handleCancelInvite(inv.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Cancel"><X className="w-4 h-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {invites.length === 0 && <div className="text-center py-8 text-gray-500">No invites sent yet. Send your first invite!</div>}
          </div>
        </>
      )}

      {/* Add/Edit Member Modal */}
      <Modal isOpen={showModal} onClose={closeModal} title={editMember ? 'Edit Member' : 'Add Member'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex justify-center mb-4">
            <div className="relative">
              {formData.photo ? <img src={formData.photo} alt="" className="w-24 h-24 rounded-full object-cover" /> : <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center"><User className="w-12 h-12 text-purple-600" /></div>}
              <button type="button" onClick={() => fileInputRef.current?.click()} className="absolute bottom-0 right-0 bg-purple-600 text-white p-2 rounded-full hover:bg-purple-700"><Camera className="w-4 h-4" /></button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label><input type="text" className="input-field" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label><input type="text" className="input-field" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" className="input-field" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="text" className="input-field" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Address</label><input type="text" className="input-field" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">City</label><input type="text" className="input-field" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">State</label><input type="text" className="input-field" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Zip</label><input type="text" className="input-field" value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Family</label>
              <select className="select-field" value={formData.family_id} onChange={(e) => setFormData({ ...formData, family_id: e.target.value })}>
                <option value="">Select...</option>{families.map((f) => <option key={f.id} value={f.id}>{f.family_name}</option>)}
              </select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Classification</label>
              <select className="select-field" value={formData.classification} onChange={(e) => setFormData({ ...formData, classification: e.target.value })}>
                <option value="member">Member</option><option value="regular_attendee">Regular Attendee</option><option value="visitor">Visitor</option><option value="staff">Staff</option><option value="volunteer">Volunteer</option>
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select className="select-field" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                <option value="active">Active</option><option value="inactive">Inactive</option>
              </select></div>
          </div>
          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2"><Link className="w-4 h-4" /> Social Media</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2"><Facebook className="w-4 h-4 text-blue-600" /><input type="text" className="input-field flex-1" placeholder="Facebook URL" value={formData.facebook} onChange={(e) => setFormData({ ...formData, facebook: e.target.value })} /></div>
              <div className="flex items-center gap-2"><Twitter className="w-4 h-4 text-sky-500" /><input type="text" className="input-field flex-1" placeholder="Twitter/X handle" value={formData.twitter} onChange={(e) => setFormData({ ...formData, twitter: e.target.value })} /></div>
              <div className="flex items-center gap-2"><Instagram className="w-4 h-4 text-pink-600" /><input type="text" className="input-field flex-1" placeholder="Instagram handle" value={formData.instagram} onChange={(e) => setFormData({ ...formData, instagram: e.target.value })} /></div>
              <div className="flex items-center gap-2"><Linkedin className="w-4 h-4 text-blue-700" /><input type="text" className="input-field flex-1" placeholder="LinkedIn URL" value={formData.linkedin} onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })} /></div>
            </div>
          </div>
          {/* Staff Section */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2"><Briefcase className="w-4 h-4" /> Staff Information</h4>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500" checked={formData.is_staff} onChange={(e) => setFormData({ ...formData, is_staff: e.target.checked })} data-testid="is-staff-toggle" />
                <span className="text-sm font-medium text-gray-700">This person is a staff member</span>
              </label>
            </div>
            {formData.is_staff && (
              <div className="bg-purple-50 p-4 rounded-lg space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Position / Title *</label><input type="text" className="input-field" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} placeholder="e.g., Pastor, Secretary" required={formData.is_staff} data-testid="staff-position" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Department</label><input type="text" className="input-field" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} placeholder="e.g., Administration" data-testid="staff-department" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label><input type="date" className="input-field" value={formData.hire_date} onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })} data-testid="staff-hire-date" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Salary Type</label>
                    <select className="select-field" value={formData.salary_type} onChange={(e) => setFormData({ ...formData, salary_type: e.target.value })} data-testid="staff-salary-type">
                      <option value="hourly">Hourly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Salary Amount</label><input type="number" className="input-field" value={formData.salary_amount} onChange={(e) => setFormData({ ...formData, salary_amount: e.target.value })} placeholder="0.00" step="0.01" data-testid="staff-salary-amount" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Work Phone</label><input type="tel" className="input-field" value={formData.work_phone} onChange={(e) => setFormData({ ...formData, work_phone: e.target.value })} placeholder="Work phone number" data-testid="staff-work-phone" /></div>
                </div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Work Email</label><input type="email" className="input-field" value={formData.work_email} onChange={(e) => setFormData({ ...formData, work_email: e.target.value })} placeholder="work@example.com" data-testid="staff-work-email" /></div>
                <div className="border-t border-purple-200 pt-4 mt-2">
                  <p className="text-sm text-purple-700 mb-3 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Emergency Contact</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label><input type="text" className="input-field" value={formData.emergency_contact_name} onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })} placeholder="Emergency contact name" data-testid="staff-emergency-name" /></div>
                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label><input type="tel" className="input-field" value={formData.emergency_contact_phone} onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })} placeholder="Emergency contact phone" data-testid="staff-emergency-phone" /></div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>

      {/* Send Invite Modal */}
      <Modal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} title="Send Member Invite">
        <form onSubmit={handleInviteSubmit} className="space-y-4">
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-sm text-purple-700"><Send className="w-4 h-4 inline mr-2" />Send an invitation link that allows someone to register as a member. The link expires in 7 days.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <input type="email" className="input-field" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} required placeholder="person@example.com" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">First Name</label><input type="text" className="input-field" value={inviteForm.first_name} onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })} placeholder="Optional" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label><input type="text" className="input-field" value={inviteForm.last_name} onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })} placeholder="Optional" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="tel" className="input-field" value={inviteForm.phone} onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })} placeholder="Optional" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Invite Type</label>
              <select className="select-field" value={inviteForm.invite_type} onChange={(e) => setInviteForm({ ...inviteForm, invite_type: e.target.value })}>
                <option value="member">Member</option>
                <option value="visitor">Visitor</option>
                <option value="volunteer">Volunteer</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Personal Message</label>
            <textarea className="input-field" rows="3" value={inviteForm.message} onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })} placeholder="Add a personal message that will be shown on the invite page..." />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowInviteModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary flex items-center gap-2"><Send className="w-4 h-4" /> Send Invite</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Families Component
const Families = () => {
  const { canEdit, canDelete } = useAuth();
  const [families, setFamilies] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [editFamily, setEditFamily] = useState(null);
  const [formData, setFormData] = useState({ family_name: '', phone: '', email: '', address: '', photo: '' });
  const [newMemberData, setNewMemberData] = useState({ first_name: '', last_name: '', email: '', phone: '', relationship: '' });
  const [memberSearch, setMemberSearch] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { fetchFamilies(); fetchAllMembers(); }, []);

  const fetchFamilies = async () => { try { const r = await api.get('/api/families'); setFamilies(r.data.families); } catch (e) { console.error(e); } };
  const fetchAllMembers = async () => { try { const r = await api.get('/api/members'); setAllMembers(r.data.members); } catch (e) { console.error(e); } };
  const fetchFamilyMembers = async (familyId) => { try { const r = await api.get(`/api/families/${familyId}/members`); setFamilyMembers(r.data.members); } catch (e) { console.error(e); } };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData({ ...formData, photo: reader.result });
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try { 
      if (editFamily) {
        await api.put(`/api/families/${editFamily.id}`, formData);
      } else {
        await api.post('/api/families', formData); 
      }
      fetchFamilies(); 
      closeModal();
    } catch (e) { console.error(e); alert('Error saving family'); }
  };

  const handleDelete = async (id) => { 
    if (window.confirm('Are you sure you want to delete this family? All family members will be unlinked.')) { 
      try { 
        await api.delete(`/api/families/${id}`); 
        fetchFamilies(); 
        alert('Family deleted successfully');
      } catch (e) { console.error(e); alert('Failed to delete family'); } 
    } 
  };

  const handleAddExistingMember = async (memberId) => {
    try {
      await api.post(`/api/families/${selectedFamily.id}/members?member_id=${memberId}`);
      fetchFamilyMembers(selectedFamily.id);
      fetchFamilies();
      fetchAllMembers();
      alert('Member added to family!');
    } catch (e) { alert(e.response?.data?.detail || 'Error adding member'); }
  };

  const handleRemoveMember = async (memberId) => {
    if (window.confirm('Remove this member from the family?')) {
      try {
        await api.delete(`/api/families/${selectedFamily.id}/members/${memberId}`);
        fetchFamilyMembers(selectedFamily.id);
        fetchFamilies();
        fetchAllMembers();
      } catch (e) { alert('Error removing member'); }
    }
  };

  const handleCreateNewMember = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/families/${selectedFamily.id}/members/new`, newMemberData);
      fetchFamilyMembers(selectedFamily.id);
      fetchFamilies();
      fetchAllMembers();
      setShowAddMemberModal(false);
      setNewMemberData({ first_name: '', last_name: '', email: '', phone: '', relationship: '' });
      alert('New member created and added to family!');
    } catch (e) { alert(e.response?.data?.detail || 'Error creating member'); }
  };

  const openModal = (family = null) => {
    if (family) {
      setEditFamily(family);
      setFormData({ family_name: family.family_name || '', phone: family.phone || '', email: family.email || '', address: family.address || '', photo: family.photo || '' });
    } else {
      setEditFamily(null);
      setFormData({ family_name: '', phone: '', email: '', address: '', photo: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditFamily(null); setFormData({ family_name: '', phone: '', email: '', address: '', photo: '' }); };

  const openMembersModal = (family) => {
    setSelectedFamily(family);
    fetchFamilyMembers(family.id);
    setShowMembersModal(true);
  };

  // Filter available members (not already in this family)
  const availableMembers = allMembers.filter(m => 
    m.family_id !== selectedFamily?.id && 
    (`${m.first_name} ${m.last_name}`.toLowerCase().includes(memberSearch.toLowerCase()) || 
     (m.email && m.email.toLowerCase().includes(memberSearch.toLowerCase())))
  );

  return (
    <div className="space-y-6" data-testid="families-page">
      <div className="flex justify-end">
        {canEdit('families') && (
          <button onClick={() => openModal()} className="btn-primary flex items-center gap-2" data-testid="add-family-btn"><Plus className="w-4 h-4" /> Add Family</button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {families.map((f) => (
          <div key={f.id} className="card hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                {f.photo ? (
                  <img src={f.photo} alt={f.family_name} className="w-16 h-16 rounded-xl object-cover" />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{f.family_name}</h3>
                  {f.phone && <p className="text-sm text-gray-500">{f.phone}</p>}
                  {f.email && <p className="text-sm text-gray-500">{f.email}</p>}
                </div>
              </div>
            </div>
            {f.address && <p className="text-sm text-gray-600 mb-3"><span className="font-medium">Address:</span> {f.address}</p>}
            <div className="flex items-center justify-between pt-3 border-t">
              <button onClick={() => openMembersModal(f)} className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1" data-testid={`manage-members-${f.id}`}>
                <Users className="w-4 h-4" /> {f.member_count || 0} members
              </button>
              <div className="flex gap-2">
                {canEdit('families') && (
                  <button onClick={() => openModal(f)} className="text-blue-600 hover:text-blue-800" data-testid={`edit-family-${f.id}`}><Edit className="w-4 h-4" /></button>
                )}
                {canDelete('families') && (
                  <button onClick={() => handleDelete(f.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-family-${f.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {families.length === 0 && <div className="text-center py-12 text-gray-500">No families added yet. Click "Add Family" to create one.</div>}
      
      {/* Add/Edit Family Modal */}
      <Modal isOpen={showModal} onClose={closeModal} title={editFamily ? 'Edit Family' : 'Add Family'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex justify-center mb-4">
            <div className="relative">
              {formData.photo ? (
                <img src={formData.photo} alt="Family" className="w-24 h-24 rounded-xl object-cover" />
              ) : (
                <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
                  <Users className="w-10 h-10 text-white" />
                </div>
              )}
              <button type="button" onClick={() => fileInputRef.current?.click()} className="absolute -bottom-2 -right-2 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white hover:bg-purple-700">
                <Camera className="w-4 h-4" />
              </button>
              <input type="file" ref={fileInputRef} onChange={handlePhotoUpload} accept="image/*" className="hidden" />
            </div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Family Name *</label><input type="text" className="input-field" value={formData.family_name} onChange={(e) => setFormData({ ...formData, family_name: e.target.value })} required data-testid="family-name-input" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="text" className="input-field" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" className="input-field" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Address</label><input type="text" className="input-field" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="123 Main St, City, State" /></div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={closeModal} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary" data-testid="save-family-btn">{editFamily ? 'Update' : 'Save'}</button></div>
        </form>
      </Modal>

      {/* Manage Family Members Modal */}
      <Modal isOpen={showMembersModal} onClose={() => { setShowMembersModal(false); setMemberSearch(''); }} title={`${selectedFamily?.family_name || ''} - Family Members`}>
        <div className="space-y-4">
          {/* Current Family Members */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-gray-900">Current Members ({familyMembers.length})</h4>
              <button onClick={() => setShowAddMemberModal(true)} className="btn-primary text-sm flex items-center gap-1" data-testid="add-new-member-btn">
                <UserPlus className="w-4 h-4" /> Add New Member
              </button>
            </div>
            {familyMembers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No members in this family yet.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {familyMembers.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {m.photo ? (
                        <img src={m.photo} alt={m.first_name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-purple-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{m.first_name} {m.last_name}</p>
                        <p className="text-xs text-gray-500">{m.email || m.phone || 'No contact info'}</p>
                      </div>
                    </div>
                    <button onClick={() => handleRemoveMember(m.id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`remove-member-${m.id}`}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add Existing Members */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Add Existing Members</h4>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search members by name or email..." 
                className="input-field pl-10" 
                value={memberSearch} 
                onChange={(e) => setMemberSearch(e.target.value)}
                data-testid="search-members-input"
              />
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {availableMembers.length === 0 ? (
                <p className="text-gray-500 text-center py-4 text-sm">
                  {memberSearch ? 'No matching members found.' : 'All members are already assigned to families or no members exist.'}
                </p>
              ) : (
                availableMembers.slice(0, 10).map(m => (
                  <div key={m.id} className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      {m.photo ? (
                        <img src={m.photo} alt={m.first_name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{m.first_name} {m.last_name}</p>
                        <p className="text-xs text-gray-500">{m.email || m.phone || 'No contact info'}</p>
                      </div>
                    </div>
                    <button onClick={() => handleAddExistingMember(m.id)} className="btn-secondary text-sm" data-testid={`add-existing-member-${m.id}`}>
                      Add
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Create New Member Modal */}
      <Modal isOpen={showAddMemberModal} onClose={() => { setShowAddMemberModal(false); setNewMemberData({ first_name: '', last_name: '', email: '', phone: '', relationship: '' }); }} title="Add New Family Member">
        <form onSubmit={handleCreateNewMember} className="space-y-4">
          <p className="text-sm text-gray-600 mb-4">Create a new member and add them directly to the {selectedFamily?.family_name} family.</p>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label><input type="text" className="input-field" value={newMemberData.first_name} onChange={(e) => setNewMemberData({ ...newMemberData, first_name: e.target.value })} required data-testid="new-member-first-name" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label><input type="text" className="input-field" value={newMemberData.last_name} onChange={(e) => setNewMemberData({ ...newMemberData, last_name: e.target.value })} required data-testid="new-member-last-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" className="input-field" value={newMemberData.email} onChange={(e) => setNewMemberData({ ...newMemberData, email: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="text" className="input-field" value={newMemberData.phone} onChange={(e) => setNewMemberData({ ...newMemberData, phone: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowAddMemberModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="create-new-member-btn">Create & Add</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Groups Component (Ministry Teams, Small Groups, etc.)
const Groups = () => {
  const { canEdit, canDelete } = useAuth();
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [editGroup, setEditGroup] = useState(null);
  const [formData, setFormData] = useState({ name: '', group_type: 'ministry', description: '', leader_id: '', meeting_schedule: '' });

  useEffect(() => { fetchGroups(); fetchMembers(); }, []);

  const fetchGroups = async () => { try { const r = await api.get('/api/groups'); setGroups(r.data.groups); } catch (e) { console.error(e); } };
  const fetchMembers = async () => { try { const r = await api.get('/api/members'); setMembers(r.data.members); } catch (e) { console.error(e); } };
  const fetchGroupMembers = async (groupId) => { try { const r = await api.get(`/api/groups/${groupId}/members`); setGroupMembers(r.data.members); } catch (e) { console.error(e); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editGroup) await api.put(`/api/groups/${editGroup.id}`, formData);
      else await api.post('/api/groups', formData);
      fetchGroups(); closeModal();
    } catch (e) { console.error(e); alert('Error saving group'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this group? All members will be removed.')) {
      try { await api.delete(`/api/groups/${id}`); fetchGroups(); } catch (e) { alert('Failed to delete'); }
    }
  };

  const handleAddMember = async (memberId) => {
    try { await api.post(`/api/groups/${selectedGroup.id}/members?member_id=${memberId}`); fetchGroupMembers(selectedGroup.id); fetchGroups(); } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleRemoveMember = async (memberId) => {
    try { await api.delete(`/api/groups/${selectedGroup.id}/members/${memberId}`); fetchGroupMembers(selectedGroup.id); fetchGroups(); } catch (e) { alert('Error removing member'); }
  };

  const openModal = (group = null) => {
    if (group) { setEditGroup(group); setFormData({ name: group.name, group_type: group.group_type, description: group.description || '', leader_id: group.leader_id || '', meeting_schedule: group.meeting_schedule || '' }); }
    else { setEditGroup(null); setFormData({ name: '', group_type: 'ministry', description: '', leader_id: '', meeting_schedule: '' }); }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditGroup(null); };

  const openMembersModal = (group) => { setSelectedGroup(group); fetchGroupMembers(group.id); setShowMembersModal(true); };

  const groupTypeColors = { ministry: 'bg-purple-100 text-purple-800', small_group: 'bg-blue-100 text-blue-800', committee: 'bg-green-100 text-green-800', sunday_school: 'bg-orange-100 text-orange-800' };

  return (
    <div className="space-y-6" data-testid="groups-page">
      <div className="flex justify-between items-center">
        <div className="flex gap-4">
          <div className="card py-3 px-4 flex items-center gap-3"><UsersRound className="w-5 h-5 text-purple-600" /><div><p className="text-2xl font-bold">{groups.length}</p><p className="text-xs text-gray-500">Total Groups</p></div></div>
          <div className="card py-3 px-4 flex items-center gap-3"><Users className="w-5 h-5 text-blue-600" /><div><p className="text-2xl font-bold">{groups.reduce((s, g) => s + (g.member_count || 0), 0)}</p><p className="text-xs text-gray-500">Total Members</p></div></div>
        </div>
        {canEdit('groups') && (
          <button onClick={() => openModal()} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Add Group</button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((g) => (
          <div key={g.id} className="card hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-lg text-gray-900">{g.name}</h3>
                <span className={`badge text-xs ${groupTypeColors[g.group_type] || 'bg-gray-100'}`}>{g.group_type?.replace('_', ' ')}</span>
              </div>
              <div className="flex gap-1">
                {canEdit('groups') && (
                  <button onClick={() => openModal(g)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
                )}
                {canDelete('groups') && (
                  <button onClick={() => handleDelete(g.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
            {g.description && <p className="text-sm text-gray-600 mb-3">{g.description}</p>}
            {g.leader && <p className="text-sm text-gray-500 mb-2"><span className="font-medium">Leader:</span> {g.leader.first_name} {g.leader.last_name}</p>}
            {g.meeting_schedule && <p className="text-sm text-gray-500 mb-3"><Clock className="w-3 h-3 inline mr-1" />{g.meeting_schedule}</p>}
            <div className="flex justify-between items-center pt-3 border-t">
              <span className="text-sm font-medium text-gray-700">{g.member_count || 0} members</span>
              <button onClick={() => openMembersModal(g)} className="text-purple-600 text-sm hover:underline">Manage Members</button>
            </div>
          </div>
        ))}
      </div>
      {groups.length === 0 && <div className="text-center py-12 text-gray-500">No groups yet. Create your first group!</div>}

      <Modal isOpen={showModal} onClose={closeModal} title={editGroup ? 'Edit Group' : 'Add Group'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label><input type="text" className="input-field" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select className="select-field" value={formData.group_type} onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}>
              <option value="ministry">Ministry Team</option><option value="small_group">Small Group</option><option value="committee">Committee</option><option value="sunday_school">Sunday School</option>
            </select>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><textarea className="input-field" rows="2" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Leader</label>
            <select className="select-field" value={formData.leader_id} onChange={(e) => setFormData({ ...formData, leader_id: e.target.value })}>
              <option value="">Select a leader</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
            </select>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Meeting Schedule</label><input type="text" className="input-field" placeholder="e.g. Wednesdays at 7pm" value={formData.meeting_schedule} onChange={(e) => setFormData({ ...formData, meeting_schedule: e.target.value })} /></div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={closeModal} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">{editGroup ? 'Update' : 'Create'}</button></div>
        </form>
      </Modal>

      <Modal isOpen={showMembersModal} onClose={() => setShowMembersModal(false)} title={`Members - ${selectedGroup?.name}`} size="lg">
        <div className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Add Member</label>
            <select className="select-field" onChange={(e) => { if (e.target.value) { handleAddMember(e.target.value); e.target.value = ''; } }}>
              <option value="">Select member to add...</option>
              {members.filter(m => !groupMembers.find(gm => gm.member_id === m.id)).map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
            </select>
          </div>
          <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
            {groupMembers.map((gm) => (
              <div key={gm.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  {gm.member?.photo ? <img src={gm.member.photo} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-purple-600" /></div>}
                  <div><p className="font-medium">{gm.member?.first_name} {gm.member?.last_name}</p><p className="text-xs text-gray-500">{gm.role}</p></div>
                </div>
                <button onClick={() => handleRemoveMember(gm.member_id)} className="text-red-600 hover:text-red-800"><X className="w-4 h-4" /></button>
              </div>
            ))}
            {groupMembers.length === 0 && <p className="text-center py-4 text-gray-500">No members in this group</p>}
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Calendar Component
const CalendarView = () => {
  const { canEdit } = useAuth();
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [selectedEventForReminder, setSelectedEventForReminder] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [formData, setFormData] = useState({ title: '', event_type: 'general', start_date: '', end_date: '', start_time: '', end_time: '', location: '', description: '', is_recurring: false, recurrence_pattern: '', recurrence_end_date: '', monthly_week: '1', monthly_day: '0' });

  useEffect(() => { fetchEvents(); }, [currentDate]);

  const fetchEvents = async () => {
    try {
      const r = await api.get(`/api/calendar/events?month=${currentDate.getMonth() + 1}&year=${currentDate.getFullYear()}`);
      setEvents(r.data.events);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Don't allow editing recurring instances, edit the parent instead
      if (editEvent?.is_instance) {
        alert('To edit a recurring event, please edit the original event.');
        return;
      }
      if (editEvent) await api.put(`/api/calendar/events/${editEvent.id}`, formData);
      else await api.post('/api/calendar/events', formData);
      fetchEvents(); closeModal();
    } catch (e) { console.error(e); alert('Error saving event'); }
  };

  const handleDelete = async (id) => {
    // Check if it's a recurring instance
    if (id.includes('__')) {
      alert('To delete a recurring event, please delete the original event from its first occurrence.');
      return;
    }
    if (window.confirm('Delete this event? If it\'s recurring, all instances will be deleted.')) {
      try { await api.delete(`/api/calendar/events/${id}`); fetchEvents(); } catch (e) { alert('Failed to delete'); }
    }
  };

  const handleSendReminder = async (event, reminderType = '1_day') => {
    const eventId = event.parent_event_id || event.id;
    if (eventId.includes('__')) return; // Don't send for instances
    
    setSendingReminder(true);
    try {
      // Send in-app notifications
      await api.post(`/api/events/${eventId}/notify`);
      // Send email reminders
      const result = await api.post(`/api/events/${eventId}/send-email-reminder?reminder_type=${reminderType}`);
      alert(`Reminder sent! ${result.data.emails_sent} emails sent for "${event.title}"`);
    } catch (e) {
      console.error(e);
      alert('Error sending reminder');
    } finally {
      setSendingReminder(false);
      setSelectedEventForReminder(null);
    }
  };

  const openModal = (date = null, event = null) => {
    if (event) {
      setEditEvent(event);
      setFormData({ 
        title: event.title, 
        event_type: event.event_type, 
        start_date: event.start_date, 
        end_date: event.end_date || '', 
        start_time: event.start_time || '', 
        end_time: event.end_time || '', 
        location: event.location || '', 
        description: event.description || '',
        is_recurring: event.is_recurring || false,
        recurrence_pattern: event.recurrence_pattern || '',
        recurrence_end_date: event.recurrence_end_date || '',
        monthly_week: event.monthly_week || '1',
        monthly_day: event.monthly_day || '0'
      });
    } else {
      setEditEvent(null);
      const dateStr = date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      setFormData({ title: '', event_type: 'general', start_date: dateStr, end_date: '', start_time: '', end_time: '', location: '', description: '', is_recurring: false, recurrence_pattern: '', recurrence_end_date: '', monthly_week: '1', monthly_day: '0' });
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditEvent(null); };

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const getEventsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.start_date === dateStr);
  };

  // Comprehensive Pastoral Service Event Types with Colors
  const eventTypeColors = { 
    // Weekly Pastoral Services
    sunday_worship: 'bg-purple-600',
    bible_study: 'bg-green-600',
    midweek_service: 'bg-green-500',
    baptism: 'bg-cyan-500',
    pastoral_counseling: 'bg-teal-500',
    hospital_visit: 'bg-orange-500',
    home_visit: 'bg-orange-400',
    prayer_meeting: 'bg-blue-500',
    staff_meeting: 'bg-slate-600',
    leadership_meeting: 'bg-slate-500',
    
    // Seasonal & Holiday Events
    easter_service: 'bg-yellow-500',
    easter_egg_hunt: 'bg-yellow-400',
    easter_breakfast: 'bg-yellow-300',
    christmas_service: 'bg-red-600',
    candlelight_service: 'bg-red-500',
    christmas_caroling: 'bg-red-400',
    live_nativity: 'bg-red-300',
    vbs: 'bg-pink-500',
    pastor_appreciation: 'bg-amber-500',
    
    // Fellowship & Community Outreach
    church_picnic: 'bg-lime-500',
    church_bbq: 'bg-lime-600',
    service_project: 'bg-emerald-500',
    food_pantry: 'bg-emerald-600',
    soup_kitchen: 'bg-emerald-400',
    mens_event: 'bg-blue-700',
    womens_event: 'bg-pink-600',
    mens_breakfast: 'bg-blue-600',
    womens_breakfast: 'bg-pink-400',
    sports_night: 'bg-indigo-500',
    youth_service: 'bg-pink-500',
    
    // Administrative & Strategic
    wedding: 'bg-rose-500',
    funeral: 'bg-gray-700',
    budget_meeting: 'bg-slate-400',
    strategy_planning: 'bg-slate-500',
    sabbatical: 'bg-violet-400',
    
    // Special Event Formats
    testimony_sunday: 'bg-amber-600',
    harvest_event: 'bg-orange-600',
    friend_day: 'bg-sky-500',
    wild_game_dinner: 'bg-amber-700',
    night_to_shine: 'bg-fuchsia-500',
    communion: 'bg-amber-500',
    choir_rehearsal: 'bg-indigo-400',
    
    // General
    general: 'bg-gray-500', 
    meeting: 'bg-blue-400', 
    class: 'bg-green-400', 
    special: 'bg-orange-400',
    outreach: 'bg-emerald-500'
  };

  const eventTypeLabels = {
    // Weekly Pastoral Services
    sunday_worship: 'Sunday Worship Service',
    bible_study: 'Bible Study',
    midweek_service: 'Midweek Service',
    baptism: 'Baptism Service',
    pastoral_counseling: 'Pastoral Counseling',
    hospital_visit: 'Hospital Visit',
    home_visit: 'Home Visit',
    prayer_meeting: 'Prayer Meeting',
    staff_meeting: 'Staff Meeting',
    leadership_meeting: 'Leadership Meeting',
    
    // Seasonal & Holiday Events
    easter_service: 'Easter Service',
    easter_egg_hunt: 'Easter Egg Hunt',
    easter_breakfast: 'Easter Breakfast',
    christmas_service: 'Christmas Service',
    candlelight_service: 'Candlelight Service',
    christmas_caroling: 'Christmas Caroling',
    live_nativity: 'Live Nativity Play',
    vbs: 'Vacation Bible School (VBS)',
    pastor_appreciation: 'Pastor Appreciation',
    
    // Fellowship & Community Outreach
    church_picnic: 'Church Picnic',
    church_bbq: 'Church BBQ',
    service_project: 'Service Project Saturday',
    food_pantry: 'Food Pantry',
    soup_kitchen: 'Soup Kitchen',
    mens_event: "Men's Event",
    womens_event: "Women's Event",
    mens_breakfast: "Men's Breakfast",
    womens_breakfast: "Women's Breakfast",
    sports_night: 'Sports/Drop-in Night',
    youth_service: 'Youth Service',
    
    // Administrative & Strategic
    wedding: 'Wedding',
    funeral: 'Funeral Service',
    budget_meeting: 'Budget Meeting',
    strategy_planning: 'Strategy Planning',
    sabbatical: 'Sabbatical',
    
    // Special Event Formats
    testimony_sunday: 'Testimony Sunday',
    harvest_event: 'Harvest Event',
    friend_day: 'Friend Day',
    wild_game_dinner: 'Wild Game Dinner',
    night_to_shine: 'Night to Shine',
    communion: "Communion/Lord's Supper",
    choir_rehearsal: 'Choir Rehearsal',
    
    // General
    general: 'General',
    meeting: 'Meeting',
    class: 'Class',
    special: 'Special Event',
    outreach: 'Community Outreach'
  };

  // Filter state for calendar view
  const [filterType, setFilterType] = useState('all');
  const [showPrintView, setShowPrintView] = useState(false);

  const filteredEvents = filterType === 'all' 
    ? events 
    : events.filter(e => e.event_type === filterType);

  // Flyer Generator State
  const [showFlyerGenerator, setShowFlyerGenerator] = useState(false);
  const [flyerConfig, setFlyerConfig] = useState({
    // Header Settings
    churchName: 'Holy Light Ministry Soul Center Headquarters',
    tagline: 'Christ The King Church And Ministry',
    headerColor: '#7c3aed',
    showLogo: true,
    
    // Footer Settings
    address: '805 S 1st St., Slaton, TX 79364',
    phone: '(770) 638-3444',
    email: 'info@holylightministry.org',
    website: 'www.holylightministry.org',
    pastorName: 'Bishop Dr. Michael D Turner, Senior Pastor',
    
    // Content Settings
    announcements: [
      { title: 'Welcome New Members', text: 'Join us in welcoming our newest members to the church family!' },
      { title: 'Building Fund Update', text: 'Thank you for your generous contributions to our building fund.' }
    ],
    includeEvents: true,
    selectedEventTypes: ['sunday_worship', 'bible_study', 'prayer_meeting', 'special'],
    customMessage: '',
    
    // Style Settings
    accentColor: '#7c3aed',
    fontStyle: 'classic' // classic, modern, elegant
  });
  const [sendingFlyer, setSendingFlyer] = useState(false);

  const handleAddAnnouncement = () => {
    setFlyerConfig({
      ...flyerConfig,
      announcements: [...flyerConfig.announcements, { title: '', text: '' }]
    });
  };

  const handleRemoveAnnouncement = (index) => {
    setFlyerConfig({
      ...flyerConfig,
      announcements: flyerConfig.announcements.filter((_, i) => i !== index)
    });
  };

  const handleUpdateAnnouncement = (index, field, value) => {
    const updated = [...flyerConfig.announcements];
    updated[index][field] = value;
    setFlyerConfig({ ...flyerConfig, announcements: updated });
  };

  const handleEmailFlyer = async () => {
    setSendingFlyer(true);
    try {
      await api.post('/api/flyer/send', {
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
        config: flyerConfig,
        events: events.filter(e => flyerConfig.selectedEventTypes.includes(e.event_type) || flyerConfig.selectedEventTypes.includes('all'))
      });
      alert('Monthly flyer sent to all members with email addresses!');
    } catch (e) {
      console.error(e);
      alert('Failed to send flyer. Please try again.');
    } finally {
      setSendingFlyer(false);
    }
  };

  const handlePrintFlyer = () => {
    const printWindow = window.open('', '_blank');
    const flyerHtml = generateFlyerHTML();
    printWindow.document.write(flyerHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const generateFlyerHTML = () => {
    const monthEvents = events
      .filter(e => flyerConfig.selectedEventTypes.includes(e.event_type) || flyerConfig.selectedEventTypes.includes('all'))
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    
    const fontFamily = flyerConfig.fontStyle === 'modern' 
      ? "'Inter', sans-serif" 
      : flyerConfig.fontStyle === 'elegant' 
        ? "'Georgia', serif" 
        : "'Times New Roman', serif";

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${flyerConfig.churchName} - ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()} Newsletter</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: ${fontFamily}; background: white; color: #1f2937; }
          .flyer { max-width: 8.5in; margin: 0 auto; padding: 0.5in; }
          .header { background: ${flyerConfig.headerColor}; color: white; padding: 30px; text-align: center; border-radius: 12px 12px 0 0; }
          .header h1 { font-size: 28px; margin-bottom: 8px; }
          .header p { font-size: 16px; opacity: 0.9; }
          .month-banner { background: linear-gradient(135deg, ${flyerConfig.accentColor}, ${flyerConfig.headerColor}); color: white; padding: 20px; text-align: center; }
          .month-banner h2 { font-size: 24px; }
          .content { padding: 30px; background: #fafafa; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 18px; font-weight: bold; color: ${flyerConfig.accentColor}; border-bottom: 2px solid ${flyerConfig.accentColor}; padding-bottom: 8px; margin-bottom: 15px; }
          .announcement { background: white; padding: 15px; border-radius: 8px; margin-bottom: 12px; border-left: 4px solid ${flyerConfig.accentColor}; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
          .announcement h4 { color: ${flyerConfig.accentColor}; margin-bottom: 5px; }
          .event { display: flex; gap: 15px; padding: 12px; background: white; border-radius: 8px; margin-bottom: 10px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
          .event-date { background: ${flyerConfig.accentColor}; color: white; padding: 10px 15px; border-radius: 8px; text-align: center; min-width: 70px; }
          .event-date .day { font-size: 24px; font-weight: bold; }
          .event-date .month { font-size: 12px; text-transform: uppercase; }
          .event-details h4 { color: #1f2937; margin-bottom: 4px; }
          .event-details p { color: #6b7280; font-size: 14px; }
          .custom-message { background: linear-gradient(135deg, #fef3c7, #fde68a); padding: 20px; border-radius: 8px; text-align: center; margin-top: 20px; }
          .footer { background: #1f2937; color: white; padding: 25px; text-align: center; border-radius: 0 0 12px 12px; }
          .footer-info { margin-bottom: 10px; }
          .footer-info p { margin: 5px 0; font-size: 14px; }
          .pastor-name { font-style: italic; margin-top: 15px; font-size: 16px; }
          @media print { 
            .flyer { padding: 0; } 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="flyer">
          <div class="header">
            <h1>${flyerConfig.churchName}</h1>
            <p>${flyerConfig.tagline}</p>
          </div>
          
          <div class="month-banner">
            <h2>${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()} Newsletter</h2>
          </div>
          
          <div class="content">
            ${flyerConfig.announcements.length > 0 ? `
              <div class="section">
                <h3 class="section-title">📢 Announcements</h3>
                ${flyerConfig.announcements.map(a => `
                  <div class="announcement">
                    <h4>${a.title}</h4>
                    <p>${a.text}</p>
                  </div>
                `).join('')}
              </div>
            ` : ''}
            
            ${flyerConfig.includeEvents && monthEvents.length > 0 ? `
              <div class="section">
                <h3 class="section-title">📅 Upcoming Events</h3>
                ${monthEvents.slice(0, 10).map(ev => {
                  const date = new Date(ev.start_date);
                  return `
                    <div class="event">
                      <div class="event-date">
                        <div class="day">${date.getDate()}</div>
                        <div class="month">${monthNames[date.getMonth()].slice(0, 3)}</div>
                      </div>
                      <div class="event-details">
                        <h4>${ev.title}</h4>
                        <p>${ev.start_time ? ev.start_time + ' • ' : ''}${ev.location || 'Main Sanctuary'}${ev.description ? ' • ' + ev.description : ''}</p>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            ` : ''}
            
            ${flyerConfig.customMessage ? `
              <div class="custom-message">
                <p>${flyerConfig.customMessage}</p>
              </div>
            ` : ''}
          </div>
          
          <div class="footer">
            <div class="footer-info">
              <p><strong>${flyerConfig.churchName}</strong></p>
              <p>${flyerConfig.address}</p>
              <p>📞 ${flyerConfig.phone} | ✉️ ${flyerConfig.email}</p>
              <p>🌐 ${flyerConfig.website}</p>
            </div>
            <p class="pastor-name">${flyerConfig.pastorName}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Pastoral Services Calendar Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Church className="w-7 h-7" />
              Holy Light Ministry Soul Center Headquarters Calendar
            </h1>
            <p className="text-purple-200 text-sm mt-1">Pastoral Services Church Planner</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button 
              onClick={() => setShowFlyerGenerator(true)} 
              className="bg-amber-500 hover:bg-amber-600 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium"
              data-testid="create-flyer-btn"
            >
              <FileText className="w-4 h-4" /> Create Flyer
            </button>
            <button 
              onClick={() => setShowPrintView(true)} 
              className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              data-testid="print-calendar-btn"
            >
              <Printer className="w-4 h-4" /> Print Calendar
            </button>
            <button onClick={() => openModal()} className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-purple-50 transition-colors">
              <Plus className="w-4 h-4" /> Add Event
            </button>
          </div>
        </div>
      </div>

      {/* Filter and Navigation Bar */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-4">
          <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
          <h2 className="text-xl font-semibold">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
          <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronRight className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center gap-3">
          <select 
            className="select-field bg-white border border-gray-200 text-sm" 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            data-testid="filter-event-type"
          >
            <option value="all">All Event Types</option>
            <optgroup label="Weekly Pastoral Services">
              <option value="sunday_worship">Sunday Worship Service</option>
              <option value="bible_study">Bible Study</option>
              <option value="midweek_service">Midweek Service</option>
              <option value="baptism">Baptism Service</option>
              <option value="pastoral_counseling">Pastoral Counseling</option>
              <option value="hospital_visit">Hospital Visit</option>
              <option value="home_visit">Home Visit</option>
              <option value="prayer_meeting">Prayer Meeting</option>
              <option value="staff_meeting">Staff Meeting</option>
              <option value="leadership_meeting">Leadership Meeting</option>
            </optgroup>
            <optgroup label="Seasonal & Holiday Events">
              <option value="easter_service">Easter Service</option>
              <option value="easter_egg_hunt">Easter Egg Hunt</option>
              <option value="easter_breakfast">Easter Breakfast</option>
              <option value="christmas_service">Christmas Service</option>
              <option value="candlelight_service">Candlelight Service</option>
              <option value="christmas_caroling">Christmas Caroling</option>
              <option value="live_nativity">Live Nativity Play</option>
              <option value="vbs">Vacation Bible School (VBS)</option>
              <option value="pastor_appreciation">Pastor Appreciation</option>
            </optgroup>
            <optgroup label="Fellowship & Community">
              <option value="church_picnic">Church Picnic</option>
              <option value="church_bbq">Church BBQ</option>
              <option value="service_project">Service Project Saturday</option>
              <option value="food_pantry">Food Pantry</option>
              <option value="soup_kitchen">Soup Kitchen</option>
              <option value="mens_event">Men's Event</option>
              <option value="womens_event">Women's Event</option>
              <option value="sports_night">Sports/Drop-in Night</option>
              <option value="youth_service">Youth Service</option>
            </optgroup>
            <optgroup label="Administrative & Strategic">
              <option value="wedding">Wedding</option>
              <option value="funeral">Funeral Service</option>
              <option value="budget_meeting">Budget Meeting</option>
              <option value="strategy_planning">Strategy Planning</option>
              <option value="sabbatical">Sabbatical</option>
            </optgroup>
            <optgroup label="Special Event Formats">
              <option value="testimony_sunday">Testimony Sunday</option>
              <option value="harvest_event">Harvest Event</option>
              <option value="friend_day">Friend Day</option>
              <option value="wild_game_dinner">Wild Game Dinner</option>
              <option value="night_to_shine">Night to Shine</option>
            </optgroup>
          </select>
        </div>
      </div>

      {/* Event Type Legend - Scrollable */}
      <div className="bg-white rounded-lg p-3 shadow-sm">
        <p className="text-xs font-medium text-gray-500 mb-2">Event Types Legend</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {Object.entries(eventTypeLabels).slice(0, 15).map(([type, label]) => (
            <div key={type} className="flex items-center gap-1">
              <div className={`w-2.5 h-2.5 rounded-full ${eventTypeColors[type]}`}></div>
              <span className="text-gray-600">{label}</span>
            </div>
          ))}
          <span className="text-gray-400">+ more...</span>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-50">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-3 text-center text-sm font-medium text-gray-700 border-b">{day}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`empty-${i}`} className="p-2 min-h-[100px] border-b border-r bg-gray-50" />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dayEvents = getEventsForDay(day).filter(e => filterType === 'all' || e.event_type === filterType);
            const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
            return (
              <div key={day} className={`p-2 min-h-[100px] border-b border-r cursor-pointer hover:bg-purple-50 ${isToday ? 'bg-purple-100' : ''}`} onClick={() => openModal(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))}>
                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-purple-600' : ''}`}>{day}</div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(ev => (
                    <div key={ev.id} className={`text-xs p-1 rounded text-white truncate flex items-center gap-1 ${eventTypeColors[ev.event_type] || 'bg-gray-500'}`} onClick={(e) => { e.stopPropagation(); openModal(null, ev); }}>
                      {(ev.is_recurring || ev.is_instance) && <RefreshCw className="w-3 h-3 flex-shrink-0" />}
                      <span className="truncate">{ev.title}</span>
                    </div>
                  ))}
                  {dayEvents.length > 3 && <div className="text-xs text-gray-500">+{dayEvents.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <h3 className="font-semibold mb-3">Upcoming Events</h3>
        <div className="space-y-2">
          {events.slice(0, 5).map(ev => (
            <div key={ev.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${eventTypeColors[ev.event_type]}`} />
                <div><p className="font-medium">{ev.title}</p><p className="text-sm text-gray-500">{ev.start_date} {ev.start_time && `at ${ev.start_time}`}</p></div>
              </div>
              <div className="flex gap-2">
                {canEdit('calendar') && !ev.is_instance && (
                  <button 
                    onClick={() => setSelectedEventForReminder(ev)} 
                    className="text-purple-600 hover:text-purple-800 p-1" 
                    title="Send Reminder"
                    data-testid={`send-reminder-${ev.id}`}
                  >
                    <Bell className="w-4 h-4" />
                  </button>
                )}
                {canEdit('calendar') && (
                  <>
                    <button onClick={() => openModal(null, ev)} className="text-blue-600 hover:text-blue-800 p-1" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(ev.id)} className="text-red-600 hover:text-red-800 p-1" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
          {events.length === 0 && <p className="text-gray-500 text-center py-4">No events this month</p>}
        </div>
      </div>

      <Modal isOpen={showModal} onClose={closeModal} title={editEvent ? (editEvent.is_instance ? 'View Recurring Event' : 'Edit Event') : 'Add Event'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Event Title *</label><input type="text" className="input-field" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required disabled={editEvent?.is_instance} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select className="select-field text-sm" value={formData.event_type} onChange={(e) => setFormData({ ...formData, event_type: e.target.value })} disabled={editEvent?.is_instance}>
                <optgroup label="Weekly Pastoral Services">
                  <option value="sunday_worship">Sunday Worship Service</option>
                  <option value="bible_study">Bible Study</option>
                  <option value="midweek_service">Midweek Service</option>
                  <option value="baptism">Baptism Service</option>
                  <option value="pastoral_counseling">Pastoral Counseling</option>
                  <option value="hospital_visit">Hospital Visit</option>
                  <option value="home_visit">Home Visit</option>
                  <option value="prayer_meeting">Prayer Meeting</option>
                  <option value="staff_meeting">Staff Meeting</option>
                  <option value="leadership_meeting">Leadership Meeting</option>
                </optgroup>
                <optgroup label="Seasonal & Holiday Events">
                  <option value="easter_service">Easter Service</option>
                  <option value="easter_egg_hunt">Easter Egg Hunt</option>
                  <option value="easter_breakfast">Easter Breakfast</option>
                  <option value="christmas_service">Christmas Service</option>
                  <option value="candlelight_service">Candlelight Service</option>
                  <option value="christmas_caroling">Christmas Caroling</option>
                  <option value="live_nativity">Live Nativity Play</option>
                  <option value="vbs">Vacation Bible School (VBS)</option>
                  <option value="pastor_appreciation">Pastor Appreciation</option>
                </optgroup>
                <optgroup label="Fellowship & Community">
                  <option value="church_picnic">Church Picnic</option>
                  <option value="church_bbq">Church BBQ</option>
                  <option value="service_project">Service Project Saturday</option>
                  <option value="food_pantry">Food Pantry</option>
                  <option value="soup_kitchen">Soup Kitchen</option>
                  <option value="mens_event">Men's Event</option>
                  <option value="womens_event">Women's Event</option>
                  <option value="mens_breakfast">Men's Breakfast</option>
                  <option value="womens_breakfast">Women's Breakfast</option>
                  <option value="sports_night">Sports/Drop-in Night</option>
                  <option value="youth_service">Youth Service</option>
                </optgroup>
                <optgroup label="Administrative & Strategic">
                  <option value="wedding">Wedding</option>
                  <option value="funeral">Funeral Service</option>
                  <option value="budget_meeting">Budget Meeting</option>
                  <option value="strategy_planning">Strategy Planning</option>
                  <option value="sabbatical">Sabbatical</option>
                </optgroup>
                <optgroup label="Special Event Formats">
                  <option value="testimony_sunday">Testimony Sunday</option>
                  <option value="harvest_event">Harvest Event</option>
                  <option value="friend_day">Friend Day</option>
                  <option value="wild_game_dinner">Wild Game Dinner</option>
                  <option value="night_to_shine">Night to Shine</option>
                  <option value="communion">Communion/Lord's Supper</option>
                  <option value="choir_rehearsal">Choir Rehearsal</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="meeting">Meeting</option>
                  <option value="class">Class</option>
                  <option value="special">Special Event</option>
                  <option value="outreach">Community Outreach</option>
                  <option value="general">General</option>
                </optgroup>
              </select>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Location</label><input type="text" className="input-field" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} disabled={editEvent?.is_instance} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label><input type="date" className="input-field" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required disabled={editEvent?.is_instance} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">End Date</label><input type="date" className="input-field" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} disabled={editEvent?.is_instance} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label><input type="time" className="input-field" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} disabled={editEvent?.is_instance} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">End Time</label><input type="time" className="input-field" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} disabled={editEvent?.is_instance} /></div>
          </div>
          
          {/* Recurring Event Options */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-3 mb-3">
              <input 
                type="checkbox" 
                id="is_recurring" 
                checked={formData.is_recurring} 
                onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked, recurrence_pattern: e.target.checked ? 'weekly' : '' })}
                className="w-4 h-4 text-purple-600 rounded"
                disabled={editEvent?.is_instance}
              />
              <label htmlFor="is_recurring" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <RefreshCw className="w-4 h-4" /> Recurring Event
              </label>
            </div>
            
            {formData.is_recurring && (
              <div className="ml-7 animate-fadeIn space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Repeat</label>
                    <select 
                      className="select-field" 
                      value={formData.recurrence_pattern} 
                      onChange={(e) => setFormData({ ...formData, recurrence_pattern: e.target.value })}
                      disabled={editEvent?.is_instance}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly (by day of week)</option>
                      <option value="monthly_date">Monthly (same date)</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Until</label>
                    <input 
                      type="date" 
                      className="input-field" 
                      value={formData.recurrence_end_date} 
                      onChange={(e) => setFormData({ ...formData, recurrence_end_date: e.target.value })}
                      min={formData.start_date}
                      disabled={editEvent?.is_instance}
                    />
                  </div>
                </div>
                
                {/* Monthly by day options - select week and day of week */}
                {formData.recurrence_pattern === 'monthly' && (
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200 animate-fadeIn">
                    <label className="block text-sm font-medium text-purple-800 mb-2">Repeat on the:</label>
                    <div className="flex items-center gap-2">
                      <select
                        className="select-field flex-1"
                        value={formData.monthly_week}
                        onChange={(e) => setFormData({ ...formData, monthly_week: e.target.value })}
                        disabled={editEvent?.is_instance}
                      >
                        <option value="1">First</option>
                        <option value="2">Second</option>
                        <option value="3">Third</option>
                        <option value="4">Fourth</option>
                        <option value="last">Last</option>
                      </select>
                      <select
                        className="select-field flex-1"
                        value={formData.monthly_day}
                        onChange={(e) => setFormData({ ...formData, monthly_day: e.target.value })}
                        disabled={editEvent?.is_instance}
                      >
                        <option value="0">Sunday</option>
                        <option value="1">Monday</option>
                        <option value="2">Tuesday</option>
                        <option value="3">Wednesday</option>
                        <option value="4">Thursday</option>
                        <option value="5">Friday</option>
                        <option value="6">Saturday</option>
                      </select>
                      <span className="text-sm text-purple-700">of each month</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><textarea className="input-field" rows="2" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} disabled={editEvent?.is_instance} /></div>
          
          {editEvent?.is_instance && (
            <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-700">
              <RefreshCw className="w-4 h-4 inline mr-2" />
              This is a recurring event instance. To edit, find the original event on {editEvent.parent_event_id?.split('__')[0] ? 'its first occurrence' : 'the first date'}.
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
            {!editEvent?.is_instance && <button type="submit" className="btn-primary">{editEvent ? 'Update' : 'Create'}</button>}
          </div>
        </form>
      </Modal>

      {/* Send Reminder Modal */}
      {selectedEventForReminder && (
        <div className="modal-overlay" onClick={() => setSelectedEventForReminder(null)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Send Event Reminder</h3>
                  <p className="text-sm text-gray-500">{selectedEventForReminder.title}</p>
                </div>
              </div>
              
              <p className="text-gray-600 mb-4">
                Send notifications and email reminders to all members about this event.
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm"><strong>Event:</strong> {selectedEventForReminder.title}</p>
                <p className="text-sm"><strong>Date:</strong> {selectedEventForReminder.start_date}</p>
                {selectedEventForReminder.start_time && <p className="text-sm"><strong>Time:</strong> {selectedEventForReminder.start_time}</p>}
                {selectedEventForReminder.location && <p className="text-sm"><strong>Location:</strong> {selectedEventForReminder.location}</p>}
              </div>

              <p className="text-sm text-gray-500 mb-4">
                This will send both in-app notifications and email reminders to all members with email addresses.
              </p>

              <div className="flex gap-3">
                <button 
                  onClick={() => handleSendReminder(selectedEventForReminder, '1_day')}
                  disabled={sendingReminder}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {sendingReminder ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  Send Reminder
                </button>
                <button 
                  onClick={() => setSelectedEventForReminder(null)} 
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print View Modal */}
      {showPrintView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
              <h2 className="text-xl font-bold text-gray-900">Monthly Calendar - Print View</h2>
              <div className="flex gap-3">
                <button 
                  onClick={() => window.print()} 
                  className="btn-primary flex items-center gap-2"
                  data-testid="print-btn"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
                <button 
                  onClick={() => setShowPrintView(false)} 
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
            
            {/* Printable Calendar Content */}
            <div className="p-6 print:p-0" id="printable-calendar">
              {/* Header */}
              <div className="text-center mb-6 print:mb-4">
                <h1 className="text-3xl font-bold text-purple-900 print:text-2xl">Holy Light Ministry Soul Center Headquarters</h1>
                <p className="text-lg text-gray-600 print:text-base">Christ The King Church And Ministry</p>
                <h2 className="text-2xl font-semibold mt-2 print:text-xl">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
              </div>

              {/* Calendar Grid for Print */}
              <div className="border border-gray-300 print:border-black">
                <div className="grid grid-cols-7 bg-purple-100 print:bg-gray-200">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                    <div key={day} className="p-2 text-center text-sm font-bold border-r border-gray-300 last:border-r-0 print:text-xs">{day}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                    <div key={`empty-${i}`} className="p-2 min-h-[80px] print:min-h-[60px] border-t border-r border-gray-300 bg-gray-50" />
                  ))}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const dayEvents = getEventsForDay(day);
                    const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
                    return (
                      <div key={day} className={`p-1 min-h-[80px] print:min-h-[60px] border-t border-r border-gray-300 ${isToday ? 'bg-purple-50' : ''}`}>
                        <div className={`text-sm font-bold mb-1 ${isToday ? 'text-purple-600' : ''}`}>{day}</div>
                        <div className="space-y-0.5">
                          {dayEvents.map(ev => (
                            <div key={ev.id} className="text-xs truncate print:text-[9px]">
                              <span className="font-medium">{ev.start_time || ''}</span> {ev.title}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Events List for Print */}
              <div className="mt-6 print:mt-4">
                <h3 className="text-lg font-bold mb-3 print:text-base">Events This Month</h3>
                <div className="grid grid-cols-2 gap-4 print:gap-2 print:text-sm">
                  {events.sort((a, b) => a.start_date.localeCompare(b.start_date) || (a.start_time || '').localeCompare(b.start_time || '')).map(ev => (
                    <div key={ev.id} className="flex gap-2 items-start">
                      <div className={`w-2 h-2 mt-1.5 rounded-full flex-shrink-0 ${eventTypeColors[ev.event_type] || 'bg-gray-500'}`}></div>
                      <div>
                        <p className="font-medium text-sm print:text-xs">{ev.title}</p>
                        <p className="text-xs text-gray-600">
                          {new Date(ev.start_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {ev.start_time && ` at ${ev.start_time}`}
                          {ev.location && ` - ${ev.location}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 pt-4 border-t text-center text-sm text-gray-500 print:mt-4 print:text-xs">
                <p>805 S 1st St., Slaton, TX 79364 | (770) 638-3444</p>
                <p className="mt-1">Prepared by Holy Light Ministry Soul Center Headquarters</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Flyer Generator Modal */}
      {showFlyerGenerator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex">
            {/* Left Panel - Configuration */}
            <div className="w-1/2 border-r overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Monthly Flyer Generator
                </h2>
                <p className="text-sm text-amber-100">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</p>
              </div>
              
              <div className="p-4 space-y-6">
                {/* Header Settings */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Building className="w-4 h-4 text-purple-600" /> Header Settings
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Church Name</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        value={flyerConfig.churchName}
                        onChange={(e) => setFlyerConfig({...flyerConfig, churchName: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tagline/Slogan</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        value={flyerConfig.tagline}
                        onChange={(e) => setFlyerConfig({...flyerConfig, tagline: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Header Color</label>
                        <input 
                          type="color" 
                          className="w-full h-10 rounded cursor-pointer" 
                          value={flyerConfig.headerColor}
                          onChange={(e) => setFlyerConfig({...flyerConfig, headerColor: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Accent Color</label>
                        <input 
                          type="color" 
                          className="w-full h-10 rounded cursor-pointer" 
                          value={flyerConfig.accentColor}
                          onChange={(e) => setFlyerConfig({...flyerConfig, accentColor: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Font Style</label>
                      <select 
                        className="select-field"
                        value={flyerConfig.fontStyle}
                        onChange={(e) => setFlyerConfig({...flyerConfig, fontStyle: e.target.value})}
                      >
                        <option value="classic">Classic (Serif)</option>
                        <option value="modern">Modern (Sans-serif)</option>
                        <option value="elegant">Elegant (Georgia)</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Footer Settings */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-purple-600" /> Footer / Contact Info
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        value={flyerConfig.address}
                        onChange={(e) => setFlyerConfig({...flyerConfig, address: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <input 
                          type="text" 
                          className="input-field" 
                          value={flyerConfig.phone}
                          onChange={(e) => setFlyerConfig({...flyerConfig, phone: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input 
                          type="email" 
                          className="input-field" 
                          value={flyerConfig.email}
                          onChange={(e) => setFlyerConfig({...flyerConfig, email: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        value={flyerConfig.website}
                        onChange={(e) => setFlyerConfig({...flyerConfig, website: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pastor Name</label>
                      <input 
                        type="text" 
                        className="input-field" 
                        value={flyerConfig.pastorName}
                        onChange={(e) => setFlyerConfig({...flyerConfig, pastorName: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Announcements */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Megaphone className="w-4 h-4 text-purple-600" /> Announcements
                    </h3>
                    <button 
                      onClick={handleAddAnnouncement}
                      className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Add
                    </button>
                  </div>
                  <div className="space-y-3">
                    {flyerConfig.announcements.map((ann, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg border relative">
                        <button 
                          onClick={() => handleRemoveAnnouncement(idx)}
                          className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <input 
                          type="text" 
                          placeholder="Announcement Title"
                          className="input-field mb-2 text-sm font-medium" 
                          value={ann.title}
                          onChange={(e) => handleUpdateAnnouncement(idx, 'title', e.target.value)}
                        />
                        <textarea 
                          placeholder="Announcement details..."
                          className="input-field text-sm" 
                          rows="2"
                          value={ann.text}
                          onChange={(e) => handleUpdateAnnouncement(idx, 'text', e.target.value)}
                        />
                      </div>
                    ))}
                    {flyerConfig.announcements.length === 0 && (
                      <p className="text-gray-500 text-sm text-center py-2">No announcements added</p>
                    )}
                  </div>
                </div>

                {/* Event Settings */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-purple-600" /> Events to Include
                  </h3>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={flyerConfig.includeEvents}
                        onChange={(e) => setFlyerConfig({...flyerConfig, includeEvents: e.target.checked})}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm text-gray-700">Include calendar events</span>
                    </label>
                    {flyerConfig.includeEvents && (
                      <div className="ml-6 space-y-2">
                        <label className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            checked={flyerConfig.selectedEventTypes.includes('all')}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFlyerConfig({...flyerConfig, selectedEventTypes: ['all']});
                              } else {
                                setFlyerConfig({...flyerConfig, selectedEventTypes: []});
                              }
                            }}
                            className="w-4 h-4 text-purple-600 rounded"
                          />
                          <span className="text-sm text-gray-700">All Events</span>
                        </label>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {['sunday_worship', 'bible_study', 'prayer_meeting', 'youth_service', 'special', 'outreach'].map(type => (
                            <label key={type} className="flex items-center gap-2">
                              <input 
                                type="checkbox" 
                                checked={flyerConfig.selectedEventTypes.includes(type)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFlyerConfig({...flyerConfig, selectedEventTypes: [...flyerConfig.selectedEventTypes.filter(t => t !== 'all'), type]});
                                  } else {
                                    setFlyerConfig({...flyerConfig, selectedEventTypes: flyerConfig.selectedEventTypes.filter(t => t !== type)});
                                  }
                                }}
                                className="w-3 h-3 text-purple-600 rounded"
                                disabled={flyerConfig.selectedEventTypes.includes('all')}
                              />
                              <span className="text-gray-600">{eventTypeLabels[type] || type}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Custom Message */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-purple-600" /> Custom Message
                  </h3>
                  <textarea 
                    placeholder="Add a special message to your congregation..."
                    className="input-field" 
                    rows="3"
                    value={flyerConfig.customMessage}
                    onChange={(e) => setFlyerConfig({...flyerConfig, customMessage: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Right Panel - Preview */}
            <div className="w-1/2 flex flex-col bg-gray-100">
              <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
                <h3 className="font-semibold text-gray-900">Live Preview</h3>
                <div className="flex gap-2">
                  <button 
                    onClick={handlePrintFlyer}
                    className="btn-secondary flex items-center gap-2 text-sm"
                  >
                    <Printer className="w-4 h-4" /> Print
                  </button>
                  <button 
                    onClick={handleEmailFlyer}
                    disabled={sendingFlyer}
                    className="btn-primary flex items-center gap-2 text-sm"
                  >
                    {sendingFlyer ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Email to Members
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden transform scale-[0.7] origin-top">
                  {/* Flyer Preview */}
                  <div style={{ fontFamily: flyerConfig.fontStyle === 'modern' ? 'Inter, sans-serif' : flyerConfig.fontStyle === 'elegant' ? 'Georgia, serif' : 'Times New Roman, serif' }}>
                    {/* Header */}
                    <div style={{ background: flyerConfig.headerColor }} className="text-white p-6 text-center">
                      <h1 className="text-2xl font-bold">{flyerConfig.churchName}</h1>
                      <p className="text-sm opacity-90 mt-1">{flyerConfig.tagline}</p>
                    </div>
                    
                    {/* Month Banner */}
                    <div style={{ background: `linear-gradient(135deg, ${flyerConfig.accentColor}, ${flyerConfig.headerColor})` }} className="text-white p-4 text-center">
                      <h2 className="text-xl font-semibold">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()} Newsletter</h2>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6 bg-gray-50">
                      {/* Announcements */}
                      {flyerConfig.announcements.length > 0 && (
                        <div className="mb-6">
                          <h3 className="font-bold mb-3" style={{ color: flyerConfig.accentColor, borderBottom: `2px solid ${flyerConfig.accentColor}`, paddingBottom: '8px' }}>
                            📢 Announcements
                          </h3>
                          {flyerConfig.announcements.map((ann, idx) => (
                            <div key={idx} className="bg-white p-3 rounded-lg mb-2 border-l-4" style={{ borderLeftColor: flyerConfig.accentColor }}>
                              <h4 className="font-medium" style={{ color: flyerConfig.accentColor }}>{ann.title || 'Untitled'}</h4>
                              <p className="text-sm text-gray-600">{ann.text || 'No details'}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Events */}
                      {flyerConfig.includeEvents && events.length > 0 && (
                        <div className="mb-6">
                          <h3 className="font-bold mb-3" style={{ color: flyerConfig.accentColor, borderBottom: `2px solid ${flyerConfig.accentColor}`, paddingBottom: '8px' }}>
                            📅 Upcoming Events
                          </h3>
                          {events
                            .filter(e => flyerConfig.selectedEventTypes.includes('all') || flyerConfig.selectedEventTypes.includes(e.event_type))
                            .slice(0, 6)
                            .map(ev => {
                              const date = new Date(ev.start_date);
                              return (
                                <div key={ev.id} className="flex gap-3 bg-white p-3 rounded-lg mb-2">
                                  <div style={{ background: flyerConfig.accentColor }} className="text-white p-2 rounded text-center min-w-[50px]">
                                    <div className="text-lg font-bold">{date.getDate()}</div>
                                    <div className="text-[10px] uppercase">{monthNames[date.getMonth()].slice(0, 3)}</div>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-sm">{ev.title}</h4>
                                    <p className="text-xs text-gray-500">{ev.start_time || ''} {ev.location ? '• ' + ev.location : ''}</p>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}
                      
                      {/* Custom Message */}
                      {flyerConfig.customMessage && (
                        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg text-center">
                          <p className="text-sm">{flyerConfig.customMessage}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Footer */}
                    <div className="bg-gray-800 text-white p-4 text-center text-sm">
                      <p className="font-medium">{flyerConfig.churchName}</p>
                      <p className="text-gray-300">{flyerConfig.address}</p>
                      <p className="text-gray-300">📞 {flyerConfig.phone} | ✉️ {flyerConfig.email}</p>
                      <p className="text-gray-300">🌐 {flyerConfig.website}</p>
                      <p className="italic mt-2 text-gray-400">{flyerConfig.pastorName}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-white border-t">
                <button 
                  onClick={() => setShowFlyerGenerator(false)}
                  className="w-full btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Church Materials Component
const ChurchMaterials = () => {
  const { canEdit } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMaterial, setEditMaterial] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ title: '', description: '', category: 'bible_study', file_data: '', file_name: '', file_type: '', url: '' });
  const fileInputRef = useRef(null);

  const categories = [
    { id: 'all', label: 'All Materials' },
    { id: 'bible_study', label: 'Bible Study' },
    { id: 'sermon_notes', label: 'Sermon Notes' },
    { id: 'training', label: 'Training' },
    { id: 'forms', label: 'Forms' },
    { id: 'newsletters', label: 'Newsletters' },
    { id: 'other', label: 'Other' }
  ];

  useEffect(() => { fetchMaterials(); }, [selectedCategory]);

  const fetchMaterials = async () => {
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const r = await api.get(`/api/materials${params}`);
      setMaterials(r.data.materials);
    } catch (e) { console.error(e); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { alert('File size must be less than 10MB'); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, file_data: reader.result.split(',')[1], file_name: file.name, file_type: file.type });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMaterial) {
        await api.put(`/api/materials/${editMaterial.id}`, formData);
      } else {
        await api.post('/api/materials', formData);
      }
      fetchMaterials();
      closeModal();
    } catch (e) { console.error(e); alert('Error saving material'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this material?')) {
      try { await api.delete(`/api/materials/${id}`); fetchMaterials(); } catch (e) { alert('Failed to delete'); }
    }
  };

  const [pdfViewer, setPdfViewer] = useState({ open: false, url: '', title: '' });

  const handleView = async (material) => {
    try {
      await api.post(`/api/materials/${material.id}/download`);
      const r = await api.get(`/api/materials/${material.id}`);
      const mat = r.data.material;
      
      if (mat.url) {
        // For external URLs, open in new tab
        window.open(mat.url, '_blank');
      } else if (mat.file_data) {
        // For uploaded files, open in viewer if PDF, otherwise download
        const fileType = mat.file_type || '';
        if (fileType === 'application/pdf' || mat.file_name?.toLowerCase().endsWith('.pdf')) {
          // Open PDF in viewer modal
          const pdfUrl = `data:application/pdf;base64,${mat.file_data}`;
          setPdfViewer({ open: true, url: pdfUrl, title: mat.title });
        } else {
          // For other files, download
          const link = document.createElement('a');
          link.href = `data:${mat.file_type};base64,${mat.file_data}`;
          link.download = mat.file_name;
          link.click();
        }
      }
      fetchMaterials();
    } catch (e) { console.error(e); alert('Error opening file'); }
  };

  const handleDownload = async (material) => {
    try {
      await api.post(`/api/materials/${material.id}/download`);
      const r = await api.get(`/api/materials/${material.id}`);
      const mat = r.data.material;
      
      if (mat.url) {
        window.open(mat.url, '_blank');
      } else if (mat.file_data) {
        const link = document.createElement('a');
        link.href = `data:${mat.file_type};base64,${mat.file_data}`;
        link.download = mat.file_name;
        link.click();
      }
      fetchMaterials();
    } catch (e) { console.error(e); alert('Error downloading'); }
  };

  const openModal = (material = null) => {
    if (material) {
      setEditMaterial(material);
      setFormData({ title: material.title, description: material.description || '', category: material.category, file_data: '', file_name: material.file_name || '', file_type: material.file_type || '', url: material.url || '' });
    } else {
      setEditMaterial(null);
      setFormData({ title: '', description: '', category: 'bible_study', file_data: '', file_name: '', file_type: '', url: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditMaterial(null); };

  const filteredMaterials = materials.filter(m => 
    m.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.description && m.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getCategoryColor = (cat) => {
    const colors = { bible_study: 'bg-purple-100 text-purple-700', sermon_notes: 'bg-blue-100 text-blue-700', training: 'bg-green-100 text-green-700', forms: 'bg-orange-100 text-orange-700', newsletters: 'bg-pink-100 text-pink-700', other: 'bg-gray-100 text-gray-700' };
    return colors[cat] || colors.other;
  };

  return (
    <div className="space-y-6" data-testid="materials-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search materials..." className="input-field pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        {canEdit('materials') && (
          <button onClick={() => openModal()} className="btn-primary flex items-center gap-2" data-testid="add-material-btn">
            <Upload className="w-4 h-4" /> Upload Material
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map(cat => (
          <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`px-3 py-1.5 rounded-full text-sm transition-colors ${selectedCategory === cat.id ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {cat.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMaterials.map(material => (
          <div key={material.id} className="card hover:shadow-lg transition-shadow" data-testid={`material-${material.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <File className="w-6 h-6 text-purple-600" />
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${getCategoryColor(material.category)}`}>
                {categories.find(c => c.id === material.category)?.label || material.category}
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">{material.title}</h3>
            {material.description && <p className="text-sm text-gray-500 mb-3 line-clamp-2">{material.description}</p>}
            <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
              <span>{material.file_name || (material.url ? 'External Link' : 'No file')}</span>
              <span><Download className="w-3 h-3 inline mr-1" />{material.download_count || 0}</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleView(material)} className="btn-primary flex-1 text-sm py-2 flex items-center justify-center gap-1" data-testid={`view-material-${material.id}`}>
                <Eye className="w-4 h-4" /> {(material.file_type === 'application/pdf' || material.file_name?.toLowerCase().endsWith('.pdf')) ? 'View' : 'Open'}
              </button>
              <button onClick={() => handleDownload(material)} className="btn-secondary p-2" title="Download" data-testid={`download-material-${material.id}`}>
                <Download className="w-4 h-4" />
              </button>
              {canEdit('materials') && (
                <>
                  <button onClick={() => openModal(material)} className="btn-secondary p-2"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(material.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </>
              )}
            </div>
          </div>
        ))}
        {filteredMaterials.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">
            <FolderOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No materials found. Upload your first material!</p>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {pdfViewer.open && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{pdfViewer.title}</h3>
              <div className="flex gap-2">
                <a href={pdfViewer.url} download={`${pdfViewer.title}.pdf`} className="btn-secondary text-sm flex items-center gap-1">
                  <Download className="w-4 h-4" /> Download
                </a>
                <button onClick={() => setPdfViewer({ open: false, url: '', title: '' })} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe src={pdfViewer.url} className="w-full h-full" title={pdfViewer.title} />
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={showModal} onClose={closeModal} title={editMaterial ? 'Edit Material' : 'Upload Material'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" className="input-field" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select className="select-field" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
              {categories.filter(c => c.id !== 'all').map(cat => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea className="input-field" rows="2" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Upload File (PDF, DOC, etc.)</label>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.png" className="input-field" />
            {formData.file_name && <p className="text-sm text-green-600 mt-1"><CheckCircle className="w-4 h-4 inline mr-1" />{formData.file_name}</p>}
          </div>
          <div className="text-center text-gray-400 text-sm">- OR -</div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">External URL</label>
            <input type="url" className="input-field" placeholder="https://..." value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{editMaterial ? 'Update' : 'Upload'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Newsletter Component
const Newsletter = () => {
  const { canEdit } = useAuth();
  const [newsletters, setNewsletters] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editNewsletter, setEditNewsletter] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [formData, setFormData] = useState({
    title: 'Weekly Newsletter',
    greeting: 'Dear Church Family,',
    content: '',
    verse: '',
    verse_reference: '',
    announcements: [''],
    prayer_requests: '',
    closing: 'God Bless,\nHoly Light Ministry Soul Center Headquarters'
  });

  useEffect(() => { fetchNewsletters(); fetchUpcomingEvents(); }, []);

  const fetchNewsletters = async () => {
    try { const r = await api.get('/api/newsletters'); setNewsletters(r.data.newsletters); } catch (e) { console.error(e); }
  };

  const fetchUpcomingEvents = async () => {
    try {
      const r = await api.get(`/api/calendar/events?month=${new Date().getMonth() + 1}&year=${new Date().getFullYear()}`);
      const today = new Date();
      const nextWeek = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
      const upcoming = (r.data.events || [])
        .filter(e => new Date(e.start_date) >= today && new Date(e.start_date) <= nextWeek)
        .slice(0, 5)
        .map(e => ({ title: e.title, date: e.start_date, time: e.start_time || '' }));
      setUpcomingEvents(upcoming);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      announcements: formData.announcements.filter(a => a.trim()),
      upcoming_events: upcomingEvents
    };
    try {
      if (editNewsletter) {
        await api.put(`/api/newsletters/${editNewsletter.id}`, data);
      } else {
        await api.post('/api/newsletters', data);
      }
      fetchNewsletters();
      closeModal();
    } catch (e) { console.error(e); alert('Error saving newsletter'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this newsletter?')) {
      try { await api.delete(`/api/newsletters/${id}`); fetchNewsletters(); } catch (e) { alert('Failed to delete'); }
    }
  };

  const handleSend = async (newsletter) => {
    if (!window.confirm(`Send this newsletter to all members?\n\n"${newsletter.title}"`)) return;
    setSending(true);
    try {
      const result = await api.post(`/api/newsletters/${newsletter.id}/send`);
      alert(`Newsletter sent to ${result.data.emails_sent} members!`);
      fetchNewsletters();
    } catch (e) {
      console.error(e);
      alert('Error sending newsletter');
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async (newsletter) => {
    try {
      const r = await api.get(`/api/newsletters/${newsletter.id}/preview`);
      setPreviewHtml(r.data.html);
      setShowPreview(true);
    } catch (e) { alert('Error loading preview'); }
  };

  const openModal = (newsletter = null) => {
    if (newsletter) {
      setEditNewsletter(newsletter);
      setFormData({
        title: newsletter.title || 'Weekly Newsletter',
        greeting: newsletter.greeting || 'Dear Church Family,',
        content: newsletter.content || '',
        verse: newsletter.verse || '',
        verse_reference: newsletter.verse_reference || '',
        announcements: newsletter.announcements?.length ? newsletter.announcements : [''],
        prayer_requests: newsletter.prayer_requests || '',
        closing: newsletter.closing || 'God Bless,\nHoly Light Ministry'
      });
    } else {
      setEditNewsletter(null);
      setFormData({
        title: 'Weekly Newsletter',
        greeting: 'Dear Church Family,',
        content: '',
        verse: '',
        verse_reference: '',
        announcements: [''],
        prayer_requests: '',
        closing: 'God Bless,\nHoly Light Ministry Soul Center Headquarters'
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditNewsletter(null);
  };

  const addAnnouncement = () => setFormData({ ...formData, announcements: [...formData.announcements, ''] });
  const updateAnnouncement = (index, value) => {
    const updated = [...formData.announcements];
    updated[index] = value;
    setFormData({ ...formData, announcements: updated });
  };
  const removeAnnouncement = (index) => {
    const updated = formData.announcements.filter((_, i) => i !== index);
    setFormData({ ...formData, announcements: updated.length ? updated : [''] });
  };

  return (
    <div className="space-y-6" data-testid="newsletter-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Weekly Newsletter</h2>
          <p className="text-gray-500">Create and send newsletters to your church family</p>
        </div>
        {canEdit('newsletter') && (
          <button onClick={() => openModal()} className="btn-primary flex items-center gap-2" data-testid="create-newsletter-btn">
            <Plus className="w-4 h-4" /> Create Newsletter
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <Mail className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{newsletters.length}</p>
            <p className="text-sm text-gray-500">Total Newsletters</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <Send className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{newsletters.filter(n => n.status === 'sent').length}</p>
            <p className="text-sm text-gray-500">Sent</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-yellow-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{newsletters.filter(n => n.status === 'draft').length}</p>
            <p className="text-sm text-gray-500">Drafts</p>
          </div>
        </div>
      </div>

      {/* Newsletter List */}
      <div className="card">
        <h3 className="font-semibold mb-4">All Newsletters</h3>
        {newsletters.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No newsletters yet. Create your first one!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {newsletters.map((nl) => (
              <div key={nl.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${nl.status === 'sent' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                    {nl.status === 'sent' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <FileText className="w-5 h-5 text-yellow-600" />}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{nl.title}</h4>
                    <p className="text-sm text-gray-500">
                      {nl.status === 'sent' 
                        ? `Sent on ${new Date(nl.sent_at).toLocaleDateString()} to ${nl.recipients_count} members`
                        : `Draft - Created ${new Date(nl.created_at).toLocaleDateString()}`
                      }
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handlePreview(nl)} className="btn-secondary text-sm p-2" title="Preview">
                    <Eye className="w-4 h-4" />
                  </button>
                  {nl.status === 'draft' && canEdit('newsletter') && (
                    <>
                      <button onClick={() => openModal(nl)} className="btn-secondary text-sm p-2" title="Edit">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleSend(nl)} disabled={sending} className="btn-primary text-sm flex items-center gap-1" title="Send">
                        {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send
                      </button>
                    </>
                  )}
                  {canEdit('newsletter') && (
                    <button onClick={() => handleDelete(nl.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={closeModal} title={editNewsletter ? 'Edit Newsletter' : 'Create Newsletter'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" className="input-field" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Greeting</label>
            <input type="text" className="input-field" value={formData.greeting} onChange={(e) => setFormData({ ...formData, greeting: e.target.value })} placeholder="Dear Church Family," />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Main Content *</label>
            <textarea className="input-field" rows="5" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} placeholder="Write your newsletter message here..." required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scripture Verse</label>
              <textarea className="input-field" rows="2" value={formData.verse} onChange={(e) => setFormData({ ...formData, verse: e.target.value })} placeholder="For God so loved the world..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Verse Reference</label>
              <input type="text" className="input-field" value={formData.verse_reference} onChange={(e) => setFormData({ ...formData, verse_reference: e.target.value })} placeholder="John 3:16" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Announcements</label>
            {formData.announcements.map((a, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input type="text" className="input-field flex-1" value={a} onChange={(e) => updateAnnouncement(i, e.target.value)} placeholder={`Announcement ${i + 1}`} />
                <button type="button" onClick={() => removeAnnouncement(i)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={addAnnouncement} className="text-sm text-purple-600 hover:underline">+ Add Announcement</button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prayer Requests</label>
            <textarea className="input-field" rows="2" value={formData.prayer_requests} onChange={(e) => setFormData({ ...formData, prayer_requests: e.target.value })} placeholder="Please keep the following in your prayers..." />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Closing</label>
            <textarea className="input-field" rows="2" value={formData.closing} onChange={(e) => setFormData({ ...formData, closing: e.target.value })} placeholder="God Bless," />
          </div>

          {upcomingEvents.length > 0 && (
            <div className="bg-purple-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-purple-700 mb-2">📅 Upcoming events will be included:</p>
              <ul className="text-sm text-purple-600">
                {upcomingEvents.map((e, i) => <li key={i}>• {e.title} - {e.date}</li>)}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{editNewsletter ? 'Update' : 'Save Draft'}</button>
          </div>
        </form>
      </Modal>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Newsletter Preview</h3>
              <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-gray-100">
              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Tithe & Offering Reminders Component
const TitheReminders = () => {
  const { canEdit } = useAuth();
  const [settings, setSettings] = useState({
    is_enabled: false,
    schedule: [],
    reminder_message: "This is a gentle reminder to bring your tithes and offerings to the Lord. Your faithful giving supports our ministry and helps us serve our community.",
    include_giving_link: true,
    include_giving_history: true
  });
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [newSchedule, setNewSchedule] = useState({ day: 'sunday', time: '08:00' });

  const daysOfWeek = [
    { value: 'sunday', label: 'Sunday' },
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' }
  ];

  useEffect(() => { fetchSettings(); fetchHistory(); fetchPreview(); }, []);

  const fetchSettings = async () => {
    try { const r = await api.get('/api/tithe-reminders/settings'); setSettings(r.data); } catch (e) { console.error(e); }
  };

  const fetchHistory = async () => {
    try { const r = await api.get('/api/tithe-reminders/history'); setHistory(r.data.reminders || []); } catch (e) { console.error(e); }
  };

  const fetchPreview = async () => {
    try { const r = await api.post('/api/tithe-reminders/preview'); setPreview(r.data); } catch (e) { console.error(e); }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/api/tithe-reminders/settings', settings);
      alert('Settings saved successfully!');
      fetchPreview();
    } catch (e) {
      console.error(e);
      alert('Error saving settings');
    }
  };

  const handleSendReminders = async () => {
    if (!window.confirm('Send tithe reminders to all members with email addresses?\n\nThis will send both an email and an in-app notification.')) return;
    setSending(true);
    try {
      const r = await api.post('/api/tithe-reminders/send');
      alert(`Tithe reminders sent to ${r.data.emails_sent} members!`);
      fetchHistory();
    } catch (e) {
      console.error(e);
      alert('Error sending reminders: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  const addSchedule = () => {
    if (!newSchedule.day || !newSchedule.time) return;
    const exists = settings.schedule.some(s => s.day === newSchedule.day && s.time === newSchedule.time);
    if (exists) {
      alert('This schedule already exists');
      return;
    }
    setSettings({ ...settings, schedule: [...settings.schedule, { ...newSchedule }] });
    setShowScheduleModal(false);
    setNewSchedule({ day: 'sunday', time: '08:00' });
  };

  const removeSchedule = (index) => {
    const updated = settings.schedule.filter((_, i) => i !== index);
    setSettings({ ...settings, schedule: updated });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="space-y-6" data-testid="tithe-reminders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tithe & Offering Reminders</h2>
          <p className="text-gray-500">Send weekly reminders to encourage faithful giving</p>
        </div>
        {canEdit('notifications') && (
          <button 
            onClick={handleSendReminders} 
            disabled={sending}
            className="btn-primary flex items-center gap-2"
            data-testid="send-reminders-btn"
          >
            {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending...' : 'Send Reminders Now'}
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
              <CalendarDays className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-purple-600 font-medium">Scheduled Days</p>
              <p className="text-2xl font-bold text-purple-900">{settings.schedule.length}</p>
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-600 font-medium">Last Sent</p>
              <p className="text-lg font-bold text-green-900">{history[0]?.sent_at ? new Date(history[0].sent_at).toLocaleDateString() : 'Never'}</p>
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Sent (Last)</p>
              <p className="text-2xl font-bold text-blue-900">{history[0]?.emails_sent || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Card */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-purple-600" /> Reminder Settings
          </h3>
          
          <div className="space-y-4">
            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Message</label>
              <textarea
                className="input-field"
                rows="4"
                value={settings.reminder_message}
                onChange={(e) => setSettings({ ...settings, reminder_message: e.target.value })}
                placeholder="Enter your tithe reminder message..."
              />
            </div>

            {/* Options */}
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.include_giving_link}
                  onChange={(e) => setSettings({ ...settings, include_giving_link: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm text-gray-700">Include "Give Online" button with link</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.include_giving_history}
                  onChange={(e) => setSettings({ ...settings, include_giving_history: e.target.checked })}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <span className="text-sm text-gray-700">Include member's giving summary</span>
              </label>
            </div>

            <button onClick={handleSaveSettings} className="btn-primary w-full">
              <CheckCircle className="w-4 h-4 inline mr-2" /> Save Settings
            </button>
          </div>
        </div>

        {/* Schedule Card */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" /> Weekly Schedule
            </h3>
            <button onClick={() => setShowScheduleModal(true)} className="btn-secondary text-sm flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add Time
            </button>
          </div>

          {settings.schedule.length > 0 ? (
            <div className="space-y-2">
              {settings.schedule.map((s, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Bell className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 capitalize">{s.day}</p>
                      <p className="text-sm text-gray-500">{formatTime(s.time)}</p>
                    </div>
                  </div>
                  <button onClick={() => removeSchedule(index)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No schedule set</p>
              <p className="text-sm">Add times when reminders should be sent</p>
            </div>
          )}
          
          <p className="text-xs text-gray-400 mt-4 italic">
            Note: Scheduled reminders require manual sending. Use "Send Reminders Now" to send.
          </p>
        </div>
      </div>

      {/* Preview Card */}
      {preview && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-600" /> Email Preview
          </h3>
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden max-w-lg mx-auto">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 text-center">
                <h4 className="font-bold">{preview.org_name}</h4>
                <p className="text-sm opacity-90">💰 Tithe & Offering Reminder</p>
              </div>
              <div className="p-4">
                <p className="text-gray-700 mb-4 whitespace-pre-wrap">{preview.message}</p>
                <div className="bg-purple-50 border-l-4 border-purple-500 p-3 mb-4 text-sm italic text-purple-800">
                  "Bring the whole tithe into the storehouse..."<br/>
                  <span className="text-xs">— Malachi 3:10</span>
                </div>
                {settings.include_giving_history && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <p className="text-sm font-semibold text-green-800 mb-2">📊 Your Giving Summary</p>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-gray-600">This Year:</span><span className="font-bold text-green-700">${preview.sample_stats?.total_this_year?.toLocaleString()}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">This Month:</span><span className="font-bold text-green-700">${preview.sample_stats?.total_this_month?.toLocaleString()}</span></div>
                    </div>
                  </div>
                )}
                {settings.include_giving_link && (
                  <div className="text-center">
                    <span className="inline-block bg-purple-600 text-white px-6 py-2 rounded-lg text-sm font-semibold">💝 Give Online Now</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Card */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-purple-600" /> Reminder History
        </h3>
        {history.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Date Sent</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Sent By</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Emails Sent</th>
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 10).map((h) => (
                  <tr key={h.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3 text-sm">{new Date(h.sent_at).toLocaleString()}</td>
                    <td className="py-2 px-3 text-sm">{h.sent_by}</td>
                    <td className="py-2 px-3 text-sm font-medium">{h.emails_sent} / {h.total_members}</td>
                    <td className="py-2 px-3">
                      {h.errors_count > 0 ? (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">{h.errors_count} errors</span>
                      ) : (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Success</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No reminders sent yet</p>
        )}
      </div>

      {/* Add Schedule Modal */}
      <Modal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} title="Add Reminder Time">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
            <select
              className="select-field"
              value={newSchedule.day}
              onChange={(e) => setNewSchedule({ ...newSchedule, day: e.target.value })}
            >
              {daysOfWeek.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
            <input
              type="time"
              className="input-field"
              value={newSchedule.time}
              onChange={(e) => setNewSchedule({ ...newSchedule, time: e.target.value })}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button onClick={() => setShowScheduleModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={addSchedule} className="btn-primary">Add Schedule</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// In-App Notifications Management Component
const NotificationsManager = () => {
  const { canEdit } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'announcement',
    priority: 'normal',
    recipient_type: 'all',
    recipient_ids: [],
    send_email: true
  });

  const notificationTypes = [
    { id: 'announcement', label: 'Announcement', icon: Megaphone, color: 'purple' },
    { id: 'event_reminder', label: 'Event Reminder', icon: Calendar, color: 'blue' },
    { id: 'birthday', label: 'Birthday', icon: Gift, color: 'pink' },
    { id: 'welcome', label: 'Welcome', icon: UserPlus, color: 'green' },
    { id: 'prayer', label: 'Prayer Request', icon: Heart, color: 'red' },
    { id: 'general', label: 'General', icon: Bell, color: 'gray' }
  ];

  const priorities = [
    { id: 'low', label: 'Low', color: 'gray' },
    { id: 'normal', label: 'Normal', color: 'blue' },
    { id: 'high', label: 'High', color: 'red' }
  ];

  useEffect(() => { fetchNotifications(); fetchMembers(); }, []);

  const fetchNotifications = async () => {
    try { 
      const r = await api.get('/api/alerts'); 
      setNotifications(r.data.alerts || []); 
    } catch (e) { console.error(e); }
  };

  const fetchMembers = async () => {
    try { 
      const r = await api.get('/api/members'); 
      setMembers(r.data.members || []); 
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      if (formData.send_email) {
        // Send via email API
        const response = await api.post('/api/notifications/send-email', {
          type: formData.type,
          title: formData.title,
          message: formData.message,
          priority: formData.priority,
          recipient_type: formData.recipient_type,
          recipient_ids: formData.recipient_ids
        });
        alert(`${response.data.message}\n\nEmails sent: ${response.data.emails_sent} of ${response.data.total_members} members with email addresses`);
      } else {
        // In-app only notifications
        if (formData.recipient_type === 'all') {
          let successCount = 0;
          for (const member of members) {
            await api.post('/api/alerts', {
              type: formData.type,
              title: formData.title,
              message: formData.message,
              priority: formData.priority,
              recipient_type: 'member',
              recipient_id: member.id,
              recipient_email: member.email
            });
            successCount++;
          }
          alert(`In-app notification sent to ${successCount} members!`);
        } else if (formData.recipient_type === 'selected' && formData.recipient_ids.length > 0) {
          for (const memberId of formData.recipient_ids) {
            const member = members.find(m => m.id === memberId);
            await api.post('/api/alerts', {
              type: formData.type,
              title: formData.title,
              message: formData.message,
              priority: formData.priority,
              recipient_type: 'member',
              recipient_id: memberId,
              recipient_email: member?.email
            });
          }
          alert(`In-app notification sent to ${formData.recipient_ids.length} selected members!`);
        } else {
          await api.post('/api/alerts', {
            type: formData.type,
            title: formData.title,
            message: formData.message,
            priority: formData.priority,
            recipient_type: 'all'
          });
          alert('Broadcast notification created!');
        }
      }
      fetchNotifications();
      closeModal();
    } catch (e) { 
      console.error(e); 
      alert(e.response?.data?.detail || 'Error sending notification'); 
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this notification?')) {
      try { await api.delete(`/api/alerts/${id}`); fetchNotifications(); } catch (e) { alert('Failed to delete'); }
    }
  };

  const handleMarkAllRead = async () => {
    try { await api.put('/api/alerts/mark-all-read'); fetchNotifications(); } catch (e) { console.error(e); }
  };

  const openModal = () => {
    setFormData({
      title: '',
      message: '',
      type: 'announcement',
      priority: 'normal',
      recipient_type: 'all',
      recipient_ids: [],
      send_email: true
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({ title: '', message: '', type: 'announcement', priority: 'normal', recipient_type: 'all', recipient_ids: [], send_email: true });
  };

  const filteredNotifications = selectedFilter === 'all' 
    ? notifications 
    : selectedFilter === 'unread' 
      ? notifications.filter(n => n.status === 'unread')
      : notifications.filter(n => n.type === selectedFilter);

  const getTypeConfig = (type) => notificationTypes.find(t => t.id === type) || notificationTypes[5];
  const getPriorityConfig = (priority) => priorities.find(p => p.id === priority) || priorities[1];

  const unreadCount = notifications.filter(n => n.status === 'unread').length;

  const toggleMemberSelection = (memberId) => {
    setFormData(prev => ({
      ...prev,
      recipient_ids: prev.recipient_ids.includes(memberId)
        ? prev.recipient_ids.filter(id => id !== memberId)
        : [...prev.recipient_ids, memberId]
    }));
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">In-App Notifications</h2>
          <p className="text-gray-500 text-sm">Send announcements and alerts to your church members</p>
        </div>
        {canEdit('notifications') && (
          <button onClick={openModal} className="btn-primary flex items-center gap-2" data-testid="create-notification-btn">
            <Plus className="w-4 h-4" /> Create Notification
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <Bell className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{notifications.length}</p>
            <p className="text-sm text-gray-500">Total</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{unreadCount}</p>
            <p className="text-sm text-gray-500">Unread</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Megaphone className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{notifications.filter(n => n.type === 'announcement').length}</p>
            <p className="text-sm text-gray-500">Announcements</p>
          </div>
        </div>
        <div className="card flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <Users className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold">{members.length}</p>
            <p className="text-sm text-gray-500">Members</p>
          </div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSelectedFilter('all')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${selectedFilter === 'all' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              All
            </button>
            <button onClick={() => setSelectedFilter('unread')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1 ${selectedFilter === 'unread' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              Unread {unreadCount > 0 && <span className="bg-red-500 text-white text-xs px-1.5 rounded-full">{unreadCount}</span>}
            </button>
            {notificationTypes.slice(0, 4).map(type => (
              <button key={type.id} onClick={() => setSelectedFilter(type.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${selectedFilter === type.id ? `bg-${type.color}-100 text-${type.color}-700` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {type.label}
              </button>
            ))}
          </div>
          {unreadCount > 0 && (
            <button onClick={handleMarkAllRead} className="text-sm text-purple-600 hover:underline flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> Mark all as read
            </button>
          )}
        </div>

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No notifications found</p>
            {canEdit('notifications') && (
              <button onClick={openModal} className="mt-4 text-purple-600 hover:underline">Create your first notification</button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notif) => {
              const typeConfig = getTypeConfig(notif.type);
              const priorityConfig = getPriorityConfig(notif.priority);
              const TypeIcon = typeConfig.icon || Bell;
              return (
                <div key={notif.id} className={`flex items-start gap-4 p-4 rounded-lg border transition ${notif.status === 'unread' ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${notif.status === 'unread' ? `bg-${typeConfig.color}-200` : `bg-${typeConfig.color}-100`}`}>
                    <TypeIcon className={`w-5 h-5 text-${typeConfig.color}-600`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          {notif.title}
                          {notif.status === 'unread' && <span className="w-2 h-2 bg-purple-500 rounded-full" />}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notif.message}</p>
                      </div>
                      {notif.priority === 'high' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full flex-shrink-0">High Priority</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(notif.created_at).toLocaleString()}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full bg-${typeConfig.color}-100 text-${typeConfig.color}-700`}>
                        {typeConfig.label}
                      </span>
                    </div>
                  </div>
                  {canEdit('notifications') && (
                    <button onClick={() => handleDelete(notif.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Notification Modal */}
      <Modal isOpen={showModal} onClose={closeModal} title="Create Notification" size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notification Type *</label>
            <div className="grid grid-cols-3 gap-2">
              {notificationTypes.map(type => {
                const TypeIcon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: type.id })}
                    className={`p-3 rounded-lg border-2 transition flex flex-col items-center gap-1 ${formData.type === type.id ? `border-${type.color}-500 bg-${type.color}-50` : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <TypeIcon className={`w-5 h-5 ${formData.type === type.id ? `text-${type.color}-600` : 'text-gray-400'}`} />
                    <span className={`text-xs font-medium ${formData.type === type.id ? `text-${type.color}-700` : 'text-gray-600'}`}>{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input 
              type="text" 
              className="input-field" 
              value={formData.title} 
              onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
              placeholder="Enter notification title..."
              required 
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
            <textarea 
              className="input-field" 
              rows="4" 
              value={formData.message} 
              onChange={(e) => setFormData({ ...formData, message: e.target.value })} 
              placeholder="Write your notification message..."
              required 
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select className="select-field" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Send To</label>
              <select className="select-field" value={formData.recipient_type} onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value, recipient_ids: [] })}>
                <option value="all">All Members</option>
                <option value="selected">Select Members</option>
                <option value="broadcast">Broadcast Only (No individual)</option>
              </select>
            </div>
          </div>

          {formData.recipient_type === 'selected' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Members ({formData.recipient_ids.length} selected)</label>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                {members.map(member => (
                  <label key={member.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.recipient_ids.includes(member.id)}
                      onChange={() => toggleMemberSelection(member.id)}
                      className="rounded text-purple-600"
                    />
                    <span className="text-sm">{member.first_name} {member.last_name}</span>
                    {member.email && <span className="text-xs text-gray-400">({member.email})</span>}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Email Toggle */}
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <label className="flex items-center gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.send_email} 
                onChange={(e) => setFormData({ ...formData, send_email: e.target.checked })}
                className="w-5 h-5 rounded text-purple-600 focus:ring-purple-500"
              />
              <div>
                <span className="font-medium text-purple-900 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Send via Email
                </span>
                <p className="text-xs text-purple-600 mt-0.5">
                  {formData.send_email 
                    ? `Will send email to ${members.filter(m => m.email).length} members with email addresses` 
                    : 'In-app notification only'}
                </p>
              </div>
            </label>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
            <p className="font-medium mb-1">📢 Preview:</p>
            <p><strong>Type:</strong> {getTypeConfig(formData.type).label}</p>
            <p><strong>Recipients:</strong> {formData.recipient_type === 'all' ? `All ${members.length} members` : formData.recipient_type === 'selected' ? `${formData.recipient_ids.length} selected members` : 'Broadcast'}</p>
            <p><strong>Priority:</strong> {getPriorityConfig(formData.priority).label}</p>
            <p><strong>Delivery:</strong> {formData.send_email ? '📧 Email + In-App' : '🔔 In-App Only'}</p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={sending || !formData.title || !formData.message} className="btn-primary flex items-center gap-2">
              {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : formData.send_email ? <Mail className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {sending ? 'Sending...' : formData.send_email ? 'Send Email' : 'Send Notification'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Task Assignments Component
const TaskAssignments = () => {
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [formData, setFormData] = useState({ title: '', description: '', task_type: 'study', assigned_to: '', due_date: '', priority: 'normal', material_id: '', notes: '' });

  const taskTypes = [
    { id: 'all', label: 'All Types' },
    { id: 'study', label: 'Study' },
    { id: 'volunteer', label: 'Volunteer' },
    { id: 'follow_up', label: 'Follow Up' },
    { id: 'outreach', label: 'Outreach' },
    { id: 'training', label: 'Training' },
    { id: 'other', label: 'Other' }
  ];

  const statuses = [
    { id: 'all', label: 'All Status' },
    { id: 'pending', label: 'Pending' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'completed', label: 'Completed' },
    { id: 'cancelled', label: 'Cancelled' }
  ];

  const priorities = ['low', 'normal', 'high', 'urgent'];

  useEffect(() => { fetchTasks(); fetchMembers(); fetchMaterials(); }, [selectedStatus, selectedType]);

  const fetchTasks = async () => {
    try {
      let params = [];
      if (selectedStatus !== 'all') params.push(`status=${selectedStatus}`);
      if (selectedType !== 'all') params.push(`task_type=${selectedType}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      const r = await api.get(`/api/tasks${queryString}`);
      setTasks(r.data.tasks);
    } catch (e) { console.error(e); }
  };

  const fetchMembers = async () => {
    try { const r = await api.get('/api/members'); setMembers(r.data.members); } catch (e) { console.error(e); }
  };

  const fetchMaterials = async () => {
    try { const r = await api.get('/api/materials'); setMaterials(r.data.materials); } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editTask) {
        await api.put(`/api/tasks/${editTask.id}`, formData);
      } else {
        await api.post('/api/tasks', formData);
      }
      fetchTasks();
      closeModal();
    } catch (e) { console.error(e); alert('Error saving task'); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.put(`/api/tasks/${taskId}`, { status: newStatus });
      fetchTasks();
    } catch (e) { console.error(e); alert('Error updating status'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this task?')) {
      try { await api.delete(`/api/tasks/${id}`); fetchTasks(); } catch (e) { alert('Failed to delete'); }
    }
  };

  const openModal = (task = null) => {
    if (task) {
      setEditTask(task);
      setFormData({ title: task.title, description: task.description || '', task_type: task.task_type, assigned_to: task.assigned_to, due_date: task.due_date || '', priority: task.priority, material_id: task.material_id || '', notes: task.notes || '' });
    } else {
      setEditTask(null);
      setFormData({ title: '', description: '', task_type: 'study', assigned_to: '', due_date: '', priority: 'normal', material_id: '', notes: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditTask(null); };

  const getStatusColor = (status) => {
    const colors = { pending: 'bg-yellow-100 text-yellow-700', in_progress: 'bg-blue-100 text-blue-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-gray-100 text-gray-500' };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = { low: 'text-gray-400', normal: 'text-blue-500', high: 'text-orange-500', urgent: 'text-red-500' };
    return colors[priority] || colors.normal;
  };

  const getTypeIcon = (type) => {
    const icons = { study: BookOpen, volunteer: Users, follow_up: UserCheck, outreach: Heart, training: Briefcase, other: Clipboard };
    return icons[type] || Clipboard;
  };

  const isOverdue = (dueDate) => dueDate && new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-xl font-bold text-gray-900">Task Assignments</h2>
        <button onClick={() => openModal()} className="btn-primary flex items-center gap-2" data-testid="add-task-btn">
          <Plus className="w-4 h-4" /> Assign Task
        </button>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select className="select-field" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <select className="select-field" value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
          {taskTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {tasks.map(task => {
          const TypeIcon = getTypeIcon(task.task_type);
          return (
            <div key={task.id} className={`card flex flex-col sm:flex-row items-start sm:items-center gap-4 ${task.status === 'completed' ? 'opacity-60' : ''}`} data-testid={`task-${task.id}`}>
              <button onClick={() => handleStatusChange(task.id, task.status === 'completed' ? 'pending' : 'completed')} className="flex-shrink-0">
                {task.status === 'completed' ? <CheckSquare className="w-6 h-6 text-green-500" /> : <Square className="w-6 h-6 text-gray-300 hover:text-purple-500" />}
              </button>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <TypeIcon className={`w-4 h-4 ${getPriorityColor(task.priority)}`} />
                  <h3 className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-900'}`}>{task.title}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(task.status)}`}>{task.status?.replace('_', ' ')}</span>
                  {task.priority === 'urgent' && <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">Urgent</span>}
                </div>
                {task.description && <p className="text-sm text-gray-500 mb-1">{task.description}</p>}
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{task.assigned_to_name || 'Unassigned'}</span>
                  {task.due_date && <span className={`flex items-center gap-1 ${isOverdue(task.due_date) && task.status !== 'completed' ? 'text-red-500' : ''}`}><Calendar className="w-3 h-3" />{task.due_date}{isOverdue(task.due_date) && task.status !== 'completed' && ' (Overdue)'}</span>}
                  {task.material_title && <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{task.material_title}</span>}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <select className="select-field text-sm py-1" value={task.status} onChange={(e) => handleStatusChange(task.id, e.target.value)}>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button onClick={() => openModal(task)} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg"><Edit className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(task.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
        {tasks.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <ListTodo className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No tasks found. Assign your first task!</p>
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={closeModal} title={editTask ? 'Edit Task' : 'Assign New Task'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Task Title *</label>
            <input type="text" className="input-field" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required placeholder="e.g., Complete Bible Study Chapter 3" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assign To *</label>
              <select className="select-field" value={formData.assigned_to} onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })} required>
                <option value="">Select Member</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
              <select className="select-field" value={formData.task_type} onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}>
                {taskTypes.filter(t => t.id !== 'all').map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input type="date" className="input-field" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select className="select-field" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                {priorities.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link to Material (Optional)</label>
            <select className="select-field" value={formData.material_id} onChange={(e) => setFormData({ ...formData, material_id: e.target.value })}>
              <option value="">No material linked</option>
              {materials.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea className="input-field" rows="2" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Additional details about the task..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea className="input-field" rows="2" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Private notes (only visible to admins)" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{editTask ? 'Update Task' : 'Assign Task'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Church Loans Component
const ChurchLoans = () => {
  const [loans, setLoans] = useState([]);
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editLoan, setEditLoan] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [stats, setStats] = useState({});
  const [formData, setFormData] = useState({ member_id: '', loan_type: 'personal', amount: '', interest_rate: '0', term_months: '12', purpose: '', collateral: '', guarantor_name: '', guarantor_phone: '', notes: '' });
  const [paymentData, setPaymentData] = useState({ amount: '', payment_date: '', payment_method: 'cash', notes: '' });

  const loanTypes = [
    { id: 'personal', label: 'Personal' },
    { id: 'emergency', label: 'Emergency' },
    { id: 'education', label: 'Education' },
    { id: 'medical', label: 'Medical' },
    { id: 'housing', label: 'Housing' },
    { id: 'business', label: 'Business' }
  ];

  useEffect(() => { fetchLoans(); fetchMembers(); fetchStats(); }, [selectedStatus]);

  const fetchLoans = async () => {
    try {
      const params = selectedStatus !== 'all' ? `?status=${selectedStatus}` : '';
      const r = await api.get(`/api/loans${params}`);
      setLoans(r.data.loans);
    } catch (e) { console.error(e); }
  };

  const fetchMembers = async () => {
    try { const r = await api.get('/api/members'); setMembers(r.data.members); } catch (e) { console.error(e); }
  };

  const fetchStats = async () => {
    try { const r = await api.get('/api/loans/summary/stats'); setStats(r.data); } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData, amount: parseFloat(formData.amount), interest_rate: parseFloat(formData.interest_rate), term_months: parseInt(formData.term_months) };
      if (editLoan) {
        await api.put(`/api/loans/${editLoan.id}`, submitData);
      } else {
        await api.post('/api/loans', submitData);
      }
      fetchLoans(); fetchStats();
      closeModal();
    } catch (e) { console.error(e); alert('Error saving loan'); }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/loans/${selectedLoan.id}/payments?amount=${parseFloat(paymentData.amount)}&payment_date=${paymentData.payment_date || new Date().toISOString().split('T')[0]}&payment_method=${paymentData.payment_method}&notes=${paymentData.notes || ''}`);
      fetchLoans(); fetchStats();
      setShowPaymentModal(false);
      setPaymentData({ amount: '', payment_date: '', payment_method: 'cash', notes: '' });
      alert('Payment recorded successfully!');
    } catch (e) { console.error(e); alert('Error recording payment'); }
  };

  const handleDisburse = async (loanId) => {
    if (window.confirm('Mark this loan as disbursed?')) {
      try {
        await api.post(`/api/loans/${loanId}/disburse`);
        fetchLoans();
      } catch (e) { alert('Error disbursing loan'); }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this loan and all associated payments?')) {
      try { await api.delete(`/api/loans/${id}`); fetchLoans(); fetchStats(); } catch (e) { alert('Failed to delete'); }
    }
  };

  const openModal = (loan = null) => {
    if (loan) {
      setEditLoan(loan);
      setFormData({ member_id: loan.member_id, loan_type: loan.loan_type, amount: loan.amount.toString(), interest_rate: loan.interest_rate.toString(), term_months: loan.term_months.toString(), purpose: loan.purpose || '', collateral: loan.collateral || '', guarantor_name: loan.guarantor_name || '', guarantor_phone: loan.guarantor_phone || '', notes: loan.notes || '' });
    } else {
      setEditLoan(null);
      setFormData({ member_id: '', loan_type: 'personal', amount: '', interest_rate: '0', term_months: '12', purpose: '', collateral: '', guarantor_name: '', guarantor_phone: '', notes: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditLoan(null); };

  const openPaymentModal = (loan) => {
    setSelectedLoan(loan);
    setPaymentData({ amount: loan.monthly_payment?.toString() || '', payment_date: new Date().toISOString().split('T')[0], payment_method: 'cash', notes: '' });
    setShowPaymentModal(true);
  };

  const openDetailsModal = async (loan) => {
    try {
      const r = await api.get(`/api/loans/${loan.id}`);
      setSelectedLoan(r.data.loan);
      setShowDetailsModal(true);
    } catch (e) { console.error(e); }
  };

  const getStatusColor = (status) => {
    const colors = { active: 'bg-blue-100 text-blue-700', paid_off: 'bg-green-100 text-green-700', defaulted: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-500' };
    return colors[status] || colors.active;
  };

  return (
    <div className="space-y-6" data-testid="loans-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center gap-3">
            <Banknote className="w-8 h-8 opacity-80" />
            <div><p className="text-blue-100 text-sm">Total Disbursed</p><p className="text-2xl font-bold">${stats.total_disbursed?.toLocaleString() || 0}</p></div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="flex items-center gap-3">
            <CircleDollarSign className="w-8 h-8 opacity-80" />
            <div><p className="text-green-100 text-sm">Total Collected</p><p className="text-2xl font-bold">${stats.total_collected?.toLocaleString() || 0}</p></div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 opacity-80" />
            <div><p className="text-orange-100 text-sm">Outstanding</p><p className="text-2xl font-bold">${stats.outstanding_balance?.toLocaleString() || 0}</p></div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 opacity-80" />
            <div><p className="text-purple-100 text-sm">Active Loans</p><p className="text-2xl font-bold">{stats.active_loans || 0}</p></div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <select className="select-field" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            <option value="all">All Loans</option>
            <option value="active">Active</option>
            <option value="paid_off">Paid Off</option>
            <option value="defaulted">Defaulted</option>
          </select>
        </div>
        <button onClick={() => openModal()} className="btn-primary flex items-center gap-2" data-testid="add-loan-btn">
          <Plus className="w-4 h-4" /> New Loan
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="table-header">Member</th>
              <th className="table-header">Type</th>
              <th className="table-header">Amount</th>
              <th className="table-header">Remaining</th>
              <th className="table-header">Monthly</th>
              <th className="table-header">Status</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loans.map((loan) => (
              <tr key={loan.id} className="hover:bg-gray-50">
                <td className="table-cell">
                  <div>
                    <p className="font-medium">{loan.member_name}</p>
                    <p className="text-sm text-gray-500">{loan.member_phone}</p>
                  </div>
                </td>
                <td className="table-cell capitalize">{loan.loan_type}</td>
                <td className="table-cell font-medium">${loan.amount?.toLocaleString()}</td>
                <td className="table-cell">
                  <span className={loan.remaining_balance > 0 ? 'text-orange-600' : 'text-green-600'}>
                    ${loan.remaining_balance?.toLocaleString()}
                  </span>
                </td>
                <td className="table-cell">${loan.monthly_payment?.toLocaleString()}</td>
                <td className="table-cell"><span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(loan.status)}`}>{loan.status?.replace('_', ' ')}</span></td>
                <td className="table-cell">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openDetailsModal(loan)} className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded" title="View Details"><Eye className="w-4 h-4" /></button>
                    {loan.status === 'active' && (
                      <button onClick={() => openPaymentModal(loan)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded" title="Record Payment"><DollarSign className="w-4 h-4" /></button>
                    )}
                    {!loan.disbursement_date && loan.status === 'active' && (
                      <button onClick={() => handleDisburse(loan.id)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Disburse"><Banknote className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => openModal(loan)} className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(loan.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loans.length === 0 && <div className="text-center py-8 text-gray-500">No loans found. Create your first loan!</div>}
      </div>

      {/* Create/Edit Loan Modal */}
      <Modal isOpen={showModal} onClose={closeModal} title={editLoan ? 'Edit Loan' : 'New Loan'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member *</label>
            <select className="select-field" value={formData.member_id} onChange={(e) => setFormData({ ...formData, member_id: e.target.value })} required>
              <option value="">Select member...</option>
              {members.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type</label>
              <select className="select-field" value={formData.loan_type} onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}>
                {loanTypes.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
              <input type="number" step="0.01" className="input-field" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Interest Rate (%)</label>
              <input type="number" step="0.01" className="input-field" value={formData.interest_rate} onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Term (Months) *</label>
              <input type="number" className="input-field" value={formData.term_months} onChange={(e) => setFormData({ ...formData, term_months: e.target.value })} required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
            <textarea className="input-field" rows="2" value={formData.purpose} onChange={(e) => setFormData({ ...formData, purpose: e.target.value })} placeholder="Reason for the loan..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Guarantor Name</label>
              <input type="text" className="input-field" value={formData.guarantor_name} onChange={(e) => setFormData({ ...formData, guarantor_name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Guarantor Phone</label>
              <input type="tel" className="input-field" value={formData.guarantor_phone} onChange={(e) => setFormData({ ...formData, guarantor_phone: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Collateral</label>
            <input type="text" className="input-field" value={formData.collateral} onChange={(e) => setFormData({ ...formData, collateral: e.target.value })} placeholder="Any collateral offered..." />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{editLoan ? 'Update' : 'Create Loan'}</button>
          </div>
        </form>
      </Modal>

      {/* Record Payment Modal */}
      <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="Record Payment">
        <form onSubmit={handlePayment} className="space-y-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-500">Loan Balance</p>
            <p className="text-2xl font-bold text-gray-900">${selectedLoan?.remaining_balance?.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Monthly Payment: ${selectedLoan?.monthly_payment?.toLocaleString()}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount *</label>
            <input type="number" step="0.01" className="input-field" value={paymentData.amount} onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
              <input type="date" className="input-field" value={paymentData.payment_date} onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
              <select className="select-field" value={paymentData.payment_method} onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="transfer">Bank Transfer</option>
                <option value="mobile">Mobile Payment</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input type="text" className="input-field" value={paymentData.notes} onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowPaymentModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Record Payment</button>
          </div>
        </form>
      </Modal>

      {/* Loan Details Modal */}
      <Modal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} title="Loan Details">
        {selectedLoan && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-sm text-gray-500">Member</p><p className="font-medium">{selectedLoan.member_name}</p></div>
              <div><p className="text-sm text-gray-500">Status</p><span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedLoan.status)}`}>{selectedLoan.status}</span></div>
              <div><p className="text-sm text-gray-500">Loan Amount</p><p className="font-medium">${selectedLoan.amount?.toLocaleString()}</p></div>
              <div><p className="text-sm text-gray-500">Remaining</p><p className="font-medium text-orange-600">${selectedLoan.remaining_balance?.toLocaleString()}</p></div>
              <div><p className="text-sm text-gray-500">Interest Rate</p><p className="font-medium">{selectedLoan.interest_rate}%</p></div>
              <div><p className="text-sm text-gray-500">Term</p><p className="font-medium">{selectedLoan.term_months} months</p></div>
            </div>
            {selectedLoan.purpose && <div><p className="text-sm text-gray-500">Purpose</p><p>{selectedLoan.purpose}</p></div>}
            {selectedLoan.guarantor_name && <div><p className="text-sm text-gray-500">Guarantor</p><p>{selectedLoan.guarantor_name} - {selectedLoan.guarantor_phone}</p></div>}
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Payment History ({selectedLoan.payments?.length || 0})</h4>
              {selectedLoan.payments?.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedLoan.payments.map(p => (
                    <div key={p.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">${p.amount?.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{p.payment_method}</p>
                      </div>
                      <p className="text-sm text-gray-500">{p.payment_date}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No payments recorded yet.</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// Employees Component
const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [entryMode, setEntryMode] = useState('member'); // 'member' or 'manual'
  const [formData, setFormData] = useState({ 
    member_id: '', 
    first_name: '', 
    last_name: '', 
    email: '', 
    phone: '',
    position: '', 
    department: '', 
    hire_date: '', 
    salary_type: 'monthly', 
    salary_amount: '',
    work_phone: '',
    work_email: '',
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });

  useEffect(() => { fetchEmployees(); fetchMembers(); }, []);

  const fetchEmployees = async () => { try { const r = await api.get('/api/employees'); setEmployees(r.data.employees); } catch (e) { console.error(e); } };
  const fetchMembers = async () => { try { const r = await api.get('/api/members'); setMembers(r.data.members); } catch (e) { console.error(e); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData, salary_amount: parseFloat(formData.salary_amount) };
      // Clear member_id if manual entry
      if (entryMode === 'manual') {
        submitData.member_id = null;
      } else {
        // Clear manual fields if using member
        submitData.first_name = null;
        submitData.last_name = null;
      }
      
      if (editEmployee) {
        await api.put(`/api/employees/${editEmployee.id}`, submitData);
      } else {
        await api.post('/api/employees', submitData);
      }
      fetchEmployees();
      closeModal();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee? This action cannot be undone.')) {
      try { 
        await api.delete(`/api/employees/${id}`); 
        fetchEmployees(); 
        alert('Employee deleted successfully');
      } catch (e) { 
        console.error(e); 
        alert('Failed to delete employee: ' + (e.response?.data?.detail || e.message)); 
      }
    }
  };

  const openModal = (employee = null) => {
    if (employee) {
      setEditEmployee(employee);
      setEntryMode(employee.member_id ? 'member' : 'manual');
      setFormData({
        member_id: employee.member_id || '',
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        email: employee.email || '',
        phone: employee.phone || '',
        position: employee.position,
        department: employee.department,
        hire_date: employee.hire_date,
        salary_type: employee.salary_type,
        salary_amount: employee.salary_amount?.toString() || '',
        work_phone: employee.work_phone || '',
        work_email: employee.work_email || '',
        emergency_contact_name: employee.emergency_contact_name || '',
        emergency_contact_phone: employee.emergency_contact_phone || ''
      });
    } else {
      setEditEmployee(null);
      setEntryMode('member');
      setFormData({ member_id: '', first_name: '', last_name: '', email: '', phone: '', position: '', department: '', hire_date: '', salary_type: 'monthly', salary_amount: '', work_phone: '', work_email: '', emergency_contact_name: '', emergency_contact_phone: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditEmployee(null); };

  const getEmployeeName = (emp) => {
    if (emp.first_name) return `${emp.first_name} ${emp.last_name}`;
    if (emp.member) return `${emp.member.first_name} ${emp.member.last_name}`;
    return 'Unknown';
  };

  const getEmployeeContact = (emp) => {
    return {
      email: emp.email || emp.member?.email,
      phone: emp.phone || emp.member?.phone,
      workPhone: emp.work_phone,
      workEmail: emp.work_email
    };
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Employees</h2>
        <button onClick={() => openModal()} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Add Employee</button>
      </div>
      
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="table-header">Employee</th>
              <th className="table-header">Position</th>
              <th className="table-header">Department</th>
              <th className="table-header">Contact</th>
              <th className="table-header">Salary</th>
              <th className="table-header">Status</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {employees.map((emp) => {
              const contact = getEmployeeContact(emp);
              return (
                <tr key={emp.id} className="hover:bg-gray-50">
                  <td className="table-cell">
                    <div className="flex items-center gap-3">
                      {emp.member?.photo ? 
                        <img src={emp.member.photo} alt="" className="w-10 h-10 rounded-full object-cover" /> : 
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-purple-600" /></div>
                      }
                      <div>
                        <p className="font-medium">{getEmployeeName(emp)}</p>
                        <p className="text-sm text-gray-500">{emp.member_id ? 'Member' : 'Manual Entry'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell">{emp.position}</td>
                  <td className="table-cell">{emp.department}</td>
                  <td className="table-cell">
                    <div className="text-sm space-y-1">
                      {contact.workPhone && <div className="flex items-center gap-1 text-purple-600"><Phone className="w-3 h-3" />{contact.workPhone}</div>}
                      {!contact.workPhone && contact.phone && <div className="flex items-center gap-1 text-gray-500"><Phone className="w-3 h-3" />{contact.phone}</div>}
                      {contact.workEmail && <div className="flex items-center gap-1 text-purple-600"><Mail className="w-3 h-3" />{contact.workEmail}</div>}
                      {!contact.workEmail && contact.email && <div className="flex items-center gap-1 text-gray-500 truncate max-w-[150px]"><Mail className="w-3 h-3" />{contact.email}</div>}
                    </div>
                  </td>
                  <td className="table-cell">${emp.salary_amount?.toLocaleString()} / {emp.salary_type}</td>
                  <td className="table-cell"><span className={`badge ${emp.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{emp.status}</span></td>
                  <td className="table-cell">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openModal(emp)} className="text-purple-600 hover:text-purple-800"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(emp.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-employee-${emp.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {employees.length === 0 && <div className="text-center py-8 text-gray-500">No employees yet. Add your first employee!</div>}
      </div>
      
      <Modal isOpen={showModal} onClose={closeModal} title={editEmployee ? 'Edit Employee' : 'Add Employee'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Entry Mode Toggle */}
          {!editEmployee && (
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button type="button" onClick={() => setEntryMode('member')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${entryMode === 'member' ? 'bg-white shadow text-purple-600' : 'text-gray-600'}`}>
                From Member
              </button>
              <button type="button" onClick={() => setEntryMode('manual')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${entryMode === 'manual' ? 'bg-white shadow text-purple-600' : 'text-gray-600'}`}>
                Manual Entry
              </button>
            </div>
          )}
          
          {entryMode === 'member' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Member *</label>
              <select className="select-field" value={formData.member_id} onChange={(e) => setFormData({ ...formData, member_id: e.target.value })} required>
                <option value="">Select a member...</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
              </select>
            </div>
          ) : (
            <div className="space-y-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Personal Information</p>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label><input type="text" className="input-field" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required={entryMode === 'manual'} /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label><input type="text" className="input-field" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required={entryMode === 'manual'} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" className="input-field" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="tel" className="input-field" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Position *</label><input type="text" className="input-field" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Department *</label><input type="text" className="input-field" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} required /></div>
          </div>
          
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Hire Date *</label><input type="date" className="input-field" value={formData.hire_date} onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })} required /></div>
          
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Salary Type</label><select className="select-field" value={formData.salary_type} onChange={(e) => setFormData({ ...formData, salary_type: e.target.value })}><option value="hourly">Hourly</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label><input type="number" step="0.01" className="input-field" value={formData.salary_amount} onChange={(e) => setFormData({ ...formData, salary_amount: e.target.value })} required /></div>
          </div>
          
          {/* Staff Contact Info */}
          <div className="space-y-4 p-3 bg-purple-50 rounded-lg">
            <p className="text-sm font-medium text-purple-700 flex items-center gap-2"><Phone className="w-4 h-4" /> Staff Contact Information</p>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Work Phone</label><input type="tel" className="input-field" placeholder="Work phone number" value={formData.work_phone} onChange={(e) => setFormData({ ...formData, work_phone: e.target.value })} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Work Email</label><input type="email" className="input-field" placeholder="Work email" value={formData.work_email} onChange={(e) => setFormData({ ...formData, work_email: e.target.value })} /></div>
            </div>
          </div>
          
          {/* Emergency Contact */}
          <div className="space-y-4 p-3 bg-orange-50 rounded-lg">
            <p className="text-sm font-medium text-orange-700 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Emergency Contact</p>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label><input type="text" className="input-field" placeholder="Emergency contact name" value={formData.emergency_contact_name} onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label><input type="tel" className="input-field" placeholder="Emergency contact phone" value={formData.emergency_contact_phone} onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })} /></div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={closeModal} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">{editEmployee ? 'Update' : 'Save'}</button></div>
        </form>
      </Modal>
    </div>
  );
};

// HR & Hiring Component
const HRHiring = () => {
  const [activeTab, setActiveTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [onboarding, setOnboarding] = useState([]);
  const [exitPackages, setExitPackages] = useState([]);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showHireModal, setShowHireModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);
  const [jobForm, setJobForm] = useState({ title: '', department: '', description: '', requirements: '', salary_range: '', employment_type: 'full_time' });
  const [hireForm, setHireForm] = useState({ position: '', department: '', hire_date: '', salary_type: 'monthly', salary_amount: '' });
  const [exitForm, setExitForm] = useState({
    employee_id: '', termination_type: 'resignation', termination_reason: '', last_work_date: '', notice_date: '',
    regular_pay: 0, unused_pto_hours: 0, pto_payout: 0, severance_pay: 0, deductions: 0,
    benefits_end_date: '', cobra_eligible: false, exit_interview_notes: '', rehire_eligible: true, notes: ''
  });

  useEffect(() => { fetchJobs(); fetchApplications(); fetchOnboarding(); fetchExitPackages(); fetchStaffMembers(); }, []);

  const fetchJobs = async () => { try { const r = await api.get('/api/hr/job-postings'); setJobs(r.data.job_postings); } catch (e) { console.error(e); } };
  const fetchApplications = async () => { try { const r = await api.get('/api/hr/applications'); setApplications(r.data.applications); } catch (e) { console.error(e); } };
  const fetchOnboarding = async () => { try { const r = await api.get('/api/hr/onboarding'); setOnboarding(r.data.onboarding_records); } catch (e) { console.error(e); } };
  const fetchExitPackages = async () => { try { const r = await api.get('/api/hr/exit-packages'); setExitPackages(r.data.exit_packages || []); } catch (e) { console.error(e); } };
  const fetchStaffMembers = async () => { 
    try { 
      // Fetch both staff members and employees
      const [membersRes, employeesRes] = await Promise.all([
        api.get('/api/members'),
        api.get('/api/employees')
      ]);
      const staffFromMembers = (membersRes.data.members || []).filter(m => m.is_staff);
      const employees = employeesRes.data.employees || [];
      
      // Combine and deduplicate by email or name
      const combined = [...staffFromMembers];
      employees.forEach(emp => {
        const exists = combined.some(s => 
          (s.email && emp.email && s.email === emp.email) || 
          (s.first_name === emp.first_name && s.last_name === emp.last_name)
        );
        if (!exists) {
          combined.push({ ...emp, source: 'employee' });
        }
      });
      
      setStaffMembers(combined);
    } catch (e) { console.error(e); } 
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    try { await api.post(`/api/hr/job-postings?title=${jobForm.title}&department=${jobForm.department}&description=${jobForm.description}&requirements=${jobForm.requirements}&salary_range=${jobForm.salary_range}&employment_type=${jobForm.employment_type}`); fetchJobs(); setShowJobModal(false); } catch (e) { console.error(e); }
  };

  const handleHire = async (e) => {
    e.preventDefault();
    try { await api.post(`/api/hr/applications/${selectedApp.id}/hire?position=${hireForm.position}&department=${hireForm.department}&hire_date=${hireForm.hire_date}&salary_type=${hireForm.salary_type}&salary_amount=${hireForm.salary_amount}`); fetchApplications(); fetchOnboarding(); setShowHireModal(false); } catch (e) { console.error(e); alert(e.response?.data?.detail || 'Error'); }
  };

  const updateAppStatus = async (id, status) => { try { await api.put(`/api/hr/applications/${id}/status?status=${status}`); fetchApplications(); } catch (e) { console.error(e); } };

  const openHireModal = (app) => { setSelectedApp(app); setHireForm({ position: '', department: '', hire_date: '', salary_type: 'monthly', salary_amount: '' }); setShowHireModal(true); };

  return (
    <div className="space-y-6" data-testid="hr-page">
      <div className="flex gap-2 mb-6 flex-wrap">
        {['jobs', 'applications', 'onboarding', 'exit'].map((t) => (
          <button key={t} onClick={() => setActiveTab(t)} className={`px-4 py-2 rounded-lg ${activeTab === t ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
            {t === 'jobs' ? 'Job Postings' : t === 'applications' ? 'Applications' : t === 'onboarding' ? 'Onboarding' : 'Exit Packages'}
          </button>
        ))}
      </div>

      {activeTab === 'jobs' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <p className="text-sm text-blue-800">
                <strong>Public Careers Page:</strong>{' '}
                <a href="/#/careers" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  View Careers Page
                </a>
              </p>
            </div>
            <button onClick={() => setShowJobModal(true)} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Post Job</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jobs.map((j) => (
              <div key={j.id} className="card">
                <div className="flex justify-between items-start">
                  <div><h3 className="font-semibold text-lg">{j.title}</h3><p className="text-sm text-gray-500">{j.department} - {j.employment_type}</p></div>
                  <span className={`badge ${j.status === 'open' ? 'badge-success' : 'badge-warning'}`}>{j.status}</span>
                </div>
                <p className="text-gray-600 mt-2 text-sm">{j.description?.substring(0, 100)}...</p>
                {j.salary_range && <p className="text-purple-600 font-medium mt-2">{j.salary_range}</p>}
                <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2">
                  <button 
                    onClick={() => { 
                      const link = window.location.origin + '/#/careers/' + j.id; 
                      navigator.clipboard.writeText(link); 
                      alert('Job link copied!'); 
                    }}
                    className="btn-secondary text-xs px-3 py-2 flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> Copy Link
                  </button>
                  <button
                    onClick={() => window.open('/#/careers/' + j.id, '_blank')}
                    className="btn-primary text-xs px-3 py-2 flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> Preview
                  </button>
                  <button 
                    onClick={() => api.put('/api/hr/job-postings/' + j.id + '/status?status=' + (j.status === 'open' ? 'closed' : 'open')).then(fetchJobs)} 
                    className={`text-xs px-3 py-1 rounded ${j.status === 'open' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}
                  >
                    {j.status === 'open' ? 'Close' : 'Reopen'}
                  </button>
                </div>
              </div>
            ))}
            {jobs.length === 0 && (
              <div className="col-span-2 text-center py-12 bg-gray-50 rounded-xl">
                <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No job postings yet</p>
                <button onClick={() => setShowJobModal(true)} className="btn-primary mt-4">Post Your First Job</button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'applications' && (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50"><tr><th className="table-header">Applicant</th><th className="table-header">Position</th><th className="table-header">Email</th><th className="table-header">Status</th><th className="table-header">Actions</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {applications.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="table-cell font-medium">{a.first_name} {a.last_name}</td>
                  <td className="table-cell">{a.job_title}</td>
                  <td className="table-cell text-gray-500">{a.email}</td>
                  <td className="table-cell"><span className={`badge ${a.status === 'hired' ? 'badge-success' : a.status === 'rejected' ? 'badge-danger' : 'badge-info'}`}>{a.status}</span></td>
                  <td className="table-cell">
                    {a.status !== 'hired' && a.status !== 'rejected' && (
                      <div className="flex gap-2">
                        <button onClick={() => updateAppStatus(a.id, 'interview')} className="text-blue-600 text-sm">Interview</button>
                        <button onClick={() => openHireModal(a)} className="text-green-600 text-sm font-medium">Hire</button>
                        <button onClick={() => updateAppStatus(a.id, 'rejected')} className="text-red-600 text-sm">Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'onboarding' && (
        <div className="space-y-4">
          {onboarding.map((o) => (
            <div key={o.id} className="card">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center"><UserCheck className="w-6 h-6 text-purple-600" /></div>
                <div><h3 className="font-semibold">{o.employee?.member?.first_name} {o.employee?.member?.last_name}</h3><p className="text-sm text-gray-500">{o.employee?.position}</p></div>
              </div>
              <div className="space-y-2">
                {o.tasks?.map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="checkbox" checked={t.completed} onChange={async () => { try { await api.put(`/api/hr/onboarding/${o.id}/task?task_name=${encodeURIComponent(t.name)}&completed=${!t.completed}`); fetchOnboarding(); } catch (e) { console.error(e); } }} className="w-4 h-4" />
                    <span className={t.completed ? 'line-through text-gray-400' : ''}>{t.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {onboarding.length === 0 && <div className="text-center py-8 text-gray-500">No onboarding records</div>}
        </div>
      )}

      {/* Exit Packages Tab */}
      {activeTab === 'exit' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Exit/Termination Packages</h3>
            <button onClick={() => { setSelectedPackage(null); setExitForm({ employee_id: '', termination_type: 'resignation', termination_reason: '', last_work_date: '', notice_date: '', regular_pay: 0, unused_pto_hours: 0, pto_payout: 0, severance_pay: 0, deductions: 0, benefits_end_date: '', cobra_eligible: false, exit_interview_notes: '', rehire_eligible: true, notes: '' }); setShowExitModal(true); }} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Exit Package
            </button>
          </div>
          
          {exitPackages.length === 0 ? (
            <div className="card text-center py-12 text-gray-500">
              <UserCog className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No exit packages found</p>
              <button onClick={() => setShowExitModal(true)} className="mt-4 text-purple-600 hover:underline">Create your first exit package</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {exitPackages.map((pkg) => (
                <div key={pkg.id} className="card">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-semibold text-lg">{pkg.employee_name}</h4>
                      <p className="text-sm text-gray-500 capitalize">{pkg.termination_type?.replace('_', ' ')}</p>
                    </div>
                    <span className={`badge ${pkg.status === 'completed' ? 'badge-success' : pkg.status === 'approved' ? 'badge-info' : pkg.status === 'draft' ? 'badge-warning' : 'badge-secondary'}`}>
                      {pkg.status}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Last Work Date:</span><span>{pkg.last_work_date || 'TBD'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Final Pay:</span><span className="font-medium text-green-600">${(pkg.final_pay?.total_final_pay || 0).toLocaleString()}</span></div>
                    {pkg.termination_reason && <p className="text-gray-600 mt-2 text-xs bg-gray-50 p-2 rounded">Reason: {pkg.termination_reason}</p>}
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <button onClick={() => { setSelectedPackage(pkg); setExitForm({ ...pkg, employee_id: pkg.employee_id, regular_pay: pkg.final_pay?.regular_pay || 0, pto_payout: pkg.final_pay?.pto_payout || 0, severance_pay: pkg.final_pay?.severance_pay || 0, deductions: pkg.final_pay?.deductions || 0, unused_pto_hours: pkg.final_pay?.unused_pto_hours || 0 }); setShowExitModal(true); }} className="text-blue-600 hover:underline text-sm">Edit</button>
                    {pkg.status === 'draft' && <button onClick={async () => { try { await api.post(`/api/hr/exit-packages/${pkg.id}/approve`); fetchExitPackages(); } catch (e) { alert(e.response?.data?.detail || 'Error'); } }} className="text-green-600 hover:underline text-sm">Approve</button>}
                    {pkg.status === 'approved' && <button onClick={async () => { if(window.confirm('Complete this exit package? This will update the employee status.')) { try { await api.post(`/api/hr/exit-packages/${pkg.id}/complete`); fetchExitPackages(); } catch (e) { alert(e.response?.data?.detail || 'Error'); } } }} className="text-purple-600 hover:underline text-sm">Complete</button>}
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/hr/exit-packages/${pkg.id}/generate-letter`} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline text-sm flex items-center gap-1"><FileText className="w-3 h-3" /> Letter</a>
                    <button onClick={async () => { if(window.confirm('Delete this exit package?')) { try { await api.delete(`/api/hr/exit-packages/${pkg.id}`); fetchExitPackages(); } catch (e) { alert(e.response?.data?.detail || 'Error'); } } }} className="text-red-600 hover:underline text-sm ml-auto">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal isOpen={showJobModal} onClose={() => setShowJobModal(false)} title="Post New Job">
        <form onSubmit={handleCreateJob} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label><input type="text" className="input-field" value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Department *</label><input type="text" className="input-field" value={jobForm.department} onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Salary Range</label><input type="text" className="input-field" placeholder="e.g. $40,000 - $50,000" value={jobForm.salary_range} onChange={(e) => setJobForm({ ...jobForm, salary_range: e.target.value })} /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description *</label><textarea className="input-field" rows="3" value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label><textarea className="input-field" rows="2" value={jobForm.requirements} onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value })} /></div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowJobModal(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Post Job</button></div>
        </form>
      </Modal>

      <Modal isOpen={showHireModal} onClose={() => setShowHireModal(false)} title={`Hire ${selectedApp?.first_name} ${selectedApp?.last_name}`}>
        <form onSubmit={handleHire} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Position *</label><input type="text" className="input-field" value={hireForm.position} onChange={(e) => setHireForm({ ...hireForm, position: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Department *</label><input type="text" className="input-field" value={hireForm.department} onChange={(e) => setHireForm({ ...hireForm, department: e.target.value })} required /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Hire Date *</label><input type="date" className="input-field" value={hireForm.hire_date} onChange={(e) => setHireForm({ ...hireForm, hire_date: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Salary Type</label><select className="select-field" value={hireForm.salary_type} onChange={(e) => setHireForm({ ...hireForm, salary_type: e.target.value })}><option value="hourly">Hourly</option><option value="monthly">Monthly</option><option value="yearly">Yearly</option></select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Salary Amount *</label><input type="number" step="0.01" className="input-field" value={hireForm.salary_amount} onChange={(e) => setHireForm({ ...hireForm, salary_amount: e.target.value })} required /></div>
          </div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowHireModal(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-success">Hire Employee</button></div>
        </form>
      </Modal>

      {/* Exit Package Modal */}
      <Modal isOpen={showExitModal} onClose={() => setShowExitModal(false)} title={selectedPackage ? 'Edit Exit Package' : 'Create Exit Package'} size="lg">
        <form onSubmit={async (e) => {
          e.preventDefault();
          const totalFinalPay = (parseFloat(exitForm.regular_pay) || 0) + (parseFloat(exitForm.pto_payout) || 0) + (parseFloat(exitForm.severance_pay) || 0) - (parseFloat(exitForm.deductions) || 0);
          const payload = {
            ...exitForm,
            regular_pay: parseFloat(exitForm.regular_pay) || 0,
            pto_payout: parseFloat(exitForm.pto_payout) || 0,
            severance_pay: parseFloat(exitForm.severance_pay) || 0,
            deductions: parseFloat(exitForm.deductions) || 0,
            unused_pto_hours: parseFloat(exitForm.unused_pto_hours) || 0,
            total_final_pay: totalFinalPay
          };
          try {
            if (selectedPackage) {
              await api.put(`/api/hr/exit-packages/${selectedPackage.id}`, payload);
            } else {
              await api.post('/api/hr/exit-packages', payload);
            }
            fetchExitPackages();
            setShowExitModal(false);
          } catch (err) { alert(err.response?.data?.detail || 'Error saving exit package'); }
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
            <select className="select-field" value={exitForm.employee_id} onChange={(e) => setExitForm({ ...exitForm, employee_id: e.target.value })} required>
              <option value="">Select Staff Member</option>
              {staffMembers.map((m) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name} - {m.position || 'Staff'}</option>)}
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Termination Type *</label>
              <select className="select-field" value={exitForm.termination_type} onChange={(e) => setExitForm({ ...exitForm, termination_type: e.target.value })}>
                <option value="resignation">Resignation</option>
                <option value="termination">Termination</option>
                <option value="retirement">Retirement</option>
                <option value="layoff">Layoff</option>
                <option value="end_of_contract">End of Contract</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Work Date *</label>
              <input type="date" className="input-field" value={exitForm.last_work_date} onChange={(e) => setExitForm({ ...exitForm, last_work_date: e.target.value })} required />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Termination Reason</label>
            <textarea className="input-field" rows="2" value={exitForm.termination_reason} onChange={(e) => setExitForm({ ...exitForm, termination_reason: e.target.value })} placeholder="Reason for separation..." />
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Final Pay Calculation</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><label className="block text-xs text-gray-500 mb-1">Regular Pay</label><input type="number" step="0.01" className="input-field" value={exitForm.regular_pay} onChange={(e) => setExitForm({ ...exitForm, regular_pay: e.target.value })} /></div>
              <div><label className="block text-xs text-gray-500 mb-1">PTO Payout</label><input type="number" step="0.01" className="input-field" value={exitForm.pto_payout} onChange={(e) => setExitForm({ ...exitForm, pto_payout: e.target.value })} /></div>
              <div><label className="block text-xs text-gray-500 mb-1">Severance Pay</label><input type="number" step="0.01" className="input-field" value={exitForm.severance_pay} onChange={(e) => setExitForm({ ...exitForm, severance_pay: e.target.value })} /></div>
              <div><label className="block text-xs text-gray-500 mb-1">Deductions</label><input type="number" step="0.01" className="input-field" value={exitForm.deductions} onChange={(e) => setExitForm({ ...exitForm, deductions: e.target.value })} /></div>
            </div>
            <div className="mt-3 p-3 bg-green-50 rounded-lg">
              <span className="text-sm text-gray-600">Total Final Pay: </span>
              <span className="text-lg font-bold text-green-600">
                ${((parseFloat(exitForm.regular_pay) || 0) + (parseFloat(exitForm.pto_payout) || 0) + (parseFloat(exitForm.severance_pay) || 0) - (parseFloat(exitForm.deductions) || 0)).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Benefits End Date</label>
              <input type="date" className="input-field" value={exitForm.benefits_end_date} onChange={(e) => setExitForm({ ...exitForm, benefits_end_date: e.target.value })} />
            </div>
            <div className="flex items-center gap-4 pt-6">
              <label className="flex items-center gap-2"><input type="checkbox" checked={exitForm.cobra_eligible} onChange={(e) => setExitForm({ ...exitForm, cobra_eligible: e.target.checked })} className="rounded" /> COBRA Eligible</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={exitForm.rehire_eligible} onChange={(e) => setExitForm({ ...exitForm, rehire_eligible: e.target.checked })} className="rounded" /> Rehire Eligible</label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exit Interview Notes</label>
            <textarea className="input-field" rows="3" value={exitForm.exit_interview_notes} onChange={(e) => setExitForm({ ...exitForm, exit_interview_notes: e.target.value })} placeholder="Notes from exit interview..." />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowExitModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{selectedPackage ? 'Update' : 'Create'} Exit Package</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Payroll & W2 Component - QuickBooks Style
const Payroll = ({ autoOpenModal = false }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [payrolls, setPayrolls] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [calendar, setCalendar] = useState([]);
  const [w2Forms, setW2Forms] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [showW2Modal, setShowW2Modal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedW2, setSelectedW2] = useState(null);
  const [w2Year, setW2Year] = useState(new Date().getFullYear());
  const [scheduleForm, setScheduleForm] = useState({ name: '', frequency: 'biweekly', pay_day: 5, second_pay_day: 20, start_date: '', is_active: true });
  const [runForm, setRunForm] = useState({ schedule_id: '', pay_period_start: '', pay_period_end: '', pay_date: '' });

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (autoOpenModal) setShowScheduleModal(true); }, [autoOpenModal]);

  const fetchAll = async () => {
    try {
      const [payrollRes, scheduleRes, runsRes, dashRes, calRes, w2Res] = await Promise.all([
        api.get('/api/payroll'),
        api.get('/api/pay-schedules'),
        api.get('/api/payroll-runs'),
        api.get('/api/payroll/dashboard'),
        api.get('/api/payroll/calendar'),
        api.get('/api/hr/w2-forms')
      ]);
      setPayrolls(payrollRes.data.payrolls || []);
      setSchedules(scheduleRes.data.schedules || []);
      setPayrollRuns(runsRes.data.runs || []);
      setDashboard(dashRes.data || {});
      setCalendar(calRes.data.calendar || []);
      setW2Forms(w2Res.data.w2_forms || []);
    } catch (e) { console.error(e); }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedSchedule) {
        await api.put(`/api/pay-schedules/${selectedSchedule.id}`, scheduleForm);
      } else {
        await api.post('/api/pay-schedules', scheduleForm);
      }
      fetchAll();
      setShowScheduleModal(false);
      setSelectedSchedule(null);
      setScheduleForm({ name: '', frequency: 'biweekly', pay_day: 5, second_pay_day: 20, start_date: '', is_active: true });
    } catch (e) { alert(e.response?.data?.detail || 'Error saving schedule'); }
  };

  const handleRunPayroll = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/payroll-runs?schedule_id=${runForm.schedule_id}&pay_period_start=${runForm.pay_period_start}&pay_period_end=${runForm.pay_period_end}&pay_date=${runForm.pay_date}`);
      fetchAll();
      setShowRunModal(false);
      alert('Payroll run created successfully!');
    } catch (e) { alert(e.response?.data?.detail || 'Error creating payroll run'); }
  };

  const approveRun = async (runId) => {
    try { await api.put(`/api/payroll-runs/${runId}/approve`); fetchAll(); alert('Payroll approved!'); } catch (e) { alert('Error approving'); }
  };

  const payRun = async (runId) => {
    try { await api.put(`/api/payroll-runs/${runId}/pay?payment_method=direct_deposit`); fetchAll(); alert('Payroll paid!'); } catch (e) { alert('Error paying'); }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Delete this pay schedule?')) return;
    try { await api.delete(`/api/pay-schedules/${id}`); fetchAll(); } catch (e) { alert('Error deleting'); }
  };

  const editSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleForm({ ...schedule });
    setShowScheduleModal(true);
  };

  const openRunModal = (schedule) => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    setRunForm({
      schedule_id: schedule?.id || '',
      pay_period_start: startDate,
      pay_period_end: endDate,
      pay_date: endDate
    });
    setShowRunModal(true);
  };

  const generateW2s = async () => { try { await api.post(`/api/hr/w2-forms/generate?tax_year=${w2Year}`); fetchAll(); alert('W2 forms generated!'); } catch (e) { console.error(e); } };

  const frequencyLabels = { weekly: 'Weekly', biweekly: 'Bi-Weekly', semimonthly: 'Semi-Monthly', monthly: 'Monthly' };
  const statusColors = { draft: 'bg-gray-100 text-gray-700', pending: 'bg-yellow-100 text-yellow-700', approved: 'bg-blue-100 text-blue-700', paid: 'bg-green-100 text-green-700', processing: 'bg-purple-100 text-purple-700' };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b pb-2">
        <button onClick={() => setActiveTab('dashboard')} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'dashboard' ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}><Home className="w-4 h-4 inline mr-2" />Dashboard</button>
        <button onClick={() => setActiveTab('schedules')} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'schedules' ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}><Calendar className="w-4 h-4 inline mr-2" />Pay Schedules</button>
        <button onClick={() => setActiveTab('runs')} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'runs' ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}><FileSpreadsheet className="w-4 h-4 inline mr-2" />Payroll Runs</button>
        <button onClick={() => setActiveTab('history')} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'history' ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}><Clock className="w-4 h-4 inline mr-2" />History</button>
        <button onClick={() => setActiveTab('w2')} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'w2' ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}><FileText className="w-4 h-4 inline mr-2" />W2 Forms</button>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl"><DollarSign className="w-6 h-6" /></div>
                <div>
                  <p className="text-green-100 text-sm">Gross This Month</p>
                  <p className="text-2xl font-bold">${(dashboard.total_gross_month || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl"><Wallet className="w-6 h-6" /></div>
                <div>
                  <p className="text-blue-100 text-sm">Net Payroll</p>
                  <p className="text-2xl font-bold">${(dashboard.total_net_month || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl"><Users className="w-6 h-6" /></div>
                <div>
                  <p className="text-purple-100 text-sm">Staff Members</p>
                  <p className="text-2xl font-bold">{dashboard.total_staff || 0}</p>
                </div>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl"><Clock className="w-6 h-6" /></div>
                <div>
                  <p className="text-orange-100 text-sm">Pending Approval</p>
                  <p className="text-2xl font-bold">{(dashboard.draft_count || 0) + (dashboard.pending_count || 0)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions & Upcoming */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Actions */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /> Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => openRunModal()} className="p-4 bg-purple-50 hover:bg-purple-100 rounded-xl text-left transition-colors">
                  <DollarSign className="w-8 h-8 text-purple-600 mb-2" />
                  <p className="font-medium">Run Payroll</p>
                  <p className="text-sm text-gray-500">Process paychecks</p>
                </button>
                <button onClick={() => setShowScheduleModal(true)} className="p-4 bg-blue-50 hover:bg-blue-100 rounded-xl text-left transition-colors">
                  <Calendar className="w-8 h-8 text-blue-600 mb-2" />
                  <p className="font-medium">Add Schedule</p>
                  <p className="text-sm text-gray-500">Create pay schedule</p>
                </button>
                <button onClick={() => setActiveTab('history')} className="p-4 bg-green-50 hover:bg-green-100 rounded-xl text-left transition-colors">
                  <FileText className="w-8 h-8 text-green-600 mb-2" />
                  <p className="font-medium">View History</p>
                  <p className="text-sm text-gray-500">Past payrolls</p>
                </button>
                <button onClick={() => setActiveTab('w2')} className="p-4 bg-yellow-50 hover:bg-yellow-100 rounded-xl text-left transition-colors">
                  <FileCheck className="w-8 h-8 text-yellow-600 mb-2" />
                  <p className="font-medium">W2 Forms</p>
                  <p className="text-sm text-gray-500">Tax documents</p>
                </button>
              </div>
            </div>

            {/* Upcoming Paydays */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-purple-600" /> Upcoming Paydays</h3>
              {calendar.length > 0 ? (
                <div className="space-y-3">
                  {calendar.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.schedule_name}</p>
                        <p className="text-sm text-gray-500">{item.period_start} to {item.period_end}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-purple-600">{new Date(item.pay_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">{frequencyLabels[item.frequency]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No upcoming paydays. Create a pay schedule first.</p>
              )}
            </div>
          </div>

          {/* Recent Payroll Runs */}
          {dashboard.recent_runs?.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-semibold mb-4">Recent Payroll Runs</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50"><tr><th className="table-header">Schedule</th><th className="table-header">Period</th><th className="table-header">Employees</th><th className="table-header">Total</th><th className="table-header">Status</th></tr></thead>
                  <tbody className="divide-y">
                    {dashboard.recent_runs.map(run => (
                      <tr key={run.id} className="hover:bg-gray-50">
                        <td className="table-cell font-medium">{run.schedule_name}</td>
                        <td className="table-cell text-sm text-gray-500">{run.pay_period_start} - {run.pay_period_end}</td>
                        <td className="table-cell">{run.employee_count}</td>
                        <td className="table-cell font-semibold">${run.total_net?.toLocaleString()}</td>
                        <td className="table-cell"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[run.status]}`}>{run.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pay Schedules Tab */}
      {activeTab === 'schedules' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Pay Schedules</h3>
            <button onClick={() => { setSelectedSchedule(null); setScheduleForm({ name: '', frequency: 'biweekly', pay_day: 5, second_pay_day: 20, start_date: '', is_active: true }); setShowScheduleModal(true); }} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Add Schedule</button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {schedules.map(schedule => (
              <div key={schedule.id} className={`card border-l-4 ${schedule.is_active ? 'border-l-green-500' : 'border-l-gray-300'}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-lg">{schedule.name}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${schedule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {schedule.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => editSchedule(schedule)} className="p-1.5 hover:bg-gray-100 rounded-lg"><Edit className="w-4 h-4 text-gray-600" /></button>
                    <button onClick={() => deleteSchedule(schedule.id)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <RefreshCw className="w-4 h-4" />
                    <span>{frequencyLabels[schedule.frequency]}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{schedule.employee_count || 0} employees assigned</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Started: {schedule.start_date}</span>
                  </div>
                </div>
                <button onClick={() => openRunModal(schedule)} className="mt-4 w-full btn-primary text-sm py-2">
                  <DollarSign className="w-4 h-4 inline mr-1" /> Run Payroll
                </button>
              </div>
            ))}
          </div>
          
          {schedules.length === 0 && (
            <div className="card text-center py-12">
              <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Pay Schedules Yet</h3>
              <p className="text-gray-500 mb-4">Create your first pay schedule to start processing payroll</p>
              <button onClick={() => setShowScheduleModal(true)} className="btn-primary">Create Pay Schedule</button>
            </div>
          )}
        </div>
      )}

      {/* Payroll Runs Tab */}
      {activeTab === 'runs' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Payroll Runs</h3>
            <button onClick={() => openRunModal()} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> New Payroll Run</button>
          </div>
          
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Schedule</th>
                  <th className="table-header">Pay Period</th>
                  <th className="table-header">Pay Date</th>
                  <th className="table-header">Employees</th>
                  <th className="table-header">Gross Pay</th>
                  <th className="table-header">Net Pay</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {payrollRuns.map(run => (
                  <tr key={run.id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{run.schedule_name || run.schedule?.name}</td>
                    <td className="table-cell text-sm">{run.pay_period_start} - {run.pay_period_end}</td>
                    <td className="table-cell">{run.pay_date}</td>
                    <td className="table-cell">{run.employee_count}</td>
                    <td className="table-cell">${run.total_gross?.toLocaleString()}</td>
                    <td className="table-cell font-semibold text-green-600">${run.total_net?.toLocaleString()}</td>
                    <td className="table-cell"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[run.status]}`}>{run.status}</span></td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        {run.status === 'draft' && <button onClick={() => approveRun(run.id)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Approve"><CheckCircle className="w-4 h-4" /></button>}
                        {run.status === 'approved' && <button onClick={() => payRun(run.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Pay"><CreditCard className="w-4 h-4" /></button>}
                        <button onClick={() => setSelectedRun(run)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="View Details"><Eye className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {payrollRuns.length === 0 && <div className="text-center py-8 text-gray-500">No payroll runs yet. Click "New Payroll Run" to create one.</div>}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Payroll History</h3>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50"><tr><th className="table-header">Employee</th><th className="table-header">Period</th><th className="table-header">Hours</th><th className="table-header">Gross</th><th className="table-header">Deductions</th><th className="table-header">Taxes</th><th className="table-header">Net</th><th className="table-header">Status</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {payrolls.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{p.employee?.member?.first_name || p.employee_name || 'N/A'} {p.employee?.member?.last_name || ''}</td>
                    <td className="table-cell text-sm text-gray-500">{p.pay_period_start} - {p.pay_period_end}</td>
                    <td className="table-cell">{p.hours_worked || 0}</td>
                    <td className="table-cell">${(p.gross_pay || 0).toLocaleString()}</td>
                    <td className="table-cell text-red-600">-${(p.deductions || 0).toLocaleString()}</td>
                    <td className="table-cell text-orange-600">-${(p.taxes || 0).toLocaleString()}</td>
                    <td className="table-cell font-semibold text-green-600">${(p.net_pay || 0).toLocaleString()}</td>
                    <td className="table-cell"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[p.status]}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {payrolls.length === 0 && <div className="text-center py-8 text-gray-500">No payroll history yet.</div>}
          </div>
        </div>
      )}

      {/* W2 Tab */}
      {activeTab === 'w2' && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium">Tax Year:</label>
              <select className="select-field w-32" value={w2Year} onChange={(e) => setW2Year(parseInt(e.target.value))}>{[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}</select>
            </div>
            <button onClick={generateW2s} className="btn-primary flex items-center gap-2"><FileCheck className="w-4 h-4" /> Generate W2s for {w2Year}</button>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50"><tr><th className="table-header">Employee</th><th className="table-header">Year</th><th className="table-header">Wages (Box 1)</th><th className="table-header">Federal Tax</th><th className="table-header">SS Tax</th><th className="table-header">Status</th><th className="table-header">View</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {w2Forms.map((w) => (
                  <tr key={w.id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{w.employee_name}</td>
                    <td className="table-cell">{w.tax_year}</td>
                    <td className="table-cell">${w.box1_wages?.toLocaleString()}</td>
                    <td className="table-cell">${w.box2_federal_tax?.toLocaleString()}</td>
                    <td className="table-cell">${w.box4_social_security_tax?.toLocaleString()}</td>
                    <td className="table-cell"><span className={`badge ${w.status === 'finalized' ? 'badge-success' : 'badge-warning'}`}>{w.status}</span></td>
                    <td className="table-cell"><button onClick={() => { setSelectedW2(w); setShowW2Modal(true); }} className="text-purple-600"><FileText className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {w2Forms.length === 0 && <div className="text-center py-8 text-gray-500">No W2 forms. Click "Generate W2s" to create them.</div>}
          </div>
        </>
      )}

      {/* Schedule Modal */}
      <Modal isOpen={showScheduleModal} onClose={() => { setShowScheduleModal(false); setSelectedSchedule(null); }} title={selectedSchedule ? 'Edit Pay Schedule' : 'Create Pay Schedule'}>
        <form onSubmit={handleScheduleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Name *</label>
            <input type="text" className="input-field" value={scheduleForm.name} onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })} required placeholder="e.g., Bi-Weekly Staff Payroll" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pay Frequency *</label>
            <select className="select-field" value={scheduleForm.frequency} onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-Weekly (Every 2 weeks)</option>
              <option value="semimonthly">Semi-Monthly (1st & 15th)</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          {(scheduleForm.frequency === 'weekly' || scheduleForm.frequency === 'biweekly') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Day</label>
              <select className="select-field" value={scheduleForm.pay_day} onChange={(e) => setScheduleForm({ ...scheduleForm, pay_day: parseInt(e.target.value) })}>
                <option value={0}>Monday</option><option value={1}>Tuesday</option><option value={2}>Wednesday</option><option value={3}>Thursday</option><option value={4}>Friday</option>
              </select>
            </div>
          )}
          {scheduleForm.frequency === 'semimonthly' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">First Pay Day</label><input type="number" className="input-field" value={scheduleForm.pay_day} onChange={(e) => setScheduleForm({ ...scheduleForm, pay_day: parseInt(e.target.value) })} min={1} max={28} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Second Pay Day</label><input type="number" className="input-field" value={scheduleForm.second_pay_day} onChange={(e) => setScheduleForm({ ...scheduleForm, second_pay_day: parseInt(e.target.value) })} min={1} max={31} /></div>
            </div>
          )}
          {scheduleForm.frequency === 'monthly' && (
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Pay Day (Day of Month)</label><input type="number" className="input-field" value={scheduleForm.pay_day} onChange={(e) => setScheduleForm({ ...scheduleForm, pay_day: parseInt(e.target.value) })} min={1} max={31} /></div>
          )}
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label><input type="date" className="input-field" value={scheduleForm.start_date} onChange={(e) => setScheduleForm({ ...scheduleForm, start_date: e.target.value })} required /></div>
          <label className="flex items-center gap-2">
            <input type="checkbox" className="w-4 h-4 text-purple-600 rounded" checked={scheduleForm.is_active} onChange={(e) => setScheduleForm({ ...scheduleForm, is_active: e.target.checked })} />
            <span className="text-sm font-medium text-gray-700">Active Schedule</span>
          </label>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowScheduleModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{selectedSchedule ? 'Update' : 'Create'} Schedule</button>
          </div>
        </form>
      </Modal>

      {/* Run Payroll Modal */}
      <Modal isOpen={showRunModal} onClose={() => setShowRunModal(false)} title="Run Payroll">
        <form onSubmit={handleRunPayroll} className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-700 flex items-center gap-2"><AlertCircle className="w-4 h-4" /> This will generate payroll for all staff members assigned to the selected schedule.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pay Schedule *</label>
            <select className="select-field" value={runForm.schedule_id} onChange={(e) => setRunForm({ ...runForm, schedule_id: e.target.value })} required>
              <option value="">Select a schedule...</option>
              {schedules.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name} ({frequencyLabels[s.frequency]})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Period Start *</label><input type="date" className="input-field" value={runForm.pay_period_start} onChange={(e) => setRunForm({ ...runForm, pay_period_start: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Period End *</label><input type="date" className="input-field" value={runForm.pay_period_end} onChange={(e) => setRunForm({ ...runForm, pay_period_end: e.target.value })} required /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Pay Date *</label><input type="date" className="input-field" value={runForm.pay_date} onChange={(e) => setRunForm({ ...runForm, pay_date: e.target.value })} required /></div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowRunModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary flex items-center gap-2"><DollarSign className="w-4 h-4" /> Run Payroll</button>
          </div>
        </form>
      </Modal>

      {/* W2 Detail Modal */}
      <Modal isOpen={showW2Modal} onClose={() => setShowW2Modal(false)} title={`W2 Form - ${selectedW2?.employee_name}`} size="lg">
        {selectedW2 && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Employer Information</h4>
              <p>{selectedW2.employer_name}</p>
              <p className="text-sm text-gray-500">EIN: {selectedW2.employer_ein}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Employee Information</h4>
              <p>{selectedW2.employee_name}</p>
              <p className="text-sm text-gray-500">SSN: {selectedW2.employee_ssn}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 border rounded"><p className="text-xs text-gray-500">1 Wages, tips, other</p><p className="font-semibold text-lg">${selectedW2.box1_wages?.toLocaleString()}</p></div>
              <div className="p-3 border rounded"><p className="text-xs text-gray-500">2 Federal income tax</p><p className="font-semibold text-lg">${selectedW2.box2_federal_tax?.toLocaleString()}</p></div>
              <div className="p-3 border rounded"><p className="text-xs text-gray-500">3 Social security wages</p><p className="font-semibold text-lg">${selectedW2.box3_social_security_wages?.toLocaleString()}</p></div>
              <div className="p-3 border rounded"><p className="text-xs text-gray-500">4 Social security tax</p><p className="font-semibold text-lg">${selectedW2.box4_social_security_tax?.toLocaleString()}</p></div>
              <div className="p-3 border rounded"><p className="text-xs text-gray-500">5 Medicare wages</p><p className="font-semibold text-lg">${selectedW2.box5_medicare_wages?.toLocaleString()}</p></div>
              <div className="p-3 border rounded"><p className="text-xs text-gray-500">6 Medicare tax</p><p className="font-semibold text-lg">${selectedW2.box6_medicare_tax?.toLocaleString()}</p></div>
            </div>
            <div className="flex justify-end gap-3">
              {selectedW2.status !== 'finalized' && <button onClick={async () => { await api.put(`/api/hr/w2-forms/${selectedW2.id}/finalize`); fetchAll(); setShowW2Modal(false); }} className="btn-success">Finalize W2</button>}
              <button onClick={() => setShowW2Modal(false)} className="btn-secondary">Close</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// Contractors & 1099 Component
const Contractors = () => {
  const [activeTab, setActiveTab] = useState('contractors');
  const [contractors, setContractors] = useState([]);
  const [forms1099, setForms1099] = useState([]);
  const [summary, setSummary] = useState({});
  const [showContractorModal, setShowContractorModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [show1099Modal, setShow1099Modal] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [selected1099, setSelected1099] = useState(null);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [contractorForm, setContractorForm] = useState({
    first_name: '', last_name: '', business_name: '', email: '', phone: '',
    address: '', city: '', state: '', zip_code: '', tax_id: '', tax_id_type: 'ssn',
    service_type: '', hourly_rate: '', status: 'active', notes: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_date: '', description: '', invoice_number: '', payment_method: 'check', check_number: ''
  });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [contRes, formRes, sumRes] = await Promise.all([
        api.get('/api/contractors'),
        api.get(`/api/hr/1099-forms?year=${taxYear}`),
        api.get(`/api/hr/1099-summary?year=${taxYear}`)
      ]);
      setContractors(contRes.data.contractors || []);
      setForms1099(formRes.data.forms || []);
      setSummary(sumRes.data || {});
    } catch (e) { console.error(e); }
  };

  const handleContractorSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...contractorForm };
      if (data.hourly_rate) data.hourly_rate = parseFloat(data.hourly_rate);
      else data.hourly_rate = null;
      
      if (selectedContractor) {
        await api.put(`/api/contractors/${selectedContractor.id}`, data);
      } else {
        await api.post('/api/contractors', data);
      }
      fetchAll();
      setShowContractorModal(false);
      resetContractorForm();
    } catch (e) { alert(e.response?.data?.detail || 'Error saving contractor'); }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/api/contractors/${selectedContractor.id}/payments`, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount),
        contractor_id: selectedContractor.id
      });
      fetchAll();
      setShowPaymentModal(false);
      setPaymentForm({ amount: '', payment_date: '', description: '', invoice_number: '', payment_method: 'check', check_number: '' });
      alert('Payment recorded successfully!');
    } catch (e) { alert(e.response?.data?.detail || 'Error recording payment'); }
  };

  const generate1099s = async () => {
    try {
      const res = await api.post(`/api/hr/1099-forms/generate?tax_year=${taxYear}`);
      fetchAll();
      alert(res.data.message);
    } catch (e) { alert(e.response?.data?.detail || 'Error generating 1099 forms'); }
  };

  const finalize1099 = async (formId) => {
    try {
      await api.put(`/api/hr/1099-forms/${formId}/finalize`);
      fetchAll();
      alert('1099 form finalized!');
    } catch (e) { alert('Error finalizing form'); }
  };

  const deleteContractor = async (id) => {
    if (!window.confirm('Delete this contractor?')) return;
    try { await api.delete(`/api/contractors/${id}`); fetchAll(); } catch (e) { alert('Error deleting'); }
  };

  const editContractor = (contractor) => {
    setSelectedContractor(contractor);
    setContractorForm({ ...contractor, hourly_rate: contractor.hourly_rate || '' });
    setShowContractorModal(true);
  };

  const openPaymentModal = (contractor) => {
    setSelectedContractor(contractor);
    setPaymentForm({ amount: '', payment_date: new Date().toISOString().split('T')[0], description: '', invoice_number: '', payment_method: 'check', check_number: '' });
    setShowPaymentModal(true);
  };

  const resetContractorForm = () => {
    setSelectedContractor(null);
    setContractorForm({
      first_name: '', last_name: '', business_name: '', email: '', phone: '',
      address: '', city: '', state: '', zip_code: '', tax_id: '', tax_id_type: 'ssn',
      service_type: '', hourly_rate: '', status: 'active', notes: ''
    });
  };

  const statusColors = { draft: 'bg-yellow-100 text-yellow-700', finalized: 'bg-green-100 text-green-700' };

  return (
    <div className="space-y-6" data-testid="contractors-page">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b pb-2">
        <button onClick={() => setActiveTab('contractors')} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === 'contractors' ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
          <Users className="w-4 h-4 inline mr-2" />Contractors
        </button>
        <button onClick={() => setActiveTab('1099')} className={`px-4 py-2 rounded-t-lg font-medium ${activeTab === '1099' ? 'bg-purple-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
          <FileText className="w-4 h-4 inline mr-2" />1099-NEC Forms
        </button>
      </div>

      {/* Contractors Tab */}
      {activeTab === 'contractors' && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl"><Users className="w-6 h-6" /></div>
                <div><p className="text-blue-100 text-sm">Active Contractors</p><p className="text-2xl font-bold">{summary.total_contractors || 0}</p></div>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl"><DollarSign className="w-6 h-6" /></div>
                <div><p className="text-green-100 text-sm">Total 1099 Payments ({taxYear})</p><p className="text-2xl font-bold">${(summary.total_1099_amount || 0).toLocaleString()}</p></div>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl"><FileText className="w-6 h-6" /></div>
                <div><p className="text-purple-100 text-sm">1099s Generated</p><p className="text-2xl font-bold">{summary.contractors_with_1099 || 0}</p></div>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-orange-500 to-orange-600 text-white">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl"><AlertCircle className="w-6 h-6" /></div>
                <div><p className="text-orange-100 text-sm">Pending Finalization</p><p className="text-2xl font-bold">{summary.draft_count || 0}</p></div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Contract Workers</h3>
            <button onClick={() => { resetContractorForm(); setShowContractorModal(true); }} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Contractor
            </button>
          </div>

          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Name</th>
                  <th className="table-header">Business</th>
                  <th className="table-header">Service Type</th>
                  <th className="table-header">Contact</th>
                  <th className="table-header">YTD Payments</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {contractors.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{c.first_name} {c.last_name}</td>
                    <td className="table-cell text-gray-500">{c.business_name || '-'}</td>
                    <td className="table-cell">{c.service_type || '-'}</td>
                    <td className="table-cell text-sm">
                      {c.email && <div className="flex items-center gap-1 text-gray-500"><Mail className="w-3 h-3" />{c.email}</div>}
                      {c.phone && <div className="flex items-center gap-1 text-gray-500"><Phone className="w-3 h-3" />{c.phone}</div>}
                    </td>
                    <td className="table-cell">
                      <span className={`font-semibold ${c.ytd_payments >= 600 ? 'text-green-600' : 'text-gray-600'}`}>
                        ${(c.ytd_payments || 0).toLocaleString()}
                      </span>
                      {c.ytd_payments >= 600 && <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">1099 Required</span>}
                    </td>
                    <td className="table-cell">
                      <span className={`px-2 py-1 rounded-full text-xs ${c.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openPaymentModal(c)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Record Payment">
                          <DollarSign className="w-4 h-4" />
                        </button>
                        <button onClick={() => editContractor(c)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteContractor(c.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {contractors.length === 0 && (
              <div className="text-center py-12">
                <Briefcase className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No contractors yet. Click "Add Contractor" to add your first one.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 1099 Forms Tab */}
      {activeTab === '1099' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold">1099-NEC Forms</h3>
              <select className="select-field w-32" value={taxYear} onChange={(e) => { setTaxYear(parseInt(e.target.value)); fetchAll(); }}>
                {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button onClick={generate1099s} className="btn-primary flex items-center gap-2">
              <FileText className="w-4 h-4" /> Generate 1099s for {taxYear}
            </button>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              IRS requires 1099-NEC forms for contractors who received $600 or more during the tax year.
            </p>
          </div>

          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Contractor</th>
                  <th className="table-header">Business Name</th>
                  <th className="table-header">Tax Year</th>
                  <th className="table-header">Box 1 (Compensation)</th>
                  <th className="table-header">Payments</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {forms1099.map(form => (
                  <tr key={form.id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{form.contractor_name}</td>
                    <td className="table-cell text-gray-500">{form.contractor_business_name || '-'}</td>
                    <td className="table-cell">{form.tax_year}</td>
                    <td className="table-cell font-semibold text-green-600">${form.box1_nonemployee_compensation?.toLocaleString()}</td>
                    <td className="table-cell">{form.payment_count}</td>
                    <td className="table-cell">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[form.status]}`}>
                        {form.status}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setSelected1099(form); setShow1099Modal(true); }} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded" title="View Details">
                          <Eye className="w-4 h-4" />
                        </button>
                        {form.status !== 'finalized' && (
                          <button onClick={() => finalize1099(form.id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Finalize">
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {forms1099.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">No 1099 forms generated for {taxYear}.</p>
                <button onClick={generate1099s} className="btn-primary">Generate 1099s</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Contractor Modal */}
      <Modal isOpen={showContractorModal} onClose={() => { setShowContractorModal(false); resetContractorForm(); }} title={selectedContractor ? 'Edit Contractor' : 'Add Contractor'}>
        <form onSubmit={handleContractorSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label><input type="text" className="input-field" value={contractorForm.first_name} onChange={(e) => setContractorForm({ ...contractorForm, first_name: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label><input type="text" className="input-field" value={contractorForm.last_name} onChange={(e) => setContractorForm({ ...contractorForm, last_name: e.target.value })} required /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Business Name (if applicable)</label><input type="text" className="input-field" value={contractorForm.business_name} onChange={(e) => setContractorForm({ ...contractorForm, business_name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" className="input-field" value={contractorForm.email} onChange={(e) => setContractorForm({ ...contractorForm, email: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="tel" className="input-field" value={contractorForm.phone} onChange={(e) => setContractorForm({ ...contractorForm, phone: e.target.value })} /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Address</label><input type="text" className="input-field" value={contractorForm.address} onChange={(e) => setContractorForm({ ...contractorForm, address: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">City</label><input type="text" className="input-field" value={contractorForm.city} onChange={(e) => setContractorForm({ ...contractorForm, city: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">State</label><input type="text" className="input-field" value={contractorForm.state} onChange={(e) => setContractorForm({ ...contractorForm, state: e.target.value })} maxLength={2} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label><input type="text" className="input-field" value={contractorForm.zip_code} onChange={(e) => setContractorForm({ ...contractorForm, zip_code: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Tax ID Type</label>
              <select className="select-field" value={contractorForm.tax_id_type} onChange={(e) => setContractorForm({ ...contractorForm, tax_id_type: e.target.value })}>
                <option value="ssn">SSN</option>
                <option value="ein">EIN</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Tax ID (SSN/EIN)</label><input type="text" className="input-field" value={contractorForm.tax_id} onChange={(e) => setContractorForm({ ...contractorForm, tax_id: e.target.value })} placeholder="XXX-XX-XXXX" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Service Type</label><input type="text" className="input-field" value={contractorForm.service_type} onChange={(e) => setContractorForm({ ...contractorForm, service_type: e.target.value })} placeholder="e.g., Music, Cleaning" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate</label><input type="number" className="input-field" value={contractorForm.hourly_rate} onChange={(e) => setContractorForm({ ...contractorForm, hourly_rate: e.target.value })} step="0.01" placeholder="0.00" /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label><textarea className="input-field" rows={2} value={contractorForm.notes} onChange={(e) => setContractorForm({ ...contractorForm, notes: e.target.value })} /></div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => { setShowContractorModal(false); resetContractorForm(); }} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{selectedContractor ? 'Update' : 'Add'} Contractor</button>
          </div>
        </form>
      </Modal>

      {/* Record Payment Modal */}
      <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title={`Record Payment - ${selectedContractor?.first_name} ${selectedContractor?.last_name}`}>
        <form onSubmit={handlePaymentSubmit} className="space-y-4">
          <div className="bg-green-50 p-3 rounded-lg mb-2">
            <p className="text-sm text-green-700">Recording payment for contractor. This will be included in their 1099-NEC form.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label><input type="number" className="input-field" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} required step="0.01" placeholder="0.00" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label><input type="date" className="input-field" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} required /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><input type="text" className="input-field" value={paymentForm.description} onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })} placeholder="Services rendered" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Invoice #</label><input type="text" className="input-field" value={paymentForm.invoice_number} onChange={(e) => setPaymentForm({ ...paymentForm, invoice_number: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select className="select-field" value={paymentForm.payment_method} onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}>
                <option value="check">Check</option>
                <option value="cash">Cash</option>
                <option value="direct_deposit">Direct Deposit</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          {paymentForm.payment_method === 'check' && (
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Check Number</label><input type="text" className="input-field" value={paymentForm.check_number} onChange={(e) => setPaymentForm({ ...paymentForm, check_number: e.target.value })} /></div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowPaymentModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary flex items-center gap-2"><DollarSign className="w-4 h-4" /> Record Payment</button>
          </div>
        </form>
      </Modal>

      {/* 1099 Detail Modal */}
      <Modal isOpen={show1099Modal} onClose={() => setShow1099Modal(false)} title={`1099-NEC - ${selected1099?.contractor_name}`} size="lg">
        {selected1099 && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg border-2 border-dashed">
              <div className="text-center mb-4">
                <h3 className="text-lg font-bold">Form 1099-NEC</h3>
                <p className="text-sm text-gray-500">Nonemployee Compensation</p>
                <p className="text-sm">Tax Year: {selected1099.tax_year}</p>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Payer's Information</p>
                  <p className="font-semibold">Holy Light Ministry Soul Center Headquarters</p>
                  <p className="text-sm text-gray-600">holylightministrysoul.center</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Recipient's Information</p>
                  <p className="font-semibold">{selected1099.contractor_name}</p>
                  {selected1099.contractor_business_name && <p className="text-sm">{selected1099.contractor_business_name}</p>}
                  <p className="text-sm text-gray-600">{selected1099.contractor_address}</p>
                  <p className="text-sm text-gray-600">{selected1099.contractor_city}, {selected1099.contractor_state} {selected1099.contractor_zip}</p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-lg border">
                  <p className="text-xs text-gray-500">1. Nonemployee compensation</p>
                  <p className="text-2xl font-bold text-green-600">${selected1099.box1_nonemployee_compensation?.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-white rounded-lg border">
                  <p className="text-xs text-gray-500">4. Federal income tax withheld</p>
                  <p className="text-2xl font-bold">${selected1099.box4_federal_tax_withheld?.toLocaleString() || '0.00'}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[selected1099.status]}`}>
                Status: {selected1099.status}
              </span>
              <div className="flex gap-3">
                {selected1099.status !== 'finalized' && (
                  <button onClick={() => { finalize1099(selected1099.id); setShow1099Modal(false); }} className="btn-success">Finalize 1099</button>
                )}
                <button onClick={() => setShow1099Modal(false)} className="btn-secondary">Close</button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// Attendance Component
const Attendance = () => {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ employee_id: '', date: '', check_in: '', check_out: '', status: 'present' });

  useEffect(() => { fetchAttendance(); fetchEmployees(); }, []);
  const fetchAttendance = async () => { try { const r = await api.get('/api/attendance'); setAttendance(r.data.attendance); } catch (e) { console.error(e); } };
  const fetchEmployees = async () => { try { const r = await api.get('/api/employees'); setEmployees(r.data.employees); } catch (e) { console.error(e); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hoursWorked = 0;
    if (formData.check_in && formData.check_out) { const ci = new Date(`2000-01-01T${formData.check_in}`); const co = new Date(`2000-01-01T${formData.check_out}`); hoursWorked = ((co - ci) / (1000 * 60 * 60)).toFixed(2); }
    try { await api.post('/api/attendance', { ...formData, hours_worked: parseFloat(hoursWorked) }); fetchAttendance(); setShowModal(false); } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex justify-end"><button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Record Attendance</button></div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50"><tr><th className="table-header">Employee</th><th className="table-header">Date</th><th className="table-header">In</th><th className="table-header">Out</th><th className="table-header">Hours</th><th className="table-header">Status</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {attendance.map((r) => { const emp = employees.find(e => e.id === r.employee_id); return (
              <tr key={r.id} className="hover:bg-gray-50"><td className="table-cell">{emp?.member?.first_name} {emp?.member?.last_name}</td><td className="table-cell">{r.date}</td><td className="table-cell">{r.check_in || '-'}</td><td className="table-cell">{r.check_out || '-'}</td><td className="table-cell">{r.hours_worked}h</td><td className="table-cell"><span className={`badge ${r.status === 'present' ? 'badge-success' : 'badge-danger'}`}>{r.status}</span></td></tr>
            ); })}
          </tbody>
        </table>
      </div>
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Record Attendance">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label><select className="select-field" value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} required><option value="">Select...</option>{employees.map(e => <option key={e.id} value={e.id}>{e.member?.first_name} {e.member?.last_name}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label><input type="date" className="input-field" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700 mb-1">Check In</label><input type="time" className="input-field" value={formData.check_in} onChange={(e) => setFormData({ ...formData, check_in: e.target.value })} /></div><div><label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label><input type="time" className="input-field" value={formData.check_out} onChange={(e) => setFormData({ ...formData, check_out: e.target.value })} /></div></div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Save</button></div>
        </form>
      </Modal>
    </div>
  );
};

// Donations Component
const Donations = ({ autoOpenModal = false }) => {
  const [donations, setDonations] = useState([]);
  const [showModal, setShowModal] = useState(autoOpenModal);
  const [formData, setFormData] = useState({ donor_name: '', amount: '', donation_date: '', donation_type: 'tithe', payment_method: 'cash' });

  useEffect(() => { fetchDonations(); }, []);
  useEffect(() => { if (autoOpenModal) setShowModal(true); }, [autoOpenModal]);

  const fetchDonations = async () => { try { const r = await api.get('/api/donations'); setDonations(r.data.donations); } catch (e) { console.error(e); } };

  const handleSubmit = async (e) => { e.preventDefault(); try { await api.post('/api/donations', { ...formData, amount: parseFloat(formData.amount) }); fetchDonations(); setShowModal(false); setFormData({ donor_name: '', amount: '', donation_date: '', donation_type: 'tithe', payment_method: 'cash' }); } catch (e) { console.error(e); } };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this donation record?')) {
      try { await api.delete(`/api/donations/${id}`); fetchDonations(); alert('Donation deleted'); } catch (e) { console.error(e); alert('Failed to delete donation'); }
    }
  };

  return (
    <div className="space-y-6" data-testid="donations-page">
      <div className="flex justify-between items-center">
        <div className="card flex items-center gap-4 py-4"><div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"><Heart className="w-6 h-6 text-green-600" /></div><div><p className="text-sm text-gray-500">Total</p><p className="text-2xl font-bold text-green-600">${donations.reduce((s, d) => s + (d.amount || 0), 0).toLocaleString()}</p></div></div>
        <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Record Donation</button>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full"><thead className="bg-gray-50"><tr><th className="table-header">Donor</th><th className="table-header">Amount</th><th className="table-header">Type</th><th className="table-header">Date</th><th className="table-header">Actions</th></tr></thead>
          <tbody className="divide-y divide-gray-100">{donations.map(d => (
            <tr key={d.id} className="hover:bg-gray-50">
              <td className="table-cell font-medium">{d.donor_name}</td>
              <td className="table-cell text-green-600 font-semibold">${d.amount?.toLocaleString()}</td>
              <td className="table-cell"><span className="badge bg-purple-100 text-purple-800">{d.donation_type}</span></td>
              <td className="table-cell text-gray-500">{d.donation_date}</td>
              <td className="table-cell">
                <button onClick={() => handleDelete(d.id)} className="text-red-600 hover:text-red-800" data-testid={`delete-donation-${d.id}`}><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}</tbody>
        </table>
        {donations.length === 0 && <div className="text-center py-8 text-gray-500">No donations recorded yet</div>}
      </div>
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Record Donation">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Donor Name *</label><input type="text" className="input-field" value={formData.donor_name} onChange={(e) => setFormData({ ...formData, donor_name: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label><input type="number" step="0.01" className="input-field" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required /></div><div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label><input type="date" className="input-field" value={formData.donation_date} onChange={(e) => setFormData({ ...formData, donation_date: e.target.value })} required /></div></div>
          <div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label><select className="select-field" value={formData.donation_type} onChange={(e) => setFormData({ ...formData, donation_type: e.target.value })}><option value="tithe">Tithe</option><option value="offering">Offering</option><option value="special">Special</option></select></div><div><label className="block text-sm font-medium text-gray-700 mb-1">Payment</label><select className="select-field" value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}><option value="cash">Cash</option><option value="check">Check</option><option value="card">Card</option></select></div></div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Save</button></div>
        </form>
      </Modal>
    </div>
  );
};

// Accounting Component with Full Expense Management
const Accounting = () => {
  const { canEdit } = useAuth();
  const [activeTab, setActiveTab] = useState('expenses');
  const [accounts, setAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showTxnModal, setShowTxnModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [incomeForm, setIncomeForm] = useState({
    date: new Date().toISOString().split('T')[0],
    source: 'tithes',
    description: '',
    amount: '',
    donor_name: '',
    payment_method: 'cash',
    reference_number: '',
    bank_account_id: '',
    notes: '',
    is_restricted: false,
    restriction_purpose: ''
  });
  const [selectedBank, setSelectedBank] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [bankForm, setBankForm] = useState({ name: '', account_number: '', bank_name: '', account_type: 'checking', routing_number: '', opening_balance: 0 });
  const [txnForm, setTxnForm] = useState({ transaction_type: 'deposit', amount: '', date: '', description: '', category_account_id: '' });
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0],
    vendor_id: '',
    vendor_name: '',
    description: '',
    amount: '',
    category_account_id: '',
    payment_method: 'check',
    reference_number: '',
    notes: '',
    requires_approval: false,
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_end_date: ''
  });
  const [vendorForm, setVendorForm] = useState({ name: '', contact_name: '', email: '', phone: '', address: '' });
  const [budgetForm, setBudgetForm] = useState({ account_id: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1, amount: '' });
  const [filterDate, setFilterDate] = useState({ start: '', end: '' });
  const [expenseSummary, setExpenseSummary] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [acc, bank, txn, exp, vend, bud, pending, inc, audit] = await Promise.all([
        api.get('/api/accounting/accounts'),
        api.get('/api/accounting/bank-accounts'),
        api.get('/api/accounting/transactions'),
        api.get('/api/expenses'),
        api.get('/api/accounting/vendors'),
        api.get('/api/accounting/budgets'),
        api.get('/api/expenses/pending-approvals'),
        api.get('/api/income'),
        api.get('/api/audit-trail?limit=50').catch(() => ({ data: { entries: [] } }))
      ]);
      setAccounts(acc.data.accounts || []);
      setBankAccounts(bank.data.bank_accounts || []);
      setTransactions(txn.data.transactions || []);
      setExpenses(exp.data.expenses || []);
      setVendors(vend.data.vendors || []);
      setBudgets(bud.data.budgets || []);
      setPendingApprovals(pending.data.expenses || []);
      setIncomeRecords(inc.data.income || []);
      setAuditTrail(audit.data.entries || []);
    } catch (e) { console.error(e); }
  };

  const fetchExpenseSummary = async () => {
    if (!filterDate.start || !filterDate.end) return;
    try {
      const r = await api.get(`/api/expenses/summary?start_date=${filterDate.start}&end_date=${filterDate.end}`);
      setExpenseSummary(r.data);
    } catch (e) { console.error(e); }
  };

  const handleCreateBank = async (e) => {
    e.preventDefault();
    try { await api.post(`/api/accounting/bank-accounts?name=${bankForm.name}&account_number=${bankForm.account_number}&bank_name=${bankForm.bank_name}&account_type=${bankForm.account_type}&routing_number=${bankForm.routing_number}&opening_balance=${bankForm.opening_balance}&link_to_chart_of_accounts=true`); fetchAll(); setShowBankModal(false); } catch (e) { console.error(e); }
  };

  const handleRecordTxn = async (e) => {
    e.preventDefault();
    try { await api.post(`/api/accounting/bank-accounts/${selectedBank.id}/transaction?transaction_type=${txnForm.transaction_type}&amount=${txnForm.amount}&date=${txnForm.date}&description=${txnForm.description}${txnForm.category_account_id ? `&category_account_id=${txnForm.category_account_id}` : ''}`); fetchAll(); setShowTxnModal(false); } catch (e) { console.error(e); }
  };

  const handleSaveExpense = async (e) => {
    e.preventDefault();
    try {
      const data = { ...expenseForm, amount: parseFloat(expenseForm.amount) };
      if (editingExpense) {
        await api.put(`/api/expenses/${editingExpense.id}`, data);
      } else {
        await api.post('/api/expenses', data);
      }
      fetchAll();
      setShowExpenseModal(false);
      setEditingExpense(null);
      resetExpenseForm();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Error saving expense');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/api/expenses/${id}`);
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Cannot delete');
    }
  };

  const handleApproveExpense = async (id) => {
    try {
      await api.post(`/api/expenses/${id}/approve`);
      fetchAll();
      alert('Expense approved!');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error approving');
    }
  };

  const handleRejectExpense = async (id) => {
    const reason = prompt('Rejection reason (optional):');
    try {
      await api.post(`/api/expenses/${id}/reject?reason=${encodeURIComponent(reason || '')}`);
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error rejecting');
    }
  };

  const handleVoidExpense = async (id) => {
    if (!window.confirm('Void this expense? This will reverse the journal entry.')) return;
    try {
      await api.post(`/api/expenses/${id}/void`);
      fetchAll();
      alert('Expense voided');
    } catch (e) {
      alert(e.response?.data?.detail || 'Error voiding');
    }
  };

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    try {
      if (editingVendor) {
        await api.put(`/api/accounting/vendors/${editingVendor.id}`, vendorForm);
      } else {
        await api.post('/api/accounting/vendors', vendorForm);
      }
      fetchAll();
      setShowVendorModal(false);
      setEditingVendor(null);
      setVendorForm({ name: '', contact_name: '', email: '', phone: '', address: '' });
    } catch (e) { console.error(e); alert('Error saving vendor'); }
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name || '',
      contact_name: vendor.contact_name || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || ''
    });
    setShowVendorModal(true);
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await api.delete(`/api/accounting/vendors/${vendorId}`);
      fetchAll();
    } catch (e) {
      console.error(e);
      alert('Error deleting vendor');
    }
  };

  const handleSaveBudget = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/accounting/budgets', { ...budgetForm, amount: parseFloat(budgetForm.amount) });
      fetchAll();
      setShowBudgetModal(false);
    } catch (e) { console.error(e); }
  };

  const handleSaveIncome = async (e) => {
    e.preventDefault();
    try {
      const data = { ...incomeForm, amount: parseFloat(incomeForm.amount) };
      await api.post('/api/income', data);
      fetchAll();
      setShowIncomeModal(false);
      setIncomeForm({
        date: new Date().toISOString().split('T')[0],
        source: 'tithes',
        description: '',
        amount: '',
        donor_name: '',
        payment_method: 'cash',
        reference_number: '',
        bank_account_id: '',
        notes: '',
        is_restricted: false,
        restriction_purpose: ''
      });
      alert('Income recorded successfully!');
    } catch (e) {
      console.error(e);
      alert('Error recording income');
    }
  };

  const openTxnModal = (bank) => { setSelectedBank(bank); setTxnForm({ transaction_type: 'deposit', amount: '', date: '', description: '', category_account_id: '' }); setShowTxnModal(true); };

  const openEditExpense = (exp) => {
    setEditingExpense(exp);
    setExpenseForm({
      date: exp.date,
      vendor_id: exp.vendor_id || '',
      vendor_name: exp.vendor_name || '',
      description: exp.description,
      amount: exp.amount?.toString(),
      category_account_id: exp.category_account_id,
      payment_method: exp.payment_method || 'check',
      reference_number: exp.reference_number || '',
      notes: exp.notes || '',
      requires_approval: exp.requires_approval || false,
      is_recurring: exp.is_recurring || false,
      recurring_frequency: exp.recurring_frequency || 'monthly',
      recurring_end_date: exp.recurring_end_date || ''
    });
    setShowExpenseModal(true);
  };

  const resetExpenseForm = () => {
    setExpenseForm({
      date: new Date().toISOString().split('T')[0],
      vendor_id: '',
      vendor_name: '',
      description: '',
      amount: '',
      category_account_id: '',
      payment_method: 'check',
      reference_number: '',
      notes: '',
      requires_approval: false,
      is_recurring: false,
      recurring_frequency: 'monthly',
      recurring_end_date: ''
    });
  };

  const groupedAccounts = { asset: accounts.filter(a => a.type === 'asset'), liability: accounts.filter(a => a.type === 'liability'), income: accounts.filter(a => a.type === 'income'), expense: accounts.filter(a => a.type === 'expense'), equity: accounts.filter(a => a.type === 'equity') };
  const expenseAccounts = accounts.filter(a => a.type === 'expense');
  const incomeAccounts = accounts.filter(a => a.type === 'income');

  const categoryLabels = {
    personnel: 'Personnel',
    facilities: 'Facilities',
    administrative: 'Administrative',
    ministry: 'Ministry',
    operational: 'Operational',
    capital: 'Capital'
  };

  const statusColors = {
    approved: 'bg-green-100 text-green-700',
    pending_approval: 'bg-yellow-100 text-yellow-700',
    rejected: 'bg-red-100 text-red-700',
    voided: 'bg-gray-100 text-gray-500'
  };

  return (
    <div className="space-y-6" data-testid="accounting-page">
      {/* Pending Approvals Alert */}
      {pendingApprovals.length > 0 && canEdit('accounting') && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800 font-medium">{pendingApprovals.length} expense(s) awaiting approval</span>
          </div>
          <button onClick={() => setActiveTab('approvals')} className="text-yellow-700 hover:text-yellow-900 font-semibold text-sm">Review Now →</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'expenses', label: 'Expenses', icon: Receipt },
          { id: 'income', label: 'Income', icon: DollarSign },
          { id: 'approvals', label: `Approvals${pendingApprovals.length > 0 ? ` (${pendingApprovals.length})` : ''}`, icon: CheckCircle },
          { id: 'vendors', label: 'Vendors', icon: Building2 },
          { id: 'budgets', label: 'Budgets', icon: Target },
          { id: 'bank', label: 'Bank Accounts', icon: Landmark },
          { id: 'accounts', label: 'Chart of Accounts', icon: BookOpen },
          { id: 'audit', label: 'Audit Trail', icon: FileCheck },
          { id: 'reports', label: 'Reports', icon: FileSpreadsheet }
        ].map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${activeTab === t.id ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2">
              <input type="date" className="input-field" value={filterDate.start} onChange={(e) => setFilterDate({ ...filterDate, start: e.target.value })} />
              <span className="self-center text-gray-400">to</span>
              <input type="date" className="input-field" value={filterDate.end} onChange={(e) => setFilterDate({ ...filterDate, end: e.target.value })} />
              <button onClick={fetchExpenseSummary} className="btn-secondary text-sm">View Summary</button>
            </div>
            <button onClick={() => { setEditingExpense(null); resetExpenseForm(); setShowExpenseModal(true); }} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Expense
            </button>
          </div>

          {expenseSummary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="card bg-red-50 border-red-200">
                <p className="text-sm text-red-600 font-medium">Total Expenses</p>
                <p className="text-2xl font-bold text-red-700">${expenseSummary.total?.toLocaleString()}</p>
                <p className="text-xs text-red-500">{expenseSummary.expense_count} transactions</p>
              </div>
              {Object.entries(expenseSummary.by_category || {}).slice(0, 3).map(([cat, data]) => (
                <div key={cat} className="card">
                  <p className="text-sm text-gray-600 font-medium capitalize">{categoryLabels[cat] || cat}</p>
                  <p className="text-xl font-bold text-gray-900">${data.total?.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{data.count} items</p>
                </div>
              ))}
            </div>
          )}

          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Date</th>
                  <th className="table-header">Description</th>
                  <th className="table-header">Category</th>
                  <th className="table-header">Vendor</th>
                  <th className="table-header text-right">Amount</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {expenses.slice(0, 50).map(exp => (
                  <tr key={exp.id} className="hover:bg-gray-50">
                    <td className="table-cell">{exp.date}</td>
                    <td className="table-cell font-medium">
                      <div className="flex items-center gap-2">
                        {exp.description}
                        {exp.is_recurring && <Repeat className="w-4 h-4 text-purple-500" title="Recurring expense" />}
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                        {exp.account_name || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="table-cell text-gray-500">{exp.vendor_name || '—'}</td>
                    <td className="table-cell text-right font-semibold text-red-600">${exp.amount?.toLocaleString()}</td>
                    <td className="table-cell">
                      <span className={`text-xs px-2 py-1 rounded-full ${statusColors[exp.status] || 'bg-gray-100'}`}>
                        {exp.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex gap-1">
                        {exp.status === 'pending_approval' && (
                          <button onClick={() => openEditExpense(exp)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
                        )}
                        {exp.status === 'approved' && canEdit('accounting') && (
                          <button onClick={() => handleVoidExpense(exp.id)} className="p-1 text-red-600 hover:bg-red-50 rounded" title="Void"><X className="w-4 h-4" /></button>
                        )}
                        {exp.status !== 'approved' && exp.status !== 'voided' && (
                          <button onClick={() => handleDeleteExpense(exp.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {expenses.length === 0 && <p className="text-center py-8 text-gray-500">No expenses recorded yet</p>}
          </div>
        </div>
      )}

      {/* Income Tab */}
      {activeTab === 'income' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Income Records</h3>
            <button onClick={() => setShowIncomeModal(true)} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> Record Income
            </button>
          </div>
          
          {/* Income Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {['tithes', 'offerings', 'donations', 'other'].map(source => {
              const total = incomeRecords.filter(i => i.source === source).reduce((s, i) => s + (i.amount || 0), 0);
              return (
                <div key={source} className="card bg-green-50 border-green-200">
                  <p className="text-sm text-green-600 font-medium capitalize">{source}</p>
                  <p className="text-2xl font-bold text-green-700">${total.toLocaleString()}</p>
                </div>
              );
            })}
          </div>

          {/* Income Records Table */}
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Date</th>
                  <th className="table-header">Source</th>
                  <th className="table-header">Description</th>
                  <th className="table-header">Donor</th>
                  <th className="table-header">Method</th>
                  <th className="table-header text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {incomeRecords.slice(0, 50).map(inc => (
                  <tr key={inc.id} className="hover:bg-gray-50">
                    <td className="table-cell">{inc.date}</td>
                    <td className="table-cell">
                      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 capitalize">
                        {inc.source}
                      </span>
                    </td>
                    <td className="table-cell font-medium">{inc.description}</td>
                    <td className="table-cell text-gray-500">{inc.donor_name || '—'}</td>
                    <td className="table-cell text-gray-500 capitalize">{inc.payment_method}</td>
                    <td className="table-cell text-right font-semibold text-green-600">${inc.amount?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {incomeRecords.length === 0 && <p className="text-center py-8 text-gray-500">No income recorded yet. Click "Record Income" to add your first entry.</p>}
          </div>
        </div>
      )}

      {/* Approvals Tab */}
      {activeTab === 'approvals' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Pending Expense Approvals</h3>
          {pendingApprovals.length > 0 ? (
            <div className="space-y-3">
              {pendingApprovals.map(exp => (
                <div key={exp.id} className="card border-l-4 border-yellow-400">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{exp.description}</p>
                      <p className="text-sm text-gray-500">{exp.date} • {exp.account_name} • {exp.vendor_name || 'No vendor'}</p>
                      <p className="text-sm text-gray-500">Payment: {exp.payment_method} {exp.reference_number && `• Ref: ${exp.reference_number}`}</p>
                      {exp.notes && <p className="text-sm text-gray-400 mt-1 italic">"{exp.notes}"</p>}
                      <p className="text-xs text-gray-400 mt-2">Submitted by {exp.created_by} on {new Date(exp.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-2xl font-bold text-red-600">${exp.amount?.toLocaleString()}</p>
                      <div className="flex flex-col gap-2 mt-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleApproveExpense(exp.id)} className="btn-primary text-sm flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Approve
                          </button>
                          <button onClick={() => handleRejectExpense(exp.id)} className="btn-secondary text-sm flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50">
                            <X className="w-4 h-4" /> Reject
                          </button>
                        </div>
                        <button onClick={() => openEditExpense(exp)} className="btn-secondary text-sm flex items-center justify-center gap-1">
                          <Edit className="w-4 h-4" /> Edit Before Approving
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card text-center py-12">
              <CheckCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
              <p className="text-gray-500">No expenses pending approval</p>
            </div>
          )}
        </div>
      )}

      {/* Vendors Tab */}
      {activeTab === 'vendors' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setEditingVendor(null); setVendorForm({ name: '', contact_name: '', email: '', phone: '', address: '' }); setShowVendorModal(true); }} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Vendor
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vendors.map(v => (
              <div key={v.id} className="card">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">{v.name}</h4>
                    {v.contact_name && <p className="text-sm text-gray-500">{v.contact_name}</p>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEditVendor(v)} className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteVendor(v.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  {v.email && <p className="text-sm text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" /> {v.email}</p>}
                  {v.phone && <p className="text-sm text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {v.phone}</p>}
                  {v.address && <p className="text-sm text-gray-400 text-xs mt-1">{v.address}</p>}
                </div>
              </div>
            ))}
            {vendors.length === 0 && <p className="col-span-full text-center py-8 text-gray-500">No vendors added yet</p>}
          </div>
        </div>
      )}

      {/* Budgets Tab */}
      {activeTab === 'budgets' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setShowBudgetModal(true)} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> Set Budget
            </button>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Account</th>
                  <th className="table-header">Period</th>
                  <th className="table-header text-right">Budget</th>
                  <th className="table-header text-right">Actual</th>
                  <th className="table-header text-right">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {budgets.map(b => {
                  const account = accounts.find(a => a.id === b.account_id);
                  const actual = account?.balance || 0;
                  const variance = b.amount - actual;
                  return (
                    <tr key={b.id} className="hover:bg-gray-50">
                      <td className="table-cell font-medium">{account?.name || 'Unknown'}</td>
                      <td className="table-cell">{b.month}/{b.year}</td>
                      <td className="table-cell text-right">${b.amount?.toLocaleString()}</td>
                      <td className="table-cell text-right">${actual.toLocaleString()}</td>
                      <td className={`table-cell text-right font-semibold ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${variance.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {budgets.length === 0 && <p className="text-center py-8 text-gray-500">No budgets set</p>}
          </div>
        </div>
      )}

      {/* Bank Accounts Tab */}
      {activeTab === 'bank' && (
        <div>
          <div className="flex justify-end mb-4"><button onClick={() => setShowBankModal(true)} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Add Bank Account</button></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bankAccounts.map(b => (
              <div key={b.id} className="card">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"><Landmark className="w-6 h-6 text-blue-600" /></div>
                    <div><h3 className="font-semibold">{b.name}</h3><p className="text-sm text-gray-500">{b.bank_name} • ****{b.account_number?.slice(-4)}</p></div>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-4">${b.current_balance?.toLocaleString()}</div>
                <div className="flex gap-2">
                  <button onClick={() => openTxnModal(b)} className="btn-secondary flex-1 flex items-center justify-center gap-1 text-sm"><ArrowDownCircle className="w-4 h-4" /> Deposit</button>
                  <button onClick={() => { setSelectedBank(b); setTxnForm({ ...txnForm, transaction_type: 'withdrawal' }); setShowTxnModal(true); }} className="btn-secondary flex-1 flex items-center justify-center gap-1 text-sm"><ArrowUpCircle className="w-4 h-4" /> Withdraw</button>
                </div>
              </div>
            ))}
          </div>
          {bankAccounts.length === 0 && <div className="text-center py-12 text-gray-500">No bank accounts. Add your first bank account!</div>}
        </div>
      )}

      {/* Chart of Accounts Tab */}
      {activeTab === 'accounts' && (
        <div className="space-y-6">
          {Object.entries(groupedAccounts).map(([type, accs]) => accs.length > 0 && (
            <div key={type} className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 capitalize flex items-center gap-2">
                {type === 'expense' && <TrendingDown className="w-5 h-5 text-red-500" />}
                {type === 'income' && <TrendingUp className="w-5 h-5 text-green-500" />}
                {type === 'asset' && <Wallet className="w-5 h-5 text-blue-500" />}
                {type === 'liability' && <CreditCard className="w-5 h-5 text-orange-500" />}
                {type === 'equity' && <PiggyBank className="w-5 h-5 text-purple-500" />}
                {type === 'liability' ? 'Liabilities' : type === 'equity' ? 'Equity' : `${type}s`}
              </h3>
              <table className="w-full"><thead className="bg-gray-50"><tr><th className="table-header">Code</th><th className="table-header">Name</th><th className="table-header">Category</th><th className="table-header text-right">Balance</th></tr></thead>
                <tbody className="divide-y divide-gray-100">{accs.map(a => <tr key={a.id} className="hover:bg-gray-50"><td className="table-cell font-mono text-sm">{a.code}</td><td className="table-cell font-medium">{a.name}</td><td className="table-cell text-gray-500 text-sm">{categoryLabels[a.category] || a.category?.replace(/_/g, ' ')}</td><td className={`table-cell text-right font-semibold ${type === 'expense' || type === 'liability' ? 'text-red-600' : 'text-green-600'}`}>${a.balance?.toLocaleString()}</td></tr>)}</tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-600" /> Balance Sheet</h3>
            <div className="space-y-4">
              <div className="flex justify-between"><span>Total Assets:</span><span className="font-semibold text-green-600">${accounts.filter(a => a.type === 'asset').reduce((s, a) => s + (a.balance || 0), 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span>Total Liabilities:</span><span className="font-semibold text-red-600">${accounts.filter(a => a.type === 'liability').reduce((s, a) => s + (a.balance || 0), 0).toLocaleString()}</span></div>
              <div className="flex justify-between border-t pt-2"><span>Equity:</span><span className="font-semibold">${accounts.filter(a => a.type === 'equity').reduce((s, a) => s + (a.balance || 0), 0).toLocaleString()}</span></div>
            </div>
          </div>
          <div className="card">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Scale className="w-5 h-5 text-blue-600" /> Income Statement</h3>
            <div className="space-y-4">
              <div className="flex justify-between"><span>Total Income:</span><span className="font-semibold text-green-600">${accounts.filter(a => a.type === 'income').reduce((s, a) => s + (a.balance || 0), 0).toLocaleString()}</span></div>
              <div className="flex justify-between"><span>Total Expenses:</span><span className="font-semibold text-red-600">${accounts.filter(a => a.type === 'expense').reduce((s, a) => s + (a.balance || 0), 0).toLocaleString()}</span></div>
              <div className="flex justify-between border-t pt-2"><span>Net Income:</span><span className={`font-semibold ${(accounts.filter(a => a.type === 'income').reduce((s, a) => s + (a.balance || 0), 0) - accounts.filter(a => a.type === 'expense').reduce((s, a) => s + (a.balance || 0), 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>${(accounts.filter(a => a.type === 'income').reduce((s, a) => s + (a.balance || 0), 0) - accounts.filter(a => a.type === 'expense').reduce((s, a) => s + (a.balance || 0), 0)).toLocaleString()}</span></div>
            </div>
          </div>
          <div className="card md:col-span-2">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-purple-600" /> Expense Breakdown by Category</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(
                expenseAccounts.reduce((acc, a) => {
                  const cat = a.category || 'other';
                  if (!acc[cat]) acc[cat] = 0;
                  acc[cat] += a.balance || 0;
                  return acc;
                }, {})
              ).map(([cat, total]) => (
                <div key={cat} className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500 capitalize">{categoryLabels[cat] || cat}</p>
                  <p className="text-lg font-bold text-gray-900">${total.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Audit Trail Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Audit Trail</h3>
              <p className="text-sm text-gray-500">Complete record of all financial transactions for non-profit compliance</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Shield className="w-4 h-4" />
              <span>{auditTrail.length} records</span>
            </div>
          </div>

          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Timestamp</th>
                  <th className="table-header">Action</th>
                  <th className="table-header">Type</th>
                  <th className="table-header">Description</th>
                  <th className="table-header">User</th>
                  <th className="table-header text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {auditTrail.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="table-cell text-xs text-gray-500">
                      {new Date(entry.timestamp).toLocaleString()}
                    </td>
                    <td className="table-cell">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        entry.action?.includes('income') ? 'bg-green-100 text-green-700' :
                        entry.action?.includes('expense') ? 'bg-red-100 text-red-700' :
                        entry.action?.includes('approved') ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {entry.action?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="table-cell text-gray-500 capitalize">{entry.entity_type}</td>
                    <td className="table-cell font-medium text-sm">{entry.description}</td>
                    <td className="table-cell text-gray-500">{entry.username}</td>
                    <td className="table-cell text-right font-semibold">
                      {entry.amount ? `$${entry.amount.toLocaleString()}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {auditTrail.length === 0 && (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 mx-auto text-gray-200 mb-4" />
                <p className="text-gray-500">No audit trail entries yet</p>
                <p className="text-sm text-gray-400">All income and expense transactions will be logged here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Bank Modal */}
      <Modal isOpen={showBankModal} onClose={() => setShowBankModal(false)} title="Add Bank Account">
        <form onSubmit={handleCreateBank} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Account Name *</label><input type="text" className="input-field" placeholder="e.g. Main Checking" value={bankForm.name} onChange={(e) => setBankForm({ ...bankForm, name: e.target.value })} required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label><input type="text" className="input-field" placeholder="e.g. Chase Bank" value={bankForm.bank_name} onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label><input type="text" className="input-field" value={bankForm.account_number} onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Routing Number</label><input type="text" className="input-field" value={bankForm.routing_number} onChange={(e) => setBankForm({ ...bankForm, routing_number: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label><select className="select-field" value={bankForm.account_type} onChange={(e) => setBankForm({ ...bankForm, account_type: e.target.value })}><option value="checking">Checking</option><option value="savings">Savings</option></select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label><input type="number" step="0.01" className="input-field" value={bankForm.opening_balance} onChange={(e) => setBankForm({ ...bankForm, opening_balance: parseFloat(e.target.value) || 0 })} /></div>
          </div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowBankModal(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Add Account</button></div>
        </form>
      </Modal>

      {/* Record Transaction Modal */}
      <Modal isOpen={showTxnModal} onClose={() => setShowTxnModal(false)} title={`${txnForm.transaction_type === 'deposit' ? 'Record Deposit' : 'Record Withdrawal'} - ${selectedBank?.name}`}>
        <form onSubmit={handleRecordTxn} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label><select className="select-field" value={txnForm.transaction_type} onChange={(e) => setTxnForm({ ...txnForm, transaction_type: e.target.value })}><option value="deposit">Deposit</option><option value="withdrawal">Withdrawal</option></select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label><input type="number" step="0.01" className="input-field" value={txnForm.amount} onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })} required /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label><input type="date" className="input-field" value={txnForm.date} onChange={(e) => setTxnForm({ ...txnForm, date: e.target.value })} required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description *</label><input type="text" className="input-field" value={txnForm.description} onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })} required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Category Account</label><select className="select-field" value={txnForm.category_account_id} onChange={(e) => setTxnForm({ ...txnForm, category_account_id: e.target.value })}><option value="">Select for journal entry...</option>{(txnForm.transaction_type === 'deposit' ? incomeAccounts : expenseAccounts).map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}</select></div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowTxnModal(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">{txnForm.transaction_type === 'deposit' ? 'Record Deposit' : 'Record Withdrawal'}</button></div>
        </form>
      </Modal>

      {/* Add/Edit Expense Modal */}
      <Modal isOpen={showExpenseModal} onClose={() => { setShowExpenseModal(false); setEditingExpense(null); }} title={editingExpense ? 'Edit Expense' : 'Add New Expense'}>
        <form onSubmit={handleSaveExpense} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label><input type="date" className="input-field" value={expenseForm.date} onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label><input type="number" step="0.01" className="input-field" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} required /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description *</label><input type="text" className="input-field" value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} placeholder="What was purchased?" required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Expense Category *</label>
            <select className="select-field" value={expenseForm.category_account_id} onChange={(e) => setExpenseForm({ ...expenseForm, category_account_id: e.target.value })} required>
              <option value="">Select category...</option>
              {Object.entries(
                expenseAccounts.reduce((acc, a) => {
                  const cat = categoryLabels[a.category] || a.category || 'Other';
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(a);
                  return acc;
                }, {})
              ).map(([cat, accs]) => (
                <optgroup key={cat} label={cat}>
                  {accs.map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
              <select className="select-field" value={expenseForm.vendor_id} onChange={(e) => setExpenseForm({ ...expenseForm, vendor_id: e.target.value })}>
                <option value="">Select vendor...</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select className="select-field" value={expenseForm.payment_method} onChange={(e) => setExpenseForm({ ...expenseForm, payment_method: e.target.value })}>
                <option value="check">Check</option>
                <option value="cash">Cash</option>
                <option value="card">Debit/Credit Card</option>
                <option value="transfer">Bank Transfer</option>
                <option value="auto">Auto-Pay</option>
              </select>
            </div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Reference # (Check #, Receipt #)</label><input type="text" className="input-field" value={expenseForm.reference_number} onChange={(e) => setExpenseForm({ ...expenseForm, reference_number: e.target.value })} /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label><textarea className="input-field" rows="2" value={expenseForm.notes} onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })} /></div>
          
          {/* Recurring Expense Options */}
          <div className="border-t pt-4">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={expenseForm.is_recurring} onChange={(e) => setExpenseForm({ ...expenseForm, is_recurring: e.target.checked })} className="w-4 h-4 text-purple-600 rounded" />
              <span className="text-sm font-medium text-gray-700">This is a recurring expense</span>
              <Repeat className="w-4 h-4 text-purple-500" />
            </label>
            {expenseForm.is_recurring && (
              <div className="ml-6 p-3 bg-purple-50 rounded-lg space-y-3 animate-fadeIn">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">Frequency</label>
                    <select className="select-field" value={expenseForm.recurring_frequency} onChange={(e) => setExpenseForm({ ...expenseForm, recurring_frequency: e.target.value })}>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-purple-700 mb-1">End Date (optional)</label>
                    <input type="date" className="input-field" value={expenseForm.recurring_end_date} onChange={(e) => setExpenseForm({ ...expenseForm, recurring_end_date: e.target.value })} min={expenseForm.date} />
                  </div>
                </div>
                <p className="text-xs text-purple-600">Recurring expenses will automatically create new entries on schedule.</p>
              </div>
            )}
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={expenseForm.requires_approval} onChange={(e) => setExpenseForm({ ...expenseForm, requires_approval: e.target.checked })} className="w-4 h-4 text-purple-600 rounded" />
              <span className="text-sm text-gray-700">Requires approval before posting</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => { setShowExpenseModal(false); setEditingExpense(null); }} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{editingExpense ? 'Update Expense' : 'Save Expense'}</button>
          </div>
        </form>
      </Modal>

      {/* Add/Edit Vendor Modal */}
      <Modal isOpen={showVendorModal} onClose={() => { setShowVendorModal(false); setEditingVendor(null); }} title={editingVendor ? 'Edit Vendor' : 'Add Vendor'}>
        <form onSubmit={handleSaveVendor} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name *</label><input type="text" className="input-field" value={vendorForm.name} onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} required /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label><input type="text" className="input-field" value={vendorForm.contact_name} onChange={(e) => setVendorForm({ ...vendorForm, contact_name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" className="input-field" value={vendorForm.email} onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="tel" className="input-field" value={vendorForm.phone} onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })} /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Address</label><textarea className="input-field" rows="2" value={vendorForm.address} onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })} /></div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => { setShowVendorModal(false); setEditingVendor(null); }} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">{editingVendor ? 'Update Vendor' : 'Add Vendor'}</button></div>
        </form>
      </Modal>

      {/* Set Budget Modal */}
      <Modal isOpen={showBudgetModal} onClose={() => setShowBudgetModal(false)} title="Set Budget">
        <form onSubmit={handleSaveBudget} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Account *</label>
            <select className="select-field" value={budgetForm.account_id} onChange={(e) => setBudgetForm({ ...budgetForm, account_id: e.target.value })} required>
              <option value="">Select account...</option>
              {expenseAccounts.map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Year *</label><input type="number" className="input-field" value={budgetForm.year} onChange={(e) => setBudgetForm({ ...budgetForm, year: parseInt(e.target.value) })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Month *</label>
              <select className="select-field" value={budgetForm.month} onChange={(e) => setBudgetForm({ ...budgetForm, month: parseInt(e.target.value) })} required>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{new Date(2000, m-1, 1).toLocaleString('default', { month: 'long' })}</option>)}
              </select>
            </div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Budget Amount *</label><input type="number" step="0.01" className="input-field" value={budgetForm.amount} onChange={(e) => setBudgetForm({ ...budgetForm, amount: e.target.value })} required /></div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowBudgetModal(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Set Budget</button></div>
        </form>
      </Modal>

      {/* Record Income Modal */}
      <Modal isOpen={showIncomeModal} onClose={() => setShowIncomeModal(false)} title="Record Income">
        <form onSubmit={handleSaveIncome} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label><input type="date" className="input-field" value={incomeForm.date} onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label><input type="number" step="0.01" className="input-field" value={incomeForm.amount} onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })} required /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Income Source *</label>
            <select className="select-field" value={incomeForm.source} onChange={(e) => setIncomeForm({ ...incomeForm, source: e.target.value })} required>
              <option value="tithes">Tithes</option>
              <option value="offerings">General Offerings</option>
              <option value="donations">Designated Donations</option>
              <option value="fundraiser">Fundraiser</option>
              <option value="rental">Building Rental</option>
              <option value="other">Other Income</option>
            </select>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Description *</label><input type="text" className="input-field" value={incomeForm.description} onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })} placeholder="e.g., Sunday Service Collection" required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Donor Name (optional)</label><input type="text" className="input-field" value={incomeForm.donor_name} onChange={(e) => setIncomeForm({ ...incomeForm, donor_name: e.target.value })} placeholder="Anonymous if blank" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select className="select-field" value={incomeForm.payment_method} onChange={(e) => setIncomeForm({ ...incomeForm, payment_method: e.target.value })}>
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="card">Debit/Credit Card</option>
                <option value="transfer">Bank Transfer</option>
                <option value="online">Online Giving</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Reference # (Check #)</label><input type="text" className="input-field" value={incomeForm.reference_number} onChange={(e) => setIncomeForm({ ...incomeForm, reference_number: e.target.value })} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Deposit to Bank Account</label>
              <select className="select-field" value={incomeForm.bank_account_id} onChange={(e) => setIncomeForm({ ...incomeForm, bank_account_id: e.target.value })}>
                <option value="">Select bank account...</option>
                {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label><textarea className="input-field" rows="2" value={incomeForm.notes} onChange={(e) => setIncomeForm({ ...incomeForm, notes: e.target.value })} /></div>
          
          {/* Restricted Funds */}
          <div className="border-t pt-4">
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={incomeForm.is_restricted} onChange={(e) => setIncomeForm({ ...incomeForm, is_restricted: e.target.checked })} className="w-4 h-4 text-purple-600 rounded" />
              <span className="text-sm font-medium text-gray-700">Restricted Funds (designated for specific purpose)</span>
            </label>
            {incomeForm.is_restricted && (
              <div className="ml-6">
                <input type="text" className="input-field" value={incomeForm.restriction_purpose} onChange={(e) => setIncomeForm({ ...incomeForm, restriction_purpose: e.target.value })} placeholder="e.g., Building Fund, Youth Mission Trip" />
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowIncomeModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Record Income</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Kiosk Component with Standalone Link
const Kiosk = ({ autoLaunchKiosk = false }) => {
  const [mode, setMode] = useState(autoLaunchKiosk ? 'kiosk' : 'admin');
  const [events, setEvents] = useState([]);
  const [todaysEvents, setTodaysEvents] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [kioskLink, setKioskLink] = useState('');
  const [eventForm, setEventForm] = useState({ name: '', event_type: 'service', date: '', start_time: '' });

  useEffect(() => { fetchEvents(); fetchTodaysEvents(); fetchCheckins(); generateKioskLink(); }, []);
  useEffect(() => { if (autoLaunchKiosk) setMode('kiosk'); }, [autoLaunchKiosk]);

  const fetchEvents = async () => { try { const r = await api.get('/api/kiosk/events'); setEvents(r.data.events); } catch (e) { console.error(e); } };
  const fetchTodaysEvents = async () => { try { const r = await api.get('/api/kiosk/events/today'); setTodaysEvents(r.data.events); if (r.data.events.length > 0) setSelectedEvent(r.data.events[0]); } catch (e) { console.error(e); } };
  const fetchCheckins = async () => { try { const r = await api.get('/api/kiosk/checkins'); setCheckins(r.data.checkins); } catch (e) { console.error(e); } };

  const generateKioskLink = () => {
    const baseUrl = window.location.origin;
    setKioskLink(`${baseUrl}#/kiosk`);
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (term.length >= 2) { try { const r = await api.get(`/api/kiosk/search?query=${term}`); setSearchResults(r.data.results); } catch (e) { console.error(e); } }
    else setSearchResults([]);
  };

  const handleCheckin = async (memberId) => {
    if (!selectedEvent) { alert('Please select an event first'); return; }
    try { await api.post('/api/kiosk/checkins', { member_id: memberId, event_id: selectedEvent.id, checkin_time: new Date().toISOString() }); fetchCheckins(); setSearchTerm(''); setSearchResults([]); alert('✓ Check-in successful!'); } catch (e) { console.error(e); }
  };

  const handleCreateEvent = async (e) => { e.preventDefault(); try { await api.post('/api/kiosk/events', eventForm); fetchEvents(); fetchTodaysEvents(); setShowEventModal(false); } catch (er) { console.error(er); } };

  const copyKioskLink = () => { navigator.clipboard.writeText(kioskLink); alert('Kiosk link copied!'); };

  if (mode === 'kiosk') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 p-8" data-testid="kiosk-mode">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Church className="w-16 h-16 text-white mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-white mb-2">Welcome!</h1>
            <p className="text-purple-200">Please check in for today's event</p>
          </div>
          {selectedEvent && <div className="bg-white/20 backdrop-blur rounded-xl p-4 mb-6 text-center"><p className="text-white text-lg">{selectedEvent.name}</p><p className="text-purple-200">{selectedEvent.start_time}</p></div>}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
              <input type="text" placeholder="Search by name or phone..." className="w-full pl-14 pr-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-purple-500" value={searchTerm} onChange={(e) => handleSearch(e.target.value)} autoFocus />
            </div>
            {searchResults.length > 0 && (
              <div className="space-y-3">{searchResults.map((m) => (
                <button key={m.id} onClick={() => handleCheckin(m.id)} className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all">
                  {m.photo ? <img src={m.photo} alt="" className="w-16 h-16 rounded-full object-cover" /> : <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center"><User className="w-8 h-8 text-purple-600" /></div>}
                  <div className="text-left"><p className="text-xl font-semibold">{m.first_name} {m.last_name}</p><p className="text-gray-500">{m.phone}</p></div>
                  <CheckCircle className="w-8 h-8 text-green-500 ml-auto" />
                </button>
              ))}</div>
            )}
          </div>
          <button onClick={() => setMode('admin')} className="mt-8 text-purple-200 hover:text-white text-sm">← Back to Admin</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kiosk-admin">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-gray-900">Kiosk Management</h3>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowEventModal(true)} className="btn-secondary flex items-center gap-2"><Plus className="w-4 h-4" /> Create Event</button>
          <button onClick={() => setMode('kiosk')} className="btn-primary flex items-center gap-2"><Monitor className="w-4 h-4" /> Launch Kiosk</button>
        </div>
      </div>

      {/* Kiosk Link Card */}
      <div className="card bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center"><ExternalLink className="w-6 h-6 text-white" /></div>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">Kiosk Link for iPad/Tablet</h4>
            <p className="text-sm text-gray-600">Use this link on any iPad or tablet browser for self-service check-in</p>
            <div className="flex items-center gap-2 mt-2">
              <code className="bg-white px-3 py-1 rounded border text-sm flex-1 overflow-x-auto">{kioskLink}</code>
              <button onClick={copyKioskLink} className="btn-secondary flex items-center gap-1 text-sm"><Copy className="w-4 h-4" /> Copy</button>
              <a href={kioskLink} target="_blank" rel="noopener noreferrer" className="btn-primary flex items-center gap-1 text-sm"><ExternalLink className="w-4 h-4" /> Open</a>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card">
          <h4 className="font-semibold text-gray-900 mb-4">Today's Events</h4>
          {todaysEvents.length > 0 ? <div className="space-y-3">{todaysEvents.map(e => <div key={e.id} className="p-3 border rounded-lg">
            <div className="flex items-center justify-between">
              <p className="font-medium">{e.name}</p>
              {e.source === 'calendar' && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Calendar</span>}
            </div>
            <p className="text-sm text-gray-500">{e.start_time}{e.location && ` • ${e.location}`}</p>
          </div>)}</div> : <p className="text-gray-500">No events today</p>}
        </div>
        <div className="card lg:col-span-2">
          <h4 className="font-semibold text-gray-900 mb-4">Recent Check-ins</h4>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {checkins.slice(0, 10).map(c => (
              <div key={c.id} className="flex items-center gap-3 p-3 border rounded-lg">
                {c.photo ? <img src={c.photo} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center"><User className="w-5 h-5 text-purple-600" /></div>}
                <div><p className="font-medium">{c.person_name}</p><p className="text-sm text-gray-500">{new Date(c.checkin_time).toLocaleTimeString()}</p></div>
                <span className={`badge ml-auto ${c.person_type === 'member' ? 'badge-success' : 'badge-info'}`}>{c.person_type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* All Events - Combined Calendar and Kiosk Events */}
      <div className="card">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-purple-600" /> All Upcoming Events
          <span className="text-sm font-normal text-gray-500">(Kiosk + Calendar)</span>
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Event</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Date</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Time</th>
                <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Source</th>
              </tr>
            </thead>
            <tbody>
              {events.slice(0, 15).map(e => (
                <tr key={e.id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-3">
                    <p className="font-medium text-gray-900">{e.name}</p>
                    {e.location && <p className="text-xs text-gray-500">{e.location}</p>}
                  </td>
                  <td className="py-2 px-3 text-sm text-gray-600">{e.date}</td>
                  <td className="py-2 px-3 text-sm text-gray-600">{e.start_time || '—'}</td>
                  <td className="py-2 px-3">
                    {e.source === 'calendar' ? (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Calendar</span>
                    ) : (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Kiosk</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {events.length === 0 && <p className="text-gray-500 text-center py-4">No events found</p>}
        </div>
      </div>

      <Modal isOpen={showEventModal} onClose={() => setShowEventModal(false)} title="Create Event">
        <form onSubmit={handleCreateEvent} className="space-y-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label><input type="text" className="input-field" value={eventForm.name} onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label><select className="select-field" value={eventForm.event_type} onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}><option value="service">Service</option><option value="class">Class</option><option value="meeting">Meeting</option></select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Date *</label><input type="date" className="input-field" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} required /></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label><input type="time" className="input-field" value={eventForm.start_time} onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })} required /></div>
          <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={() => setShowEventModal(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Create</button></div>
        </form>
      </Modal>
    </div>
  );
};

// Giving Goals Admin Component
const GivingGoalsAdmin = () => {
  const [goals, setGoals] = useState([]);
  const [allocations, setAllocations] = useState({ tithes: [], benevolence: [] });
  const [loading, setLoading] = useState(true);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({
    name: '',
    description: '',
    target_amount: '',
    current_amount: '',
    category: 'tithes',
    is_active: true
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [goalsRes, impactRes] = await Promise.all([
        api.get('/api/giving-impact/goals'),
        api.get('/api/public/giving-impact')
      ]);
      setGoals(goalsRes.data.goals || []);
      setAllocations(impactRes.data.allocations || { tithes: [], benevolence: [] });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoal = async () => {
    try {
      const data = {
        ...goalForm,
        target_amount: parseFloat(goalForm.target_amount) || 0,
        current_amount: parseFloat(goalForm.current_amount) || 0
      };
      
      if (editingGoal) {
        await api.put(`/api/giving-impact/goals/${editingGoal.id}`, data);
      } else {
        await api.post('/api/giving-impact/goals', data);
      }
      
      alert('Goal saved successfully!');
      setShowGoalModal(false);
      setEditingGoal(null);
      setGoalForm({ name: '', description: '', target_amount: '', current_amount: '', category: 'tithes', is_active: true });
      fetchData();
    } catch (e) {
      console.error(e);
      alert('Error saving goal');
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      name: goal.name,
      description: goal.description,
      target_amount: goal.target_amount?.toString() || '',
      current_amount: goal.current_amount?.toString() || '',
      category: goal.category || 'tithes',
      is_active: goal.is_active !== false
    });
    setShowGoalModal(true);
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      await api.delete(`/api/giving-impact/goals/${goalId}`);
      fetchData();
    } catch (e) {
      console.error(e);
      alert('Error deleting goal');
    }
  };

  const handleUpdateAllocation = (type, index, field, value) => {
    const updated = { ...allocations };
    updated[type][index][field] = field === 'percentage' ? parseInt(value) || 0 : value;
    setAllocations(updated);
  };

  const handleSaveAllocations = async () => {
    // Validate percentages sum to 100
    const tithesSum = allocations.tithes.reduce((sum, a) => sum + (a.percentage || 0), 0);
    const benevolenceSum = allocations.benevolence.reduce((sum, a) => sum + (a.percentage || 0), 0);
    
    if (tithesSum !== 100) {
      alert(`Tithes allocations must sum to 100% (currently ${tithesSum}%)`);
      return;
    }
    if (benevolenceSum !== 100) {
      alert(`Benevolence allocations must sum to 100% (currently ${benevolenceSum}%)`);
      return;
    }
    
    try {
      await api.put('/api/giving-impact/allocations', allocations);
      alert('Allocations saved successfully!');
    } catch (e) {
      console.error(e);
      alert('Error saving allocations');
    }
  };

  const addAllocationItem = (type) => {
    const updated = { ...allocations };
    updated[type].push({ name: 'New Category', percentage: 0, description: '' });
    setAllocations(updated);
  };

  const removeAllocationItem = (type, index) => {
    const updated = { ...allocations };
    updated[type].splice(index, 1);
    setAllocations(updated);
  };

  if (loading) return <div className="text-center py-8"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600" /></div>;

  return (
    <div className="space-y-6">
      {/* Giving Goals Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-600" /> Giving Goals
          </h3>
          <button onClick={() => { setEditingGoal(null); setGoalForm({ name: '', description: '', target_amount: '', current_amount: '', category: 'tithes', is_active: true }); setShowGoalModal(true); }} className="btn-primary text-sm flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add Goal
          </button>
        </div>
        
        <p className="text-sm text-gray-500 mb-4">These goals are shown on the public Giving Impact page with progress bars.</p>
        
        <div className="space-y-3">
          {goals.map((goal) => {
            const percentage = goal.target_amount > 0 ? Math.round((goal.current_amount / goal.target_amount) * 100) : 0;
            return (
              <div key={goal.id} className={`p-4 rounded-lg border ${goal.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900">{goal.name}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${goal.category === 'benevolence' ? 'bg-pink-100 text-pink-700' : 'bg-purple-100 text-purple-700'}`}>
                        {goal.category}
                      </span>
                      {!goal.is_active && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Inactive</span>}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{goal.description}</p>
                    <div className="mt-2">
                      <div className="flex justify-between text-sm mb-1">
                        <span>${goal.current_amount?.toLocaleString()} / ${goal.target_amount?.toLocaleString()}</span>
                        <span className="font-semibold">{percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className={`h-full rounded-full ${goal.category === 'benevolence' ? 'bg-pink-500' : 'bg-purple-500'}`} style={{ width: `${Math.min(100, percentage)}%` }} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => handleEditGoal(goal)} className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteGoal(goal.id)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {goals.length === 0 && (
            <p className="text-center text-gray-500 py-4">No goals created yet. Click "Add Goal" to create one.</p>
          )}
        </div>
      </div>

      {/* Fund Allocations Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tithes Allocation */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Church className="w-5 h-5 text-purple-600" /> Where Tithes Go
            </h3>
            <button onClick={() => addAllocationItem('tithes')} className="text-sm text-purple-600 hover:text-purple-800">+ Add</button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Percentages must total 100%</p>
          <div className="space-y-3">
            {allocations.tithes?.map((item, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    className="input-field flex-1 text-sm"
                    value={item.name}
                    onChange={(e) => handleUpdateAllocation('tithes', index, 'name', e.target.value)}
                    placeholder="Category name"
                  />
                  <input
                    type="number"
                    className="input-field w-20 text-sm text-center"
                    value={item.percentage}
                    onChange={(e) => handleUpdateAllocation('tithes', index, 'percentage', e.target.value)}
                    min="0"
                    max="100"
                  />
                  <span className="text-gray-500">%</span>
                  <button onClick={() => removeAllocationItem('tithes', index)} className="text-red-500 hover:text-red-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  className="input-field text-xs"
                  value={item.description}
                  onChange={(e) => handleUpdateAllocation('tithes', index, 'description', e.target.value)}
                  placeholder="Description"
                />
              </div>
            ))}
          </div>
          <div className="mt-3 text-right text-sm">
            Total: <span className={`font-bold ${allocations.tithes?.reduce((s, a) => s + (a.percentage || 0), 0) === 100 ? 'text-green-600' : 'text-red-600'}`}>
              {allocations.tithes?.reduce((s, a) => s + (a.percentage || 0), 0)}%
            </span>
          </div>
        </div>

        {/* Benevolence Allocation */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-600" /> Where Benevolence Helps
            </h3>
            <button onClick={() => addAllocationItem('benevolence')} className="text-sm text-pink-600 hover:text-pink-800">+ Add</button>
          </div>
          <p className="text-sm text-gray-500 mb-4">Percentages must total 100%</p>
          <div className="space-y-3">
            {allocations.benevolence?.map((item, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    className="input-field flex-1 text-sm"
                    value={item.name}
                    onChange={(e) => handleUpdateAllocation('benevolence', index, 'name', e.target.value)}
                    placeholder="Category name"
                  />
                  <input
                    type="number"
                    className="input-field w-20 text-sm text-center"
                    value={item.percentage}
                    onChange={(e) => handleUpdateAllocation('benevolence', index, 'percentage', e.target.value)}
                    min="0"
                    max="100"
                  />
                  <span className="text-gray-500">%</span>
                  <button onClick={() => removeAllocationItem('benevolence', index)} className="text-red-500 hover:text-red-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  className="input-field text-xs"
                  value={item.description}
                  onChange={(e) => handleUpdateAllocation('benevolence', index, 'description', e.target.value)}
                  placeholder="Description"
                />
              </div>
            ))}
          </div>
          <div className="mt-3 text-right text-sm">
            Total: <span className={`font-bold ${allocations.benevolence?.reduce((s, a) => s + (a.percentage || 0), 0) === 100 ? 'text-green-600' : 'text-red-600'}`}>
              {allocations.benevolence?.reduce((s, a) => s + (a.percentage || 0), 0)}%
            </span>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSaveAllocations} className="btn-primary flex items-center gap-2">
          <Save className="w-4 h-4" /> Save Allocations
        </button>
      </div>

      {/* Goal Modal */}
      <Modal isOpen={showGoalModal} onClose={() => setShowGoalModal(false)} title={editingGoal ? 'Edit Goal' : 'Add New Goal'}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Goal Name</label>
            <input
              type="text"
              className="input-field"
              value={goalForm.name}
              onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
              placeholder="e.g., Building Fund, Youth Ministry"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="input-field"
              rows="2"
              value={goalForm.description}
              onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
              placeholder="What will this fund be used for?"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount ($)</label>
              <input
                type="number"
                className="input-field"
                value={goalForm.target_amount}
                onChange={(e) => setGoalForm({ ...goalForm, target_amount: e.target.value })}
                placeholder="50000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Amount ($)</label>
              <input
                type="number"
                className="input-field"
                value={goalForm.current_amount}
                onChange={(e) => setGoalForm({ ...goalForm, current_amount: e.target.value })}
                placeholder="25000"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              className="select-field"
              value={goalForm.category}
              onChange={(e) => setGoalForm({ ...goalForm, category: e.target.value })}
            >
              <option value="tithes">Tithes (General Operations)</option>
              <option value="benevolence">Benevolence (Helping Those in Need)</option>
            </select>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={goalForm.is_active}
                onChange={(e) => setGoalForm({ ...goalForm, is_active: e.target.checked })}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <span className="text-sm text-gray-700">Show on public Giving Impact page</span>
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button onClick={() => setShowGoalModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSaveGoal} className="btn-primary">{editingGoal ? 'Update Goal' : 'Create Goal'}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Online Giving Component
const OnlineGiving = () => {
  const { user, canEdit } = useAuth();
  const [funds, setFunds] = useState([]);
  const [selectedFund, setSelectedFund] = useState('');
  const [amount, setAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('give');
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    fetchFunds();
    fetchHistory();
    // Check if returning from payment
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchFunds = async () => {
    try { const r = await api.get('/api/giving/funds'); setFunds(r.data.funds); if (r.data.funds.length > 0) setSelectedFund(r.data.funds[0].id); } catch (e) { console.error(e); }
  };

  const fetchHistory = async () => {
    try { const r = await api.get('/api/giving/history'); setHistory(r.data.donations || []); } catch (e) { console.error(e); }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const r = await api.get(`/api/giving/status/${sessionId}`);
        if (r.data.payment_status === 'paid') {
          setPaymentResult({ success: true, ...r.data });
          fetchHistory();
          setCheckingPayment(false);
          return;
        } else if (r.data.status === 'expired') {
          setPaymentResult({ success: false, message: 'Payment session expired' });
          setCheckingPayment(false);
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPaymentResult({ success: false, message: 'Payment status check timed out' });
          setCheckingPayment(false);
        }
      } catch (e) {
        setPaymentResult({ success: false, message: 'Error checking payment status' });
        setCheckingPayment(false);
      }
    };
    poll();
  };

  const handleGive = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { alert('Please enter a valid amount'); return; }
    setLoading(true);
    try {
      const r = await api.post('/api/giving/checkout', {
        amount: parseFloat(amount),
        fund: selectedFund,
        donor_name: donorName || undefined,
        donor_email: donorEmail || undefined,
        is_recurring: false,
        origin_url: window.location.origin
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Error creating checkout session');
    } finally {
      setLoading(false);
    }
  };

  const presetAmounts = [25, 50, 100, 250, 500];

  return (
    <div className="space-y-6" data-testid="online-giving-page">
      {checkingPayment && (
        <div className="card bg-purple-50 border-purple-200">
          <div className="flex items-center gap-3">
            <div className="animate-spin w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full"></div>
            <span className="text-purple-700">Verifying your payment...</span>
          </div>
        </div>
      )}
      
      {paymentResult && (
        <div className={`card ${paymentResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center gap-3">
            {paymentResult.success ? (
              <>
                <CheckCircle className="w-8 h-8 text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-800">Thank you for your gift!</h3>
                  <p className="text-green-600">${paymentResult.amount?.toFixed(2)} to {paymentResult.fund_name}</p>
                </div>
              </>
            ) : (
              <>
                <X className="w-8 h-8 text-red-600" />
                <div>
                  <h3 className="font-semibold text-red-800">Payment Issue</h3>
                  <p className="text-red-600">{paymentResult.message}</p>
                </div>
              </>
            )}
            <button onClick={() => setPaymentResult(null)} className="ml-auto text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setActiveTab('give')} className={`px-4 py-2 rounded-lg ${activeTab === 'give' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
          <Gift className="w-4 h-4 inline mr-2" />Give Now
        </button>
        <button onClick={() => setActiveTab('history')} className={`px-4 py-2 rounded-lg ${activeTab === 'history' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
          <FileText className="w-4 h-4 inline mr-2" />History
        </button>
        {canEdit('giving') && (
          <button onClick={() => setActiveTab('admin')} className={`px-4 py-2 rounded-lg ${activeTab === 'admin' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
            <Settings className="w-4 h-4 inline mr-2" />Manage Goals
          </button>
        )}
        <a 
          href="/#/giving-impact" 
          target="_blank" 
          rel="noopener noreferrer"
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 hover:from-purple-200 hover:to-pink-200 transition-all flex items-center gap-2"
        >
          <TrendingUp className="w-4 h-4" /> See Where Your Giving Goes
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {activeTab === 'give' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Gift className="w-6 h-6 text-purple-600" /> Make a Gift
            </h3>
            <form onSubmit={handleGive} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Fund</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {funds.map(f => (
                    <button key={f.id} type="button" onClick={() => setSelectedFund(f.id)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${selectedFund === f.id ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}`}>
                      <p className="font-medium">{f.name}</p>
                      <p className="text-xs text-gray-500">{f.description}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {presetAmounts.map(a => (
                    <button key={a} type="button" onClick={() => setAmount(a.toString())}
                      className={`px-4 py-2 rounded-lg border-2 font-medium ${amount === a.toString() ? 'border-purple-600 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-purple-300'}`}>
                      ${a}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                  <input type="number" step="0.01" min="1" placeholder="Other amount" className="input-field pl-8 text-lg"
                    value={amount} onChange={(e) => setAmount(e.target.value)} required />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name (optional)</label>
                  <input type="text" className="input-field" placeholder="Anonymous" value={donorName} onChange={(e) => setDonorName(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                  <input type="email" className="input-field" placeholder="For receipt" value={donorEmail} onChange={(e) => setDonorEmail(e.target.value)} />
                </div>
              </div>

              <button type="submit" disabled={loading} className="btn-primary w-full text-lg py-3 flex items-center justify-center gap-2">
                {loading ? <><div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div> Processing...</> : <><CreditCard className="w-5 h-5" /> Give ${amount || '0'}</>}
              </button>
              <p className="text-center text-sm text-gray-500">Secure payment powered by Stripe</p>
            </form>
          </div>

          <div className="card bg-gradient-to-br from-purple-600 to-purple-800 text-white">
            <h3 className="text-xl font-semibold mb-4">Why Give?</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <Heart className="w-6 h-6 flex-shrink-0" />
                <div><p className="font-medium">Support Ministry</p><p className="text-purple-200 text-sm">Your gifts help us serve our community</p></div>
              </div>
              <div className="flex gap-3">
                <Users className="w-6 h-6 flex-shrink-0" />
                <div><p className="font-medium">Make a Difference</p><p className="text-purple-200 text-sm">Every gift impacts lives</p></div>
              </div>
              <div className="flex gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <div><p className="font-medium">Secure & Easy</p><p className="text-purple-200 text-sm">Safe online giving with Stripe</p></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Online Gifts</h3>
          {history.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No online gifts recorded yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50"><tr>
                  <th className="table-header">Date</th>
                  <th className="table-header">Donor</th>
                  <th className="table-header">Fund</th>
                  <th className="table-header text-right">Amount</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((d, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="table-cell text-gray-500">{new Date(d.completed_at).toLocaleDateString()}</td>
                      <td className="table-cell font-medium">{d.donor_name}</td>
                      <td className="table-cell">{d.fund_name}</td>
                      <td className="table-cell text-right font-semibold text-green-600">${d.amount?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'admin' && <GivingGoalsAdmin />}
    </div>
  );
};

// Onboarding Tour Component
const OnboardingTour = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  
  const tourSteps = [
    { title: 'Welcome to Church CRM!', description: 'Let\'s take a quick tour of the key features to help you get started.', icon: Church, highlight: null },
    { title: 'Dashboard', description: 'Your central hub showing key stats, quick actions, and an overview of your church.', icon: Home, highlight: 'dashboard' },
    { title: 'Members', description: 'Manage your church members, families, and contact information with photo support.', icon: Users, highlight: 'members' },
    { title: 'Employees & Payroll', description: 'Handle employee records, process payroll, and generate W2 forms.', icon: Briefcase, highlight: 'employees' },
    { title: 'Donations & Giving', description: 'Track donations and accept online gifts through our Stripe-powered giving portal.', icon: Gift, highlight: 'donations' },
    { title: 'Accounting', description: 'Full chart of accounts, bank account tracking, and financial reporting.', icon: Calculator, highlight: 'accounting' },
    { title: 'Kiosk', description: 'Self-service check-in system for events. Get a shareable link for tablets!', icon: Monitor, highlight: 'kiosk' },
    { title: 'You\'re All Set!', description: 'You can always access help from the menu. Enjoy using Church CRM!', icon: Sparkles, highlight: null },
  ];

  const currentStep = tourSteps[step];
  const StepIcon = currentStep.icon;

  const handleNext = () => {
    if (step < tourSteps.length - 1) setStep(step + 1);
    else onComplete();
  };

  const handleSkip = () => onComplete();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 p-6 text-white">
          <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-4">
            <StepIcon className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold">{currentStep.title}</h2>
        </div>
        <div className="p-6">
          <p className="text-gray-600 mb-6">{currentStep.description}</p>
          
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-1">
              {tourSteps.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${i === step ? 'bg-purple-600' : 'bg-gray-200'}`} />
              ))}
            </div>
            <span className="text-sm text-gray-500">{step + 1} of {tourSteps.length}</span>
          </div>

          <div className="flex gap-3">
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="btn-secondary flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            <button onClick={handleSkip} className="btn-secondary flex-1">Skip Tour</button>
            <button onClick={handleNext} className="btn-primary flex items-center gap-1">
              {step === tourSteps.length - 1 ? 'Get Started' : 'Next'} {step < tourSteps.length - 1 && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// My Paperwork Component - For staff members to fill out their own documents
const MyPaperwork = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formToken, setFormToken] = useState(null);
  const [formData, setFormData] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [signatureData, setSignatureData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  useEffect(() => {
    fetchMyDocuments();
  }, []);
  
  const fetchMyDocuments = async () => {
    try {
      const response = await api.get('/api/my-documents');
      setData(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleStartForm = async (template) => {
    try {
      // If document has form_token, use it. Otherwise request a new one
      let token = template.form_token;
      
      if (!token) {
        const response = await api.post(`/api/my-documents/request/${template.id}`);
        token = response.data.form_token;
      }
      
      // Fetch form data
      const formResponse = await axios.get(`${API_URL}/api/public/form/${token}`);
      if (formResponse.data.completed) {
        alert('This form has already been completed.');
        fetchMyDocuments();
        return;
      }
      
      setFormToken(token);
      setFormData(formResponse.data);
      
      // Pre-fill form values
      const prefilled = {};
      formResponse.data.form_fields.forEach(field => {
        if (formResponse.data.prefill_data[field.name]) {
          prefilled[field.name] = formResponse.data.prefill_data[field.name];
        } else if (field.type === 'date' && field.name === 'date') {
          prefilled[field.name] = new Date().toISOString().split('T')[0];
        } else {
          prefilled[field.name] = field.type === 'checkbox' ? false : '';
        }
      });
      setFormValues(prefilled);
      setShowForm(true);
      setSignatureData(null);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Error loading form');
    }
  };
  
  // Signature canvas functions
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };
  
  useEffect(() => {
    if (showForm && canvasRef.current) {
      setTimeout(initCanvas, 100);
    }
  }, [showForm]);
  
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };
  
  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoords(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoords(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };
  
  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };
  
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };
  
  const handleInputChange = (name, value) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = formData.form_fields
      .filter(f => f.required && f.type !== 'signature' && !formValues[f.name])
      .map(f => f.label);
    
    if (missingFields.length > 0) {
      alert(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    // Check signature
    const hasSignatureField = formData.form_fields.some(f => f.type === 'signature' && f.required);
    if (hasSignatureField && !signatureData) {
      alert('Please provide your signature');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/form/${formToken}/submit`, {
        form_data: formValues,
        signature_data: signatureData
      });
      alert('Form submitted successfully!');
      setShowForm(false);
      setFormData(null);
      setFormToken(null);
      fetchMyDocuments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error submitting form');
    } finally {
      setSubmitting(false);
    }
  };
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="badge badge-success flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Completed</span>;
      case 'pending':
      case 'generated':
        return <span className="badge badge-warning flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      default:
        return <span className="badge bg-gray-100 text-gray-600 flex items-center gap-1"><FileText className="w-3 h-3" /> Not Started</span>;
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading your documents...</p>
        </div>
      </div>
    );
  }
  
  // Form filling modal
  if (showForm && formData) {
    return (
      <div className="space-y-6" data-testid="my-paperwork-form">
        <div className="flex items-center gap-4">
          <button onClick={() => { setShowForm(false); setFormData(null); }} className="btn-secondary">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Documents
          </button>
        </div>
        
        <div className="card max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white p-6 -m-6 mb-6 rounded-t-xl">
            <h2 className="text-xl font-bold">{formData.document_name}</h2>
            <p className="text-purple-200 text-sm mt-1">Please complete all required fields</p>
          </div>
          
          <form onSubmit={handleSubmitForm} className="space-y-5">
            {formData.form_fields.map((field) => (
              <div key={field.name}>
                {field.type !== 'signature' && field.type !== 'checkbox' && (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    
                    {(field.type === 'text' || field.type === 'password') && (
                      <input
                        type="text"
                        className="input"
                        value={formValues[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                    )}
                    
                    {field.type === 'email' && (
                      <input type="email" className="input" value={formValues[field.name] || ''} onChange={(e) => handleInputChange(field.name, e.target.value)} required={field.required} />
                    )}
                    
                    {field.type === 'tel' && (
                      <input type="tel" className="input" value={formValues[field.name] || ''} onChange={(e) => handleInputChange(field.name, e.target.value)} required={field.required} />
                    )}
                    
                    {field.type === 'number' && (
                      <input type="number" className="input" value={formValues[field.name] || ''} onChange={(e) => handleInputChange(field.name, e.target.value)} required={field.required} />
                    )}
                    
                    {field.type === 'date' && (
                      <input type="date" className="input" value={formValues[field.name] || ''} onChange={(e) => handleInputChange(field.name, e.target.value)} required={field.required} />
                    )}
                    
                    {field.type === 'select' && (
                      <select className="input" value={formValues[field.name] || ''} onChange={(e) => handleInputChange(field.name, e.target.value)} required={field.required}>
                        <option value="">Select...</option>
                        {field.options?.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    )}
                    
                    {field.type === 'textarea' && (
                      <textarea className="input" rows="3" value={formValues[field.name] || ''} onChange={(e) => handleInputChange(field.name, e.target.value)} required={field.required} />
                    )}
                  </>
                )}
                
                {field.type === 'checkbox' && (
                  <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-5 h-5 text-purple-600 border-gray-300 rounded mt-0.5"
                      checked={formValues[field.name] || false}
                      onChange={(e) => handleInputChange(field.name, e.target.checked)}
                      required={field.required}
                    />
                    <span className="text-gray-700">{field.label} {field.required && <span className="text-red-500">*</span>}</span>
                  </label>
                )}
                
                {field.type === 'signature' && (
                  <div className="border-t pt-5 mt-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <p className="text-sm text-gray-500 mb-3">Draw your signature below using your mouse or finger</p>
                    <div className="border-2 border-gray-300 rounded-lg p-2 bg-white">
                      <canvas
                        ref={canvasRef}
                        width={500}
                        height={150}
                        className="w-full h-32 cursor-crosshair touch-none bg-gray-50"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                      <button type="button" onClick={clearSignature} className="text-sm text-purple-600 hover:text-purple-800">
                        Clear Signature
                      </button>
                      {signatureData && <span className="text-sm text-green-600">✓ Signature captured</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            <div className="border-t pt-5 flex gap-3">
              <button type="button" onClick={() => { setShowForm(false); setFormData(null); }} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="btn-primary flex-1 flex items-center justify-center gap-2">
                {submitting ? <><RefreshCw className="w-4 h-4 animate-spin" /> Submitting...</> : <><CheckCircle className="w-4 h-4" /> Submit Form</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="my-paperwork-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Paperwork</h1>
          <p className="text-gray-600">Complete your HR documents online</p>
        </div>
        <button onClick={fetchMyDocuments} className="btn-secondary">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </button>
      </div>
      
      {/* Staff Profile Card */}
      {data?.staff_member ? (
        <div className="card bg-gradient-to-r from-purple-600 to-purple-800 text-white">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{data.staff_member.name}</h2>
              <p className="text-purple-200">{data.staff_member.position} • {data.staff_member.department}</p>
              {data.staff_member.hire_date && (
                <p className="text-purple-300 text-sm">Hired: {new Date(data.staff_member.hire_date).toLocaleDateString()}</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="card bg-yellow-50 border border-yellow-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-600" />
            <div>
              <h3 className="font-semibold text-yellow-800">Staff Profile Not Linked</h3>
              <p className="text-yellow-700 text-sm">Contact HR to link your account to your staff profile to see your documents.</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Progress Stats */}
      {data?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card text-center">
            <div className="text-3xl font-bold text-green-600">{data.stats.completed}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-yellow-600">{data.stats.pending}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="card text-center">
            <div className="text-3xl font-bold text-gray-400">{data.stats.not_started}</div>
            <div className="text-sm text-gray-600">Not Started</div>
          </div>
          <div className="card text-center bg-purple-50">
            <div className="text-3xl font-bold text-purple-600">{Math.round((data.stats.completed / data.stats.total) * 100)}%</div>
            <div className="text-sm text-gray-600">Complete</div>
          </div>
        </div>
      )}
      
      {/* Document Templates */}
      {data?.templates && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Required Documents</h3>
          <div className="space-y-3">
            {data.templates.map((template) => (
              <div key={template.id} className={`flex items-center justify-between p-4 rounded-xl border ${template.status === 'completed' ? 'bg-green-50 border-green-200' : template.status === 'pending' || template.status === 'generated' ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${template.status === 'completed' ? 'bg-green-100' : template.status === 'pending' || template.status === 'generated' ? 'bg-yellow-100' : 'bg-gray-200'}`}>
                    {template.status === 'completed' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : template.status === 'pending' || template.status === 'generated' ? (
                      <Clock className="w-5 h-5 text-yellow-600" />
                    ) : (
                      <FileText className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{template.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      {getStatusBadge(template.status)}
                      {template.completed_at && (
                        <span className="text-xs text-gray-500">
                          Completed {new Date(template.completed_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {template.status === 'completed' ? (
                    <>
                      <button
                        onClick={() => window.open(`${API_URL}/api/documents/${template.document_id}/view`, '_blank')}
                        className="btn-primary text-sm flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                      <button
                        onClick={() => window.open(`${API_URL}/api/documents/${template.document_id}/download`, '_blank')}
                        className="btn-secondary text-sm flex items-center gap-1"
                      >
                        <Download className="w-4 h-4" /> Download
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleStartForm(template)}
                      className="btn-primary text-sm"
                      data-testid={`fill-${template.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" /> {template.status === 'not_started' ? 'Start' : 'Continue'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Recent Activity */}
      {data?.documents?.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Recent Documents</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Document</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.documents.slice(0, 10).map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{doc.template_name}</td>
                    <td className="table-cell">{getStatusBadge(doc.status)}</td>
                    <td className="table-cell text-gray-500">{new Date(doc.created_at).toLocaleDateString()}</td>
                    <td className="table-cell">
                      {doc.status === 'completed' && (
                        <button
                          onClick={() => window.open(`${API_URL}/api/documents/${doc.id}/download`, '_blank')}
                          className="text-purple-600 hover:text-purple-800"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== DIGITAL BUSINESS CARDS SYSTEM ====================
const BISHOP_SEAL_URL = 'https://customer-assets.emergentagent.com/job_pastoral-events/artifacts/96equzvk_609729639_25560483913609034_3911511700910521101_n%20%281%29.png';

const DigitalBusinessCards = () => {
  const { user, canEdit } = useAuth();
  const [cards, setCards] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewCard, setPreviewCard] = useState(null);
  const [formData, setFormData] = useState({
    member_id: '',
    title: '',
    department: '',
    phone: '',
    email: '',
    photo_url: '',
    is_approved: false,
    card_style: 'classic'
  });

  useEffect(() => {
    fetchCards();
    fetchStaffMembers();
  }, []);

  const fetchCards = async () => {
    try {
      const res = await api.get('/api/business-cards');
      setCards(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchStaffMembers = async () => {
    try {
      const res = await api.get('/api/members');
      // Get members array from response
      const members = res.data.members || res.data || [];
      // For digital ID cards, show all members (not just employees)
      setStaffMembers(members);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCard) {
        await api.put(`/api/business-cards/${selectedCard.id}`, formData);
      } else {
        await api.post('/api/business-cards', formData);
      }
      fetchCards();
      setShowModal(false);
      resetForm();
    } catch (e) {
      alert('Error saving card: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleApprove = async (cardId) => {
    try {
      await api.put(`/api/business-cards/${cardId}/approve`);
      fetchCards();
    } catch (e) {
      alert('Error approving card');
    }
  };

  const handleRevoke = async (cardId) => {
    if (!window.confirm('Are you sure you want to revoke this ID card? It will no longer be verifiable.')) return;
    try {
      await api.put(`/api/business-cards/${cardId}/revoke`);
      fetchCards();
    } catch (e) {
      alert('Error revoking card');
    }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this ID card?')) return;
    try {
      await api.delete(`/api/business-cards/${cardId}`);
      fetchCards();
    } catch (e) {
      alert('Error deleting card');
    }
  };

  const resetForm = () => {
    setFormData({
      member_id: '',
      title: '',
      department: '',
      phone: '',
      email: '',
      photo_url: '',
      is_approved: false,
      card_style: 'classic'
    });
    setSelectedCard(null);
  };

  const openEditModal = (card) => {
    setSelectedCard(card);
    setFormData({
      member_id: card.member_id,
      title: card.title,
      department: card.department || '',
      phone: card.phone || '',
      email: card.email || '',
      photo_url: card.photo_url || '',
      is_approved: card.is_approved,
      card_style: card.card_style || 'classic'
    });
    setShowModal(true);
  };

  const handleMemberSelect = (memberId) => {
    const member = staffMembers.find(m => m.id === memberId);
    if (member) {
      setFormData({
        ...formData,
        member_id: memberId,
        title: member.position || '',
        department: member.department || '',
        phone: member.phone || '',
        email: member.email || '',
        photo_url: member.photo_url || ''
      });
    }
  };

  const copyVerificationLink = (card) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/#/verify/${card.verification_code}`;
    navigator.clipboard.writeText(link);
    alert('Verification link copied to clipboard!');
  };

  const openPreview = (card) => {
    setPreviewCard(card);
    setShowPreview(true);
  };

  const getMyCard = () => {
    if (!user) return null;
    return cards.find(c => c.user_id === user.id || c.email === user.email);
  };

  const myCard = getMyCard();

  return (
    <div className="space-y-6" data-testid="business-cards-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BadgeCheck className="w-7 h-7 text-amber-400" />
              Digital ID Cards
            </h1>
            <p className="text-slate-300 text-sm mt-1">Official Church Staff & Pastor Credentials with Bishop's Seal</p>
          </div>
          <button 
            onClick={() => { resetForm(); setShowModal(true); }} 
            className="bg-amber-500 hover:bg-amber-600 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors"
            data-testid="create-card-btn"
          >
            <Plus className="w-4 h-4" /> Create ID Card
          </button>
        </div>
      </div>

      {/* My Card Section */}
      {myCard && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
            <BadgeCheck className="w-5 h-5" /> My Digital ID Card
          </h2>
          <div className="flex flex-wrap gap-6 items-start">
            {/* Mini Card Preview */}
            <div 
              className="w-80 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 text-white shadow-xl cursor-pointer transform hover:scale-105 transition-transform"
              onClick={() => openPreview(myCard)}
            >
              <div className="flex items-start gap-3">
                <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0">
                  {myCard.photo_url ? (
                    <img src={myCard.photo_url} alt={myCard.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                      <User className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-sm truncate">{myCard.name}</h3>
                  <p className="text-amber-400 text-xs">{myCard.title}</p>
                  <p className="text-slate-400 text-xs">{myCard.department}</p>
                </div>
                <img src={BISHOP_SEAL_URL} alt="Bishop's Seal" className="w-12 h-12 object-contain" />
              </div>
              {myCard.is_approved && (
                <div className="mt-3 flex items-center gap-1 text-xs text-emerald-400">
                  <CheckCircle className="w-3 h-3" /> Bishop Approved
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => openPreview(myCard)} 
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                <Eye className="w-4 h-4" /> View Full Card
              </button>
              <button 
                onClick={() => copyVerificationLink(myCard)} 
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                <Copy className="w-4 h-4" /> Copy Verification Link
              </button>
              <button 
                onClick={() => {
                  const link = `${window.location.origin}/#/verify/${myCard.verification_code}`;
                  if (navigator.share) {
                    navigator.share({ title: 'My Church ID Card', url: link });
                  } else {
                    navigator.clipboard.writeText(link);
                    alert('Link copied!');
                  }
                }} 
                className="btn-primary flex items-center gap-2 text-sm"
              >
                <Share2 className="w-4 h-4" /> Share Card
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Cards Table */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">All Staff ID Cards</h2>
          <span className="text-sm text-gray-500">{cards.length} cards</span>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : cards.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <BadgeCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No ID cards created yet</p>
            <p className="text-sm">Create digital ID cards for church staff and pastors</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Staff Member</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Title</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Department</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Verification Code</th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {cards.map(card => (
                  <tr key={card.id} className="hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full overflow-hidden">
                          {card.photo_url ? (
                            <img src={card.photo_url} alt={card.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{card.name}</p>
                          <p className="text-xs text-gray-500">{card.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-sm">{card.title}</td>
                    <td className="p-3 text-sm text-gray-600">{card.department || '-'}</td>
                    <td className="p-3">
                      {card.is_approved ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                          <CheckCircle className="w-3 h-3" /> Approved
                        </span>
                      ) : card.is_revoked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                          <X className="w-3 h-3" /> Revoked
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          <Clock className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{card.verification_code}</code>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => openPreview(card)} 
                          className="p-2 hover:bg-gray-100 rounded text-gray-600"
                          title="Preview Card"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => copyVerificationLink(card)} 
                          className="p-2 hover:bg-gray-100 rounded text-gray-600"
                          title="Copy Verification Link"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        {!card.is_approved && !card.is_revoked && (
                          <button 
                            onClick={() => handleApprove(card.id)} 
                            className="p-2 hover:bg-emerald-100 rounded text-emerald-600"
                            title="Approve with Bishop's Seal"
                          >
                            <BadgeCheck className="w-4 h-4" />
                          </button>
                        )}
                        {card.is_approved && (
                          <button 
                            onClick={() => handleRevoke(card.id)} 
                            className="p-2 hover:bg-red-100 rounded text-red-600"
                            title="Revoke Approval"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        )}
                        <button 
                          onClick={() => openEditModal(card)} 
                          className="p-2 hover:bg-gray-100 rounded text-gray-600"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(card.id)} 
                          className="p-2 hover:bg-red-100 rounded text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-slate-800 text-white p-4 rounded-t-2xl">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-amber-400" />
                {selectedCard ? 'Edit ID Card' : 'Create ID Card'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Staff Member</label>
                <select 
                  className="select-field"
                  value={formData.member_id}
                  onChange={(e) => handleMemberSelect(e.target.value)}
                  required
                >
                  <option value="">-- Select Staff Member --</option>
                  {staffMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.first_name} {m.last_name} {m.position ? `- ${m.position}` : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title / Position *</label>
                <input 
                  type="text" 
                  className="input-field" 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Senior Pastor, Youth Minister, Deacon"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input 
                  type="text" 
                  className="input-field" 
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  placeholder="e.g., Pastoral Staff, Music Ministry"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input 
                    type="tel" 
                    className="input-field" 
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input 
                    type="email" 
                    className="input-field" 
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Photo URL</label>
                <input 
                  type="url" 
                  className="input-field" 
                  value={formData.photo_url}
                  onChange={(e) => setFormData({...formData, photo_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Card Style</label>
                <select 
                  className="select-field"
                  value={formData.card_style}
                  onChange={(e) => setFormData({...formData, card_style: e.target.value})}
                >
                  <option value="classic">Classic (Dark)</option>
                  <option value="modern">Modern (Purple)</option>
                  <option value="elegant">Elegant (Gold)</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">
                  {selectedCard ? 'Update Card' : 'Create Card'}
                </button>
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="btn-secondary flex-1">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Card Preview Modal */}
      {showPreview && previewCard && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="font-bold text-lg">Digital ID Card Preview</h2>
              <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-8 bg-gray-100 flex justify-center">
              {/* The ID Card */}
              <div 
                className={`w-96 rounded-2xl shadow-2xl overflow-hidden ${
                  previewCard.card_style === 'modern' 
                    ? 'bg-gradient-to-br from-purple-700 to-indigo-900' 
                    : previewCard.card_style === 'elegant'
                      ? 'bg-gradient-to-br from-amber-700 to-amber-900'
                      : 'bg-gradient-to-br from-slate-800 to-slate-900'
                }`}
              >
                {/* Card Header */}
                <div className="p-4 border-b border-white/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Church className="w-6 h-6 text-white" />
                      <div>
                        <h3 className="text-white font-bold text-sm">Holy Light Ministry</h3>
                        <p className="text-white/60 text-[10px]">Soul Center Headquarters</p>
                      </div>
                    </div>
                    <img 
                      src={BISHOP_SEAL_URL} 
                      alt="Bishop's Seal" 
                      className="w-14 h-14 object-contain"
                    />
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <div className="flex gap-4">
                    {/* Photo */}
                    <div className="w-24 h-28 bg-white rounded-lg overflow-hidden flex-shrink-0 shadow-lg">
                      {previewCard.photo_url ? (
                        <img src={previewCard.photo_url} alt={previewCard.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <User className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 text-white">
                      <h2 className="text-xl font-bold">{previewCard.name}</h2>
                      <p className={`text-lg font-medium ${
                        previewCard.card_style === 'modern' ? 'text-purple-300' :
                        previewCard.card_style === 'elegant' ? 'text-amber-300' : 'text-amber-400'
                      }`}>{previewCard.title}</p>
                      {previewCard.department && (
                        <p className="text-white/70 text-sm mt-1">{previewCard.department}</p>
                      )}
                      
                      <div className="mt-3 space-y-1 text-sm text-white/80">
                        {previewCard.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="w-3 h-3" /> {previewCard.phone}
                          </p>
                        )}
                        {previewCard.email && (
                          <p className="flex items-center gap-2">
                            <Mail className="w-3 h-3" /> {previewCard.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Footer - Verification */}
                <div className={`px-6 py-3 ${
                  previewCard.is_approved 
                    ? 'bg-emerald-600' 
                    : previewCard.is_revoked 
                      ? 'bg-red-600' 
                      : 'bg-amber-600'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white text-sm">
                      {previewCard.is_approved ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          <span className="font-medium">Bishop Approved</span>
                        </>
                      ) : previewCard.is_revoked ? (
                        <>
                          <X className="w-4 h-4" />
                          <span className="font-medium">Credential Revoked</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-4 h-4" />
                          <span className="font-medium">Pending Approval</span>
                        </>
                      )}
                    </div>
                    <div className="text-white/80 text-xs">
                      ID: {previewCard.verification_code}
                    </div>
                  </div>
                </div>

                {/* QR-like Verification Section */}
                <div className="bg-white p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      <p>Verify at:</p>
                      <p className="font-mono text-gray-700">{window.location.origin}/#/verify/{previewCard.verification_code}</p>
                    </div>
                    <div className="bg-gray-100 p-2 rounded">
                      <BadgeCheck className={`w-8 h-8 ${previewCard.is_approved ? 'text-emerald-600' : 'text-gray-400'}`} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t flex justify-center gap-3">
              <button 
                onClick={() => copyVerificationLink(previewCard)} 
                className="btn-secondary flex items-center gap-2"
              >
                <Copy className="w-4 h-4" /> Copy Verification Link
              </button>
              <button 
                onClick={() => {
                  const link = `${window.location.origin}/#/verify/${previewCard.verification_code}`;
                  if (navigator.share) {
                    navigator.share({ title: `${previewCard.name} - Church ID`, url: link });
                  } else {
                    navigator.clipboard.writeText(link);
                    alert('Link copied!');
                  }
                }} 
                className="btn-primary flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" /> Share Card
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Public Verification Page
const PublicVerifyCard = () => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = window.location.hash.split('/verify/')[1] || window.location.pathname.split('/verify/')[1];
    if (code) {
      verifyCard(code);
    } else {
      setError('No verification code provided');
      setLoading(false);
    }
  }, []);

  const verifyCard = async (code) => {
    try {
      const res = await axios.get(`${API_URL}/api/business-cards/verify/${code}`);
      setCard(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Card not found or verification failed');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying credentials...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">This credential may be invalid, revoked, or does not exist.</p>
        </div>
      </div>
    );
  }

  const BISHOP_SEAL = 'https://customer-assets.emergentagent.com/job_pastoral-events/artifacts/96equzvk_609729639_25560483913609034_3911511700910521101_n%20%281%29.png';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Verification Status Banner */}
        <div className={`rounded-t-2xl p-4 text-center ${
          card.is_approved && !card.is_revoked 
            ? 'bg-emerald-500' 
            : 'bg-red-500'
        }`}>
          <div className="flex items-center justify-center gap-2 text-white text-lg font-bold">
            {card.is_approved && !card.is_revoked ? (
              <>
                <CheckCircle className="w-6 h-6" />
                VERIFIED - Bishop Approved
              </>
            ) : card.is_revoked ? (
              <>
                <X className="w-6 h-6" />
                CREDENTIAL REVOKED
              </>
            ) : (
              <>
                <Clock className="w-6 h-6" />
                PENDING APPROVAL
              </>
            )}
          </div>
        </div>

        {/* Card Display */}
        <div className="bg-white rounded-b-2xl shadow-2xl overflow-hidden">
          {/* Church Header */}
          <div className="bg-gradient-to-r from-purple-700 to-indigo-800 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Church className="w-10 h-10 text-white" />
                <div>
                  <h2 className="text-white font-bold text-lg">Holy Light Ministry</h2>
                  <p className="text-purple-200 text-sm">Soul Center Headquarters</p>
                </div>
              </div>
              <img src={BISHOP_SEAL} alt="Bishop's Seal" className="w-20 h-20 object-contain" />
            </div>
          </div>

          {/* Person Info */}
          <div className="p-6">
            <div className="flex gap-6">
              {/* Photo */}
              <div className="w-28 h-32 bg-gray-200 rounded-xl overflow-hidden flex-shrink-0 shadow-lg border-4 border-white -mt-12">
                {card.photo_url ? (
                  <img src={card.photo_url} alt={card.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <User className="w-12 h-12 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="flex-1 pt-2">
                <h1 className="text-2xl font-bold text-gray-900">{card.name}</h1>
                <p className="text-lg text-purple-600 font-medium">{card.title}</p>
                {card.department && (
                  <p className="text-gray-600">{card.department}</p>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              {card.phone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4 text-purple-600" />
                  <span>{card.phone}</span>
                </div>
              )}
              {card.email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4 text-purple-600" />
                  <span className="truncate">{card.email}</span>
                </div>
              )}
            </div>

            {/* Verification Details */}
            <div className="mt-6 pt-6 border-t">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Verification Code</p>
                  <p className="font-mono font-bold text-gray-900">{card.verification_code}</p>
                </div>
                <div>
                  <p className="text-gray-500">Issued Date</p>
                  <p className="font-medium text-gray-900">{new Date(card.created_at).toLocaleDateString()}</p>
                </div>
                {card.approved_at && (
                  <div>
                    <p className="text-gray-500">Approved Date</p>
                    <p className="font-medium text-gray-900">{new Date(card.approved_at).toLocaleDateString()}</p>
                  </div>
                )}
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className={`font-medium ${
                    card.is_approved && !card.is_revoked 
                      ? 'text-emerald-600' 
                      : card.is_revoked 
                        ? 'text-red-600' 
                        : 'text-amber-600'
                  }`}>
                    {card.is_approved && !card.is_revoked ? 'Active' : card.is_revoked ? 'Revoked' : 'Pending'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 text-center">
            <p className="text-sm text-gray-500">
              This is an official digital credential issued by Holy Light Ministry Soul Center Headquarters
            </p>
            <p className="text-xs text-gray-400 mt-1">
              805 S 1st St., Slaton, TX 79364 | (770) 638-3444
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CHURCH DOCUMENTS SYSTEM ====================
const ChurchDocuments = () => {
  const { canEdit } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({ title: '', category: 'governance', file: null });

  // Official church document categories
  const categories = [
    { id: 'governance', label: 'Governance & Bylaws', icon: Scale },
    { id: 'policies', label: 'Corporate Policies', icon: FileCheck },
    { id: 'certificates', label: 'Certificates & Ordination', icon: BadgeCheck },
    { id: 'minutes', label: 'Meeting Minutes', icon: Clipboard },
    { id: 'doctrines', label: 'Doctrines & Beliefs', icon: Book },
    { id: 'other', label: 'Other Documents', icon: FileText }
  ];

  // Default documents will be created in database on first load
  const defaultDocuments = [
    {
      id: 'articles-of-amendment',
      title: 'Articles of Amendment',
      category: 'governance',
      type: 'text',
      description: 'Official Articles of Amendment filed with the State of Texas',
      editable: true,
      content: `# ARTICLES OF AMENDMENT
## CHRIST THE KING CHURCH AND MINISTRY
### Holy Light Ministry Soul Center Headquarters

**File Number:** 802766534
**State of Texas**

---

## ARTICLE I - NAME
The name of this corporation is **Christ The King Church And Ministry**, doing business as **Holy Light Ministry Soul Center Headquarters**.

## ARTICLE II - REGISTERED AGENT
**Registered Agent:** Derrick A. Raybon
**Address:** 805 S 1st St., Slaton, TX 79364

## ARTICLE III - DIRECTORS
The names and addresses of the current Directors are:

1. **Derrick A. Raybon** - President/Registered Agent
   - 805 S 1st St., Slaton, TX 79364

2. **Bridgett Raybon** - Director
   - 805 S 1st St., Slaton, TX 79364

3. **Irene Mcgaha** - Director
   - 805 S 1st St., Slaton, TX 79364

## ARTICLE IV - PURPOSE
This corporation is organized exclusively for religious, charitable, and educational purposes within the meaning of Section 501(c)(3) of the Internal Revenue Code.

---

*Filed with the Secretary of State of Texas*
*Original Filing Date: July 13, 2017*`
    },
    {
      id: 'finalized-bylaws',
      title: 'Church Bylaws',
      category: 'governance',
      type: 'text',
      description: 'Church bylaws defining organizational structure and governance',
      editable: true,
      content: `# BYLAWS
## HOLY LIGHT MINISTRY SOUL CENTER HEADQUARTERS
### (Christ The King Church And Ministry)

---

## ARTICLE I - NAME AND LOCATION

**Section 1.** The name of this church shall be **Holy Light Ministry Soul Center Headquarters**, operating under the legal entity **Christ The King Church And Ministry**.

**Section 2.** The principal office shall be located at **805 S 1st St., Slaton, TX 79364**.

---

## ARTICLE II - PURPOSE

This church is organized exclusively for religious, charitable, and educational purposes, including:
- Worship of Almighty God
- Preaching and teaching of the Gospel of Jesus Christ
- Administration of ordinances
- Spiritual development of members
- Community outreach and benevolence

---

## ARTICLE III - CHURCH LEADERSHIP

### Section 1. Senior Pastor
**Bishop Dr. Michael D Turner** serves as the Senior Pastor with authority over spiritual matters and church direction.

### Section 2. Founding Pastors
- **Bishop Derrick A. Raybon** - Founding Pastor
- **1st Lady Bridgett Raybon** - Founding Pastor

### Section 3. Board of Directors
The Board of Directors shall consist of:
- Derrick A. Raybon (President)
- Bridgett Raybon (Director)
- Irene Mcgaha (Director)

---

## ARTICLE IV - MEMBERSHIP

**Section 1.** Any person who professes faith in Jesus Christ as Lord and Savior may become a member.

**Section 2.** Members shall:
- Attend services regularly
- Support the church through tithes and offerings
- Live according to Biblical principles

---

## ARTICLE V - MEETINGS

**Section 1.** Regular worship services shall be held weekly.

**Section 2.** Business meetings shall be held as needed with proper notice.

**Section 3.** The Board of Directors shall meet quarterly or as needed.

---

*Last Updated: February 2026*`
    },
    {
      id: 'accountability-board',
      title: 'Accountability Board Guidelines',
      category: 'governance',
      type: 'text',
      description: 'Guidelines for the Accountability Board oversight',
      editable: true,
      content: `# ACCOUNTABILITY BOARD GUIDELINES
## Holy Light Ministry Soul Center Headquarters

---

## PURPOSE

The Accountability Board exists to provide spiritual oversight, financial accountability, and governance support to the church leadership.

---

## BOARD COMPOSITION

### Current Members:
1. **Derrick A. Raybon** - Board President
2. **Bridgett Raybon** - Board Member
3. **Irene Mcgaha** - Board Member

---

## RESPONSIBILITIES

### Financial Oversight
- Review monthly financial statements
- Approve annual budget
- Ensure proper stewardship of church funds
- Oversee benevolence fund distributions

### Spiritual Accountability
- Provide counsel to pastoral staff
- Support church vision and mission
- Pray for church leadership and members

### Governance
- Ensure compliance with bylaws
- Review and update policies as needed
- Handle grievances and appeals

---

## MEETING SCHEDULE

- **Regular Meetings:** Quarterly
- **Special Meetings:** As needed with 48-hour notice
- **Annual Review:** December of each year

---

## DECISION MAKING

- Quorum: Majority of board members
- Voting: Simple majority for most decisions
- Major decisions require 2/3 majority

---

*Adopted by the Board of Directors*
*Holy Light Ministry Soul Center Headquarters*`
    },
    {
      id: 'board-meeting-minutes',
      title: 'Board Meeting Minutes Template',
      category: 'minutes',
      type: 'text',
      description: 'Template for recording board meeting minutes',
      editable: true,
      content: `# BOARD MEETING MINUTES
## Holy Light Ministry Soul Center Headquarters

---

**Date:** [Enter Date]
**Time:** [Enter Time]
**Location:** 805 S 1st St., Slaton, TX 79364

---

## ATTENDANCE

### Present:
- [ ] Derrick A. Raybon - President
- [ ] Bridgett Raybon - Director
- [ ] Irene Mcgaha - Director
- [ ] Bishop Dr. Michael D Turner - Senior Pastor

### Absent:
- [List any absent members]

---

## CALL TO ORDER

Meeting called to order at [TIME] by [NAME].

Opening Prayer by: [NAME]

---

## APPROVAL OF PREVIOUS MINUTES

Motion to approve minutes from [DATE]:
- Motion by: [NAME]
- Seconded by: [NAME]
- Vote: [ ] Approved [ ] Not Approved

---

## FINANCIAL REPORT

**Treasury Balance:** $[AMOUNT]
**Monthly Income:** $[AMOUNT]
**Monthly Expenses:** $[AMOUNT]

---

## OLD BUSINESS

1. [Item 1]
2. [Item 2]

---

## NEW BUSINESS

1. [Item 1]
2. [Item 2]

---

## ANNOUNCEMENTS

- [Announcement 1]
- [Announcement 2]

---

## NEXT MEETING

**Date:** [Enter Date]
**Time:** [Enter Time]

---

## ADJOURNMENT

Motion to adjourn at [TIME]:
- Motion by: [NAME]
- Seconded by: [NAME]

Closing Prayer by: [NAME]

---

**Minutes Recorded By:** ____________________

**Approved By:** ____________________

**Date Approved:** ____________________`
    },
    {
      id: 'corporate-policies',
      title: 'Corporate Policies',
      category: 'policies',
      type: 'text',
      description: 'Organizational policies for church operations',
      editable: true,
      content: `# CORPORATE POLICIES
## Holy Light Ministry Soul Center Headquarters

---

## POLICY 1: EXPENSE REIMBURSEMENT

### Purpose
To establish guidelines for reimbursing church-related expenses.

### Procedure
1. Submit expense report within 30 days
2. Include original receipts
3. Obtain supervisor approval
4. Reimbursement processed within 2 weeks

### Eligible Expenses
- Ministry-related travel
- Office supplies
- Event expenses (pre-approved)
- Training and conferences

---

## POLICY 2: BENEVOLENCE FUND

### Purpose
To assist members and community in times of financial need.

### Guidelines
1. Requests must be submitted in writing
2. Review by Pastor and Board
3. Maximum assistance: $500 per request
4. Limited to one request per 6 months

### Eligible Assistance
- Utility bills
- Rent/mortgage (emergency only)
- Medical expenses
- Food assistance

---

## POLICY 3: CONFLICT OF INTEREST

### Purpose
To ensure decisions are made in the best interest of the church.

### Requirements
1. Disclose any potential conflicts
2. Recuse from voting on related matters
3. Annual disclosure statement required

---

## POLICY 4: FINANCIAL CONTROLS

### Cash Handling
- Two-person count required
- Immediate deposit of offerings
- Detailed records maintained

### Disbursements
- Dual signatures for checks over $500
- Board approval for expenses over $1,000
- Annual financial review

---

## POLICY 5: STAFF CONDUCT

### Expectations
- Professional behavior at all times
- Confidentiality of member information
- Attendance at required meetings
- Support of church mission

---

*Policies approved by the Board of Directors*
*Effective Date: [Date]*`
    },
    {
      id: 'ordination-certificate',
      title: 'Ordination Certificate Template',
      category: 'certificates',
      type: 'text',
      description: 'Template for ministerial ordination certificates',
      editable: true,
      content: `# CERTIFICATE OF ORDINATION
## Holy Light Ministry Soul Center Headquarters

---

╔══════════════════════════════════════════════════════════════╗
║                                                              ║
║                  CERTIFICATE OF ORDINATION                   ║
║                                                              ║
║  This certifies that                                         ║
║                                                              ║
║              ________________________________                ║
║                     [Minister's Name]                        ║
║                                                              ║
║  Having demonstrated a clear call to ministry and having     ║
║  completed the required examination and training, is hereby  ║
║  ordained as a Minister of the Gospel.                       ║
║                                                              ║
║  Ordained by:                                                ║
║  HOLY LIGHT MINISTRY SOUL CENTER HEADQUARTERS                ║
║  Christ The King Church And Ministry                         ║
║                                                              ║
║  Date of Ordination: ____________________                    ║
║                                                              ║
║  ___________________________    ___________________________  ║
║  Bishop Dr. Michael D Turner    Bishop Derrick A. Raybon     ║
║       Senior Pastor                 Founding Pastor          ║
║                                                              ║
║  Certificate Number: ____________________                    ║
║                                                              ║
╚══════════════════════════════════════════════════════════════╝

---

## ORDAINED MINISTERS REGISTRY

| Name | Date Ordained | Certificate # | Status |
|------|---------------|---------------|--------|
| Bishop Derrick A. Raybon | [Date] | 001 | Active |
| [Name] | [Date] | [#] | [Status] |

---

*805 S 1st St., Slaton, TX 79364*
*(770) 638-3444*`
    },
    {
      id: 'written-doctrines',
      title: 'Statement of Faith & Doctrines',
      category: 'doctrines',
      type: 'text',
      description: 'Church statement of faith and core beliefs',
      editable: true,
      content: `# STATEMENT OF FAITH & DOCTRINES
## Holy Light Ministry Soul Center Headquarters

---

## OUR BELIEFS

### 1. THE BIBLE
We believe the Bible is the inspired, infallible Word of God and the final authority in all matters of faith and conduct.

### 2. GOD
We believe in one God, eternally existing in three persons: Father, Son, and Holy Spirit.

### 3. JESUS CHRIST
We believe in the deity of Jesus Christ, His virgin birth, His sinless life, His miracles, His atoning death, His bodily resurrection, His ascension, and His future return.

### 4. HOLY SPIRIT
We believe in the present ministry of the Holy Spirit, by whose indwelling the Christian is enabled to live a godly life.

### 5. SALVATION
We believe salvation is by grace through faith in Jesus Christ alone, not by works.

### 6. THE CHURCH
We believe in the universal church, consisting of all believers in Christ, and the local church as the visible expression of the body of Christ.

### 7. BAPTISM
We believe in water baptism by immersion as an outward testimony of faith in Christ.

### 8. COMMUNION
We believe in the Lord's Supper as a memorial of Christ's sacrifice and a symbol of our unity in Him.

---

## OUR MISSION

To spread the Gospel of Jesus Christ through:
- **Worship** - Honoring God in spirit and truth
- **Discipleship** - Growing believers in faith
- **Fellowship** - Building a loving community
- **Evangelism** - Sharing the Good News
- **Service** - Meeting needs in Jesus' name

---

## OUR VISION

To be a place where:
- Lives are transformed by the power of God
- Families are strengthened
- Communities are impacted
- The name of Jesus is glorified

---

## SCRIPTURE FOUNDATION

*"For God so loved the world that He gave His only begotten Son, that whoever believes in Him should not perish but have everlasting life."*
— John 3:16

---

*Holy Light Ministry Soul Center Headquarters*
*Christ The King Church And Ministry*
*805 S 1st St., Slaton, TX 79364*`
    }
  ];

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await api.get('/api/church-documents');
      const existingDocs = res.data || [];
      
      // If no documents exist, create default ones
      if (existingDocs.length === 0) {
        await initializeDefaultDocuments();
      } else {
        setDocuments(existingDocs);
      }
    } catch (e) {
      // If API fails, try to initialize defaults
      await initializeDefaultDocuments();
    }
    setLoading(false);
  };

  const initializeDefaultDocuments = async () => {
    try {
      for (const doc of defaultDocuments) {
        await api.post('/api/church-documents', {
          id: doc.id,
          title: doc.title,
          category: doc.category,
          description: doc.description,
          content: doc.content,
          type: doc.type,
          editable: doc.editable
        });
      }
      // Fetch the newly created documents
      const res = await api.get('/api/church-documents');
      setDocuments(res.data || defaultDocuments);
    } catch (e) {
      // Fallback to default documents in memory
      setDocuments(defaultDocuments);
    }
  };

  const handleEditDocument = async (doc) => {
    setSelectedDoc(doc);
    setEditTitle(doc.title);
    setEditContent(doc.content || '');
    setShowEditor(true);
  };

  const handleViewDocument = (doc) => {
    setSelectedDoc(doc);
    setEditTitle(doc.title);
    setEditContent(doc.content || '');
    setShowEditor(true);
  };

  const handleSaveDocument = async () => {
    setSaving(true);
    try {
      await api.put(`/api/church-documents/${selectedDoc.id}`, {
        title: editTitle,
        content: editContent,
        category: selectedDoc.category,
        description: selectedDoc.description
      });
      // Update local state
      setDocuments(docs => docs.map(d => 
        d.id === selectedDoc.id 
          ? { ...d, title: editTitle, content: editContent }
          : d
      ));
      setShowEditor(false);
      alert('Document saved successfully!');
    } catch (e) {
      alert('Error saving document: ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      alert('Please select a file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post('/api/church-documents', {
          title: uploadData.title,
          category: uploadData.category,
          file_data: reader.result.split(',')[1],
          file_name: uploadData.file.name,
          file_type: uploadData.file.type
        });
        fetchDocuments();
        setShowUploadModal(false);
        setUploadData({ title: '', category: 'governance', file: null });
        alert('Document uploaded successfully!');
      } catch (err) {
        alert('Error uploading document');
      }
    };
    reader.readAsDataURL(uploadData.file);
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await api.delete(`/api/church-documents/${docId}`);
      fetchDocuments();
    } catch (e) {
      alert('Error deleting document');
    }
  };

  const getCategoryIcon = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.icon : FileText;
  };

  const getCategoryLabel = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.label : 'Other';
  };

  const groupedDocuments = categories.map(cat => ({
    ...cat,
    docs: documents.filter(d => d.category === cat.id)
  })).filter(cat => cat.docs.length > 0);

  return (
    <div className="space-y-6" data-testid="church-documents-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-purple-700 rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FolderOpen className="w-7 h-7" />
              Church Documents
            </h1>
            <p className="text-indigo-200 text-sm mt-1">Official documents, bylaws, policies & certificates</p>
          </div>
          <button 
            onClick={() => setShowUploadModal(true)}
            className="bg-white text-indigo-700 px-4 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-indigo-50 transition-colors"
            data-testid="upload-document-btn"
          >
            <Upload className="w-4 h-4" /> Upload Document
          </button>
        </div>
      </div>

      {/* Document Categories */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading documents...</div>
      ) : (
        <div className="space-y-6">
          {groupedDocuments.map(category => {
            const CategoryIcon = category.icon;
            return (
              <div key={category.id} className="card">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                  <CategoryIcon className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-semibold">{category.label}</h2>
                  <span className="text-sm text-gray-500">({category.docs.length} documents)</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.docs.map(doc => (
                    <div 
                      key={doc.id} 
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${doc.type === 'pdf' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                          <FileText className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{doc.title}</h3>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{doc.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${doc.type === 'pdf' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                              {doc.type?.toUpperCase() || 'DOC'}
                            </span>
                            {doc.editable && (
                              <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                                Editable
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                        {doc.url && (
                          <a 
                            href={doc.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex-1 btn-secondary text-xs py-1.5 text-center flex items-center justify-center gap-1"
                          >
                            <Eye className="w-3 h-3" /> View Original
                          </a>
                        )}
                        {doc.editable && (
                          <button 
                            onClick={() => handleEditDocument(doc)}
                            className="flex-1 btn-primary text-xs py-1.5 flex items-center justify-center gap-1"
                          >
                            <Edit className="w-3 h-3" /> Edit
                          </button>
                        )}
                        {!defaultDocuments.find(o => o.id === doc.id) && (
                          <button 
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Document Editor Modal */}
      {showEditor && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            <div className="sticky top-0 bg-indigo-700 text-white p-4 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Edit className="w-5 h-5" /> Edit Document
                </h2>
                <p className="text-indigo-200 text-sm">{selectedDoc.title}</p>
              </div>
              <button onClick={() => setShowEditor(false)} className="p-2 hover:bg-indigo-600 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
                <input 
                  type="text" 
                  className="input-field" 
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                />
              </div>

              <div className="flex-1 flex flex-col min-h-0">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document Content 
                  <span className="text-gray-500 font-normal">(Supports Markdown formatting)</span>
                </label>
                <textarea 
                  className="flex-1 input-field font-mono text-sm resize-none"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="Enter document content here... Supports Markdown formatting."
                  style={{ minHeight: '400px' }}
                />
              </div>

              {selectedDoc.url && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    <span>
                      <strong>Note:</strong> This document has an original file. Your edits will be saved as a separate editable version.{' '}
                      <a href={selectedDoc.url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                        View original file →
                      </a>
                    </span>
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-3">
              <button 
                onClick={() => setShowEditor(false)} 
                className="btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveDocument}
                disabled={saving}
                className="btn-primary flex items-center gap-2"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Save Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-indigo-700 text-white p-4 rounded-t-2xl">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Upload className="w-5 h-5" /> Upload Document
              </h2>
            </div>
            <form onSubmit={handleUpload} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Title *</label>
                <input 
                  type="text" 
                  className="input-field" 
                  value={uploadData.title}
                  onChange={(e) => setUploadData({...uploadData, title: e.target.value})}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select 
                  className="select-field"
                  value={uploadData.category}
                  onChange={(e) => setUploadData({...uploadData, category: e.target.value})}
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File (PDF, DOC, DOCX)</label>
                <input 
                  type="file" 
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">Upload</button>
                <button type="button" onClick={() => setShowUploadModal(false)} className="btn-secondary flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// HR Documents Component
const HRDocuments = () => {
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [activeTab, setActiveTab] = useState('templates');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [orgSettings, setOrgSettings] = useState({});
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedDocForEmail, setSelectedDocForEmail] = useState(null);
  const [emailForm, setEmailForm] = useState({ email: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    organization_name: '', organization_type: 'church', ein: '', address: '', city: '', state: '', zip_code: '', phone: '', email: '', website: '', pastor_name: '', hr_contact_name: '', hr_contact_email: ''
  });
  const [generating, setGenerating] = useState(null);
  const [handbookInfo, setHandbookInfo] = useState(null);

  useEffect(() => { fetchTemplates(); fetchDocuments(); fetchStaffMembers(); fetchOrgSettings(); fetchHandbookInfo(); }, []);

  const fetchTemplates = async () => { try { const r = await api.get('/api/documents/templates'); setTemplates(r.data.templates); } catch (e) { console.error(e); } };
  const fetchDocuments = async () => { try { const r = await api.get('/api/documents'); setDocuments(r.data.documents); } catch (e) { console.error(e); } };
  const fetchStaffMembers = async () => { 
    try { 
      const [membersRes, employeesRes] = await Promise.all([
        api.get('/api/members'),
        api.get('/api/employees')
      ]);
      const staffFromMembers = (membersRes.data.members || []).filter(m => m.is_staff === true);
      const employees = employeesRes.data.employees || [];
      const combined = [...staffFromMembers];
      employees.forEach(emp => {
        const exists = combined.some(s => s.email && emp.email && s.email === emp.email);
        if (!exists) combined.push(emp);
      });
      setStaffMembers(combined); 
    } catch (e) { console.error(e); } 
  };
  const fetchOrgSettings = async () => { try { const r = await api.get('/api/organization/settings'); if (r.data.settings) { setOrgSettings(r.data.settings); setSettingsForm(r.data.settings); } } catch (e) { console.error(e); } };
  const fetchHandbookInfo = async () => { try { const r = await api.get('/api/handbook/info'); setHandbookInfo(r.data); } catch (e) { console.error(e); } };
  
  const openEmailModal = (doc) => {
    const employee = staffMembers.find(s => s.id === doc.employee_id);
    setSelectedDocForEmail(doc);
    setEmailForm({
      email: employee?.email || '',
      recipient_name: employee ? `${employee.first_name} ${employee.last_name}` : '',
      message: ''
    });
    setShowEmailModal(true);
  };

  const sendDocumentEmail = async (e) => {
    e.preventDefault();
    if (!selectedDocForEmail) return;
    setSendingEmail(true);
    try {
      const response = await api.post(`/api/documents/${selectedDocForEmail.id}/send-email`, emailForm);
      alert(response.data.message);
      setShowEmailModal(false);
      fetchDocuments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error sending email');
    } finally {
      setSendingEmail(false);
    }
  };

  const sendBulkDocuments = async () => {
    if (!selectedEmployee) { alert('Please select an employee first'); return; }
    const employeeDocs = documents.filter(d => d.employee_id === selectedEmployee);
    if (employeeDocs.length === 0) { alert('No documents found for this employee'); return; }
    
    const employee = staffMembers.find(s => s.id === selectedEmployee);
    if (!employee?.email) { alert('Employee does not have an email address'); return; }
    
    if (!window.confirm(`Send ${employeeDocs.length} documents to ${employee.email}?`)) return;
    
    try {
      const response = await api.post('/api/documents/send-bulk-email', {
        employee_id: selectedEmployee,
        document_ids: employeeDocs.map(d => d.id),
        message: 'Please review and complete the attached HR documents.'
      });
      alert(response.data.message);
      fetchDocuments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error sending documents');
    }
  };

  const downloadHandbook = async () => {
    try {
      const response = await api.get('/api/handbook/download', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Holy_Light_Ministry_Employee_Handbook.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('Error downloading handbook');
    }
  };

  const generateDocument = async (templateId) => {
    if (!selectedEmployee) { alert('Please select an employee first'); return; }
    setGenerating(templateId);
    try {
      const response = await api.post(`/api/documents/generate/${templateId}?employee_id=${selectedEmployee}`, {}, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateId}_document.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      fetchDocuments();
    } catch (e) { console.error(e); alert('Error generating document'); }
    setGenerating(null);
  };

  const generatePacket = async () => {
    if (!selectedEmployee) { alert('Please select an employee'); return; }
    try {
      const r = await api.post(`/api/documents/generate-packet/${selectedEmployee}`);
      alert(`Generated ${r.data.total_documents} documents for new hire packet!`);
      fetchDocuments();
    } catch (e) { console.error(e); alert('Error generating packet'); }
  };

  const regeneratePacket = async () => {
    if (!selectedEmployee) { alert('Please select an employee'); return; }
    if (!window.confirm('This will delete all incomplete documents and create new ones. Completed documents will be kept. Continue?')) return;
    try {
      const r = await api.post(`/api/documents/generate-packet/${selectedEmployee}?regenerate=true`);
      alert(`Regenerated ${r.data.total_documents} documents for hiring packet!`);
      fetchDocuments();
    } catch (e) { console.error(e); alert('Error regenerating packet'); }
  };

  const deleteDocument = async (id) => {
    if (window.confirm('Delete this document?')) {
      try { await api.delete(`/api/documents/${id}`); fetchDocuments(); } catch (e) { alert('Error deleting'); }
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try { await api.post('/api/organization/settings', settingsForm); setShowSettingsModal(false); fetchOrgSettings(); alert('Settings saved!'); } catch (e) { alert('Error saving'); }
  };

  const categoryColors = { hiring: 'bg-blue-100 text-blue-800', tax: 'bg-green-100 text-green-800', compliance: 'bg-yellow-100 text-yellow-800', payroll: 'bg-purple-100 text-purple-800', legal: 'bg-red-100 text-red-800', onboarding: 'bg-indigo-100 text-indigo-800', personal: 'bg-pink-100 text-pink-800', organization: 'bg-gray-100 text-gray-800' };

  return (
    <div className="space-y-6" data-testid="hr-documents-page">
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <button onClick={() => setActiveTab('templates')} className={`px-4 py-2 rounded-lg ${activeTab === 'templates' ? 'bg-purple-600 text-white' : 'bg-gray-100'}`}><FileText className="w-4 h-4 inline mr-2" />Templates</button>
          <button onClick={() => setActiveTab('generated')} className={`px-4 py-2 rounded-lg ${activeTab === 'generated' ? 'bg-purple-600 text-white' : 'bg-gray-100'}`}><FileCheck className="w-4 h-4 inline mr-2" />Generated ({documents.length})</button>
          <button onClick={() => setActiveTab('settings')} className={`px-4 py-2 rounded-lg ${activeTab === 'settings' ? 'bg-purple-600 text-white' : 'bg-gray-100'}`}><Building2 className="w-4 h-4 inline mr-2" />Organization</button>
        </div>
      </div>

      {activeTab === 'templates' && (
        <div className="space-y-6">
          {/* Handbook Download Card */}
          {handbookInfo?.available && (
            <div className="card bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                    <Book className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">{handbookInfo.title}</h3>
                    <p className="text-indigo-100 text-sm">"{handbookInfo.motto}" • {handbookInfo.founding_scripture}</p>
                    <p className="text-indigo-200 text-xs mt-1">Senior Pastor: {handbookInfo.senior_pastor}</p>
                    <p className="text-indigo-200 text-xs">Founding Pastors: {handbookInfo.founding_pastors}</p>
                  </div>
                </div>
                <button onClick={downloadHandbook} className="bg-white text-purple-700 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 flex items-center gap-2" data-testid="download-handbook">
                  <Download className="w-5 h-5" /> Download Handbook
                </button>
              </div>
            </div>
          )}

          <div className="card bg-gradient-to-r from-purple-600 to-purple-800 text-white">
            <h3 className="text-xl font-semibold mb-4">Generate Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-2">Select Staff Member</label>
                <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} className="w-full rounded-lg border-0 p-3 text-gray-900" data-testid="select-staff-for-document">
                  <option value="">Choose a staff member...</option>
                  {staffMembers.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name} - {s.position || 'No Position'}</option>)}
                </select>
                {staffMembers.length === 0 && <p className="text-sm text-purple-200 mt-2">No staff members found. Add staff in the People section first.</p>}
              </div>
              <div className="flex items-end">
                <button onClick={generatePacket} disabled={!selectedEmployee} className="bg-white text-purple-700 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 disabled:opacity-50 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" /> Generate Full New Hire Packet
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => (
              <div key={t.id} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${t.is_sensitive ? 'bg-red-100' : 'bg-blue-100'}`}>
                      {t.is_sensitive ? <AlertCircle className="w-5 h-5 text-red-600" /> : <FileText className="w-5 h-5 text-blue-600" />}
                    </div>
                    <div>
                      <h4 className="font-semibold">{t.name}</h4>
                      <span className={`badge text-xs ${categoryColors[t.category]}`}>{t.category}</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">{t.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {t.requires_signature && <span className="text-xs text-gray-500"><Edit className="w-3 h-3 inline" /> Requires signature</span>}
                    {t.is_sensitive && <span className="text-xs text-red-500">🔒 Encrypted</span>}
                  </div>
                  <button onClick={() => generateDocument(t.id)} disabled={!selectedEmployee || generating === t.id} className="btn-primary text-sm py-1 px-3 flex items-center gap-1">
                    {generating === t.id ? <><div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" /> Generating...</> : <>Generate</>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'generated' && (
        <div className="space-y-4">
          {/* Regenerate Hiring Package Card */}
          {selectedEmployee && (
            <div className="card bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <FileSpreadsheet className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Hiring Package</h3>
                    <p className="text-purple-100 text-sm">Generate or regenerate all hiring documents for this employee</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={generatePacket}
                    className="bg-white text-purple-700 px-4 py-2 rounded-lg font-medium hover:bg-purple-50 flex items-center gap-2"
                    data-testid="generate-hiring-packet"
                  >
                    <Plus className="w-4 h-4" /> Generate New
                  </button>
                  <button 
                    onClick={regeneratePacket}
                    className="bg-white/20 text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 flex items-center gap-2"
                    data-testid="regenerate-hiring-packet"
                  >
                    <RefreshCw className="w-4 h-4" /> Regenerate All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Hiring Completion Receipt Section */}
          {selectedEmployee && documents.filter(d => d.employee_id === selectedEmployee).length > 0 && (
            <div className="card bg-gradient-to-r from-green-600 to-emerald-700 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <FileCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Hiring Completion Receipt</h3>
                    <p className="text-green-100 text-sm">Generate a PDF receipt with all completed hiring documents</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={async () => {
                      try {
                        const response = await api.get(`/api/documents/completion-receipt/${selectedEmployee}`, { responseType: 'blob' });
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        const employee = staffMembers.find(s => s.id === selectedEmployee);
                        link.download = `hiring_receipt_${employee?.first_name || 'employee'}_${new Date().toISOString().split('T')[0]}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      } catch (e) {
                        console.error(e);
                        alert(e.response?.data?.detail || 'Error generating receipt');
                      }
                    }}
                    className="bg-white text-green-700 px-4 py-2 rounded-lg font-medium hover:bg-green-50 flex items-center gap-2"
                    data-testid="download-completion-receipt"
                  >
                    <Download className="w-4 h-4" /> Download Receipt
                  </button>
                  <button 
                    onClick={async () => {
                      const employee = staffMembers.find(s => s.id === selectedEmployee);
                      if (!employee?.email) {
                        alert('Employee does not have an email address');
                        return;
                      }
                      if (!window.confirm(`Send hiring completion receipt to ${employee.email}?`)) return;
                      try {
                        const response = await api.post(`/api/documents/send-completion-receipt/${selectedEmployee}`, { email: employee.email });
                        alert(response.data.message);
                      } catch (e) {
                        console.error(e);
                        alert(e.response?.data?.detail || 'Error sending receipt');
                      }
                    }}
                    className="bg-white/20 text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 flex items-center gap-2"
                    data-testid="email-completion-receipt"
                  >
                    <Mail className="w-4 h-4" /> Email Receipt
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Generated Documents</h3>
              {documents.length > 0 && (
                <button 
                  onClick={async () => {
                    const deleteScope = selectedEmployee 
                      ? `all documents for the selected employee (${documents.filter(d => d.employee_id === selectedEmployee).length} documents)` 
                      : `ALL ${documents.length} documents`;
                    if (!window.confirm(`Are you sure you want to delete ${deleteScope}? This action cannot be undone.`)) return;
                    try {
                      const url = selectedEmployee 
                        ? `/api/documents/bulk/delete-all?employee_id=${selectedEmployee}` 
                        : '/api/documents/bulk/delete-all';
                      const response = await api.delete(url);
                      alert(response.data.message);
                      fetchDocuments();
                    } catch (e) {
                      console.error(e);
                      alert(e.response?.data?.detail || 'Error deleting documents');
                    }
                  }}
                  className="btn-danger flex items-center gap-2"
                  data-testid="delete-all-documents"
                >
                  <Trash2 className="w-4 h-4" /> Delete {selectedEmployee ? 'Employee Docs' : 'All'}
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50"><tr>
                  <th className="table-header">Document</th>
                  <th className="table-header">Staff Member</th>
                  <th className="table-header">Category</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Created</th>
                  <th className="table-header">Actions</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {documents.map((d) => (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          {d.is_sensitive && <span title="Encrypted">🔒</span>}
                          <span className="font-medium">{d.template_name}</span>
                        </div>
                      </td>
                      <td className="table-cell">{d.employee_name || 'N/A'}</td>
                      <td className="table-cell"><span className={`badge text-xs ${categoryColors[d.category]}`}>{d.category}</span></td>
                      <td className="table-cell"><span className={`badge ${d.status === 'completed' ? 'badge-success' : d.status === 'generated' ? 'badge-warning' : 'badge-secondary'}`}>{d.status === 'completed' ? '✓ Completed' : d.status}</span></td>
                      <td className="table-cell text-gray-500">{new Date(d.created_at).toLocaleDateString()}</td>
                      <td className="table-cell">
                        <div className="flex gap-1">
                          {d.status === 'completed' ? (
                            <>
                              <button onClick={() => window.open(`${API_URL}/api/documents/${d.id}/view`, '_blank')} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200 flex items-center gap-1" title="View completed document">
                                <Eye className="w-3 h-3" /> View
                              </button>
                              <button onClick={() => window.open(`${API_URL}/api/documents/${d.id}/download`, '_blank')} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200 flex items-center gap-1" title="Download">
                                <Download className="w-3 h-3" /> Save
                              </button>
                            </>
                          ) : (
                            <button onClick={() => window.open(`${API_URL}/api/documents/generate/${d.template_id}?employee_id=${d.employee_id}`, '_blank')} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200 flex items-center gap-1" title="Download blank form">
                              <Download className="w-3 h-3" /> Download
                            </button>
                          )}
                          <button onClick={() => openEmailModal(d)} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium hover:bg-purple-200 flex items-center gap-1" title="Send via Email">
                            <Mail className="w-3 h-3" /> Email
                          </button>
                          <button onClick={() => deleteDocument(d.id)} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200" title="Delete">
                            <Trash2 className="w-3 h-3" />
                          </button>
                          {d.email_sent && <span className="text-green-500 text-xs" title={`Sent to ${d.email_sent_to}`}>✓</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {documents.length === 0 && <p className="text-center py-8 text-gray-500">No documents generated yet</p>}
            </div>
            
            {/* Bulk Email Button */}
            {selectedEmployee && documents.filter(d => d.employee_id === selectedEmployee).length > 0 && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-purple-900">Send All Documents via Email</h4>
                    <p className="text-sm text-purple-600">
                      {documents.filter(d => d.employee_id === selectedEmployee).length} documents for selected employee
                    </p>
                  </div>
                  <button onClick={sendBulkDocuments} className="btn-primary flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Send All via Email
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Document Modal */}
      <Modal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} title="Send Document via Email">
        <form onSubmit={sendDocumentEmail} className="space-y-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600">Document: <strong>{selectedDocForEmail?.template_name}</strong></p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email *</label>
            <input 
              type="email" 
              className="input-field" 
              value={emailForm.email} 
              onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} 
              placeholder="employee@email.com"
              required 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Message (Optional)</label>
            <textarea 
              className="input-field" 
              rows="3" 
              value={emailForm.message} 
              onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} 
              placeholder="Add a personalized message..."
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowEmailModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={sendingEmail} className="btn-primary flex items-center gap-2">
              {sendingEmail ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {sendingEmail ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </form>
      </Modal>

      {activeTab === 'settings' && (
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Organization Settings</h3>
          <p className="text-gray-600 mb-4">Configure your organization details. These will appear on all generated documents.</p>
          <form onSubmit={saveSettings} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label><input type="text" className="input-field" value={settingsForm.organization_name} onChange={(e) => setSettingsForm({...settingsForm, organization_name: e.target.value})} required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select className="select-field" value={settingsForm.organization_type} onChange={(e) => setSettingsForm({...settingsForm, organization_type: e.target.value})}>
                  <option value="church">Church</option><option value="nonprofit">Non-Profit</option><option value="ministry">Ministry</option>
                </select>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">EIN (Encrypted) 🔒</label><input type="text" className="input-field" placeholder="XX-XXXXXXX" value={settingsForm.ein || ''} onChange={(e) => setSettingsForm({...settingsForm, ein: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label><input type="text" className="input-field" value={settingsForm.phone || ''} onChange={(e) => setSettingsForm({...settingsForm, phone: e.target.value})} /></div>
              <div className="md:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Address</label><input type="text" className="input-field" value={settingsForm.address || ''} onChange={(e) => setSettingsForm({...settingsForm, address: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">City</label><input type="text" className="input-field" value={settingsForm.city || ''} onChange={(e) => setSettingsForm({...settingsForm, city: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">State</label><input type="text" className="input-field" value={settingsForm.state || ''} onChange={(e) => setSettingsForm({...settingsForm, state: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label><input type="text" className="input-field" value={settingsForm.zip_code || ''} onChange={(e) => setSettingsForm({...settingsForm, zip_code: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" className="input-field" value={settingsForm.email || ''} onChange={(e) => setSettingsForm({...settingsForm, email: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Website</label><input type="text" className="input-field" value={settingsForm.website || ''} onChange={(e) => setSettingsForm({...settingsForm, website: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Pastor/Director Name</label><input type="text" className="input-field" value={settingsForm.pastor_name || ''} onChange={(e) => setSettingsForm({...settingsForm, pastor_name: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">HR Contact Name</label><input type="text" className="input-field" value={settingsForm.hr_contact_name || ''} onChange={(e) => setSettingsForm({...settingsForm, hr_contact_name: e.target.value})} /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">HR Contact Email</label><input type="email" className="input-field" value={settingsForm.hr_contact_email || ''} onChange={(e) => setSettingsForm({...settingsForm, hr_contact_email: e.target.value})} /></div>
            </div>
            <div className="flex justify-end pt-4"><button type="submit" className="btn-primary">Save Organization Settings</button></div>
          </form>
        </div>
      )}
    </div>
  );
};

// Reports Component
const Reports = () => {
  const [reportType, setReportType] = useState('payroll');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [reportData, setReportData] = useState(null);

  const generateReport = async () => {
    try {
      let r;
      if (reportType === 'payroll') r = await api.get(`/api/reports/payroll-summary?year=${year}&month=${month}`);
      else if (reportType === 'donation') r = await api.get(`/api/reports/donation-summary?year=${year}&month=${month}`);
      setReportData(r.data);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Report</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label><select className="select-field" value={reportType} onChange={(e) => setReportType(e.target.value)}><option value="payroll">Payroll Summary</option><option value="donation">Donation Summary</option></select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Year</label><select className="select-field" value={year} onChange={(e) => setYear(parseInt(e.target.value))}>{[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Month</label><select className="select-field" value={month} onChange={(e) => setMonth(parseInt(e.target.value))}>{Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}</option>)}</select></div>
          <div className="flex items-end"><button onClick={generateReport} className="btn-primary w-full">Generate</button></div>
        </div>
      </div>
      {reportData && <div className="card"><h3 className="text-lg font-semibold mb-4">Results</h3><pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">{JSON.stringify(reportData, null, 2)}</pre></div>}
    </div>
  );
};

// Login Component
const Login = ({ onLogin }) => {
  const [mode, setMode] = useState('login'); // login, register, forgot-email, forgot-step1, forgot-step2, reset-token
  const [formData, setFormData] = useState({ username: '', password: '', email: '', security_question: '', security_answer: '', new_password: '', confirm_password: '' });
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');

  // Check for reset token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      setMode('reset-token');
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const securityQuestions = [
    "What is your mother's maiden name?",
    "What was the name of your first pet?",
    "What city were you born in?",
    "What is your favorite Bible verse?",
    "What was the name of your elementary school?",
    "What is your favorite hymn?",
    "What year did you join the church?"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setLoading(true);
    try {
      if (mode === 'register') {
        if (!formData.security_question || !formData.security_answer) {
          setError('Please select a security question and provide an answer');
          setLoading(false);
          return;
        }
        await api.post('/api/auth/register', {
          username: formData.username,
          password: formData.password,
          email: formData.email,
          security_question: formData.security_question,
          security_answer: formData.security_answer
        });
        setSuccess('Account created! You can now login.');
        setMode('login');
        setFormData({ username: '', password: '', email: '', security_question: '', security_answer: '', new_password: '', confirm_password: '' });
      } else if (mode === 'login') {
        // Try regular login first, then owner login
        try {
          const r = await api.post('/api/auth/login', { username: formData.username, password: formData.password });
          localStorage.setItem('token', r.data.access_token);
          onLogin();
        } catch (loginErr) {
          // If regular login fails, try owner login
          try {
            const r = await api.post('/api/auth/owner-login', { username: formData.username, password: formData.password });
            localStorage.setItem('token', r.data.access_token);
            onLogin();
          } catch (ownerErr) {
            throw loginErr; // Throw original error
          }
        }
        return;
      } else if (mode === 'forgot-email') {
        // Email-based password recovery
        await api.post('/api/auth/forgot-password/email', { email: formData.email });
        setSuccess('If an account exists with this email, a reset link has been sent. Please check your inbox.');
        setFormData({ ...formData, email: '' });
      } else if (mode === 'reset-token') {
        // Reset password with token from email
        if (formData.new_password !== formData.confirm_password) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.new_password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        await api.post('/api/auth/forgot-password/reset-with-token', {
          token: resetToken,
          new_password: formData.new_password
        });
        setSuccess('Password reset successfully! You can now login.');
        setMode('login');
        setResetToken('');
        setFormData({ ...formData, new_password: '', confirm_password: '' });
      } else if (mode === 'forgot-step1') {
        const r = await api.post('/api/auth/forgot-password/verify', { username: formData.username });
        setSecurityQuestion(r.data.security_question);
        setMode('forgot-step2');
      } else if (mode === 'forgot-step2') {
        if (formData.new_password !== formData.confirm_password) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.new_password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
        await api.post('/api/auth/forgot-password/reset', {
          username: formData.username,
          security_answer: formData.security_answer,
          new_password: formData.new_password
        });
        setSuccess('Password reset successfully! You can now login.');
        setMode('login');
        setFormData({ ...formData, password: '', security_answer: '', new_password: '', confirm_password: '' });
      }
    } catch (err) { setError(err.response?.data?.detail || 'An error occurred'); } finally { setLoading(false); }
  };

  const resetForm = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccess('');
    setSecurityQuestion('');
    setResetToken('');
    if (newMode === 'login') {
      setFormData({ username: '', password: '', email: '', security_question: '', security_answer: '', new_password: '', confirm_password: '' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8" data-testid="login-page">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Logo" className="w-24 h-24 mx-auto mb-4 rounded-xl object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
          <div className="w-16 h-16 bg-purple-100 rounded-xl items-center justify-center mx-auto mb-4 hidden"><Church className="w-8 h-8 text-purple-600" /></div>
          <h1 className="text-xl font-bold text-gray-900">Holy Light Ministry</h1>
          <p className="text-gray-500">Soul Center Headquarters</p>
        </div>

        {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
        {success && <div className="bg-green-50 text-green-600 p-3 rounded-lg mb-4 text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4" />{success}</div>}

        {/* Login Form */}
        {mode === 'login' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Username</label><input type="text" className="input-field" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required data-testid="login-username" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Password</label><input type="password" className="input-field" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required data-testid="login-password" /></div>
            <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="login-submit">{loading ? 'Please wait...' : 'Login'}</button>
            <div className="text-center space-y-2">
              <button type="button" onClick={() => resetForm('forgot-email')} className="text-purple-600 hover:text-purple-800 text-sm font-medium" data-testid="forgot-password-link">Forgot Password?</button>
            </div>
          </form>
        )}

        {/* Forgot Password - Email Recovery */}
        {mode === 'forgot-email' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center mb-4">
              <Mail className="w-12 h-12 text-purple-600 mx-auto mb-2" />
              <h2 className="text-lg font-semibold text-gray-900">Reset Your Password</h2>
              <p className="text-sm text-gray-500">Enter your email to receive a reset link</p>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label><input type="email" className="input-field" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="forgot-email" placeholder="Enter your email" /></div>
            <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? 'Sending...' : 'Send Reset Link'}</button>
            <div className="text-center space-y-2">
              <button type="button" onClick={() => resetForm('forgot-step1')} className="text-gray-500 hover:text-gray-700 text-sm block mx-auto">Use security question instead</button>
              <button type="button" onClick={() => resetForm('login')} className="text-gray-600 hover:text-gray-800 text-sm">← Back to Login</button>
            </div>
          </form>
        )}

        {/* Reset Password with Token from Email */}
        {mode === 'reset-token' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center mb-4">
              <Key className="w-12 h-12 text-purple-600 mx-auto mb-2" />
              <h2 className="text-lg font-semibold text-gray-900">Create New Password</h2>
              <p className="text-sm text-gray-500">Enter your new password below</p>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">New Password</label><input type="password" className="input-field" value={formData.new_password} onChange={(e) => setFormData({ ...formData, new_password: e.target.value })} required minLength={6} data-testid="reset-new-password" placeholder="At least 6 characters" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label><input type="password" className="input-field" value={formData.confirm_password} onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })} required data-testid="reset-confirm-password" placeholder="Confirm your password" /></div>
            <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? 'Resetting...' : 'Reset Password'}</button>
            <div className="text-center">
              <button type="button" onClick={() => resetForm('login')} className="text-gray-600 hover:text-gray-800 text-sm">← Back to Login</button>
            </div>
          </form>
        )}

        {/* Register Form */}
        {mode === 'register' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Username</label><input type="text" className="input-field" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" className="input-field" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Password</label><input type="password" className="input-field" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={6} /></div>
            <div className="border-t pt-4">
              <p className="text-sm text-gray-500 mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Set up password recovery</p>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Security Question *</label>
                <select className="select-field" value={formData.security_question} onChange={(e) => setFormData({ ...formData, security_question: e.target.value })} required>
                  <option value="">Select a question...</option>
                  {securityQuestions.map((q, i) => <option key={i} value={q}>{q}</option>)}
                </select>
              </div>
              <div className="mt-3"><label className="block text-sm font-medium text-gray-700 mb-1">Your Answer *</label><input type="text" className="input-field" value={formData.security_answer} onChange={(e) => setFormData({ ...formData, security_answer: e.target.value })} required placeholder="Enter your answer" /></div>
            </div>
            <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? 'Please wait...' : 'Register'}</button>
          </form>
        )}

        {/* Forgot Password Step 1 - Enter Username */}
        {mode === 'forgot-step1' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Reset Your Password</h2>
              <p className="text-sm text-gray-500">Enter your username to continue</p>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Username</label><input type="text" className="input-field" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required data-testid="forgot-username" /></div>
            <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? 'Please wait...' : 'Continue'}</button>
            <div className="text-center">
              <button type="button" onClick={() => resetForm('login')} className="text-gray-600 hover:text-gray-800 text-sm">← Back to Login</button>
            </div>
          </form>
        )}

        {/* Forgot Password Step 2 - Answer Security Question */}
        {mode === 'forgot-step2' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="text-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Security Question</h2>
              <p className="text-sm text-gray-500">Answer your security question to reset password</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-purple-700">{securityQuestion}</p>
            </div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Your Answer</label><input type="text" className="input-field" value={formData.security_answer} onChange={(e) => setFormData({ ...formData, security_answer: e.target.value })} required data-testid="security-answer" placeholder="Enter your answer" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">New Password</label><input type="password" className="input-field" value={formData.new_password} onChange={(e) => setFormData({ ...formData, new_password: e.target.value })} required minLength={6} data-testid="new-password" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label><input type="password" className="input-field" value={formData.confirm_password} onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })} required data-testid="confirm-password" /></div>
            <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? 'Please wait...' : 'Reset Password'}</button>
            <div className="text-center">
              <button type="button" onClick={() => resetForm('login')} className="text-gray-600 hover:text-gray-800 text-sm">← Back to Login</button>
            </div>
          </form>
        )}

        {/* Toggle between Login and Register */}
        {(mode === 'login' || mode === 'register') && (
          <div className="mt-6 text-center">
            <button onClick={() => resetForm(mode === 'register' ? 'login' : 'register')} className="text-purple-600 hover:text-purple-800 text-sm font-medium">
              {mode === 'register' ? 'Already have an account? Login' : "Don't have an account? Register"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Tools Component
const AdminTools = () => {
  const [activeSection, setActiveSection] = useState('roles');
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('role');
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedCategoryType, setSelectedCategoryType] = useState('all');
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [securityForm, setSecurityForm] = useState({ security_question: '', security_answer: '' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm_password: '' });

  const securityQuestions = [
    "What is your mother's maiden name?",
    "What was the name of your first pet?",
    "What city were you born in?",
    "What is your favorite Bible verse?",
    "What was the name of your elementary school?",
    "What is your favorite hymn?",
    "What year did you join the church?"
  ];

  const categoryTypes = [
    { id: 'all', name: 'All Categories' },
    { id: 'material', name: 'Material Types' },
    { id: 'loan', name: 'Loan Types' },
    { id: 'task', name: 'Task Types' },
    { id: 'fund', name: 'Donation Funds' },
    { id: 'member_class', name: 'Member Classifications' },
    { id: 'department', name: 'Departments' }
  ];

  useEffect(() => { 
    fetchRoles(); 
    fetchCategories(); 
    fetchModules(); 
    fetchUsers(); 
    fetchPermissions(); 
  }, []);

  const fetchRoles = async () => {
    try { const r = await api.get('/api/admin/roles'); setRoles(r.data.roles); } catch (e) { console.error(e); }
  };

  const fetchCategories = async () => {
    try { const r = await api.get('/api/admin/categories'); setCategories(r.data.categories); } catch (e) { console.error(e); }
  };

  const fetchModules = async () => {
    try { const r = await api.get('/api/admin/modules'); setModules(r.data.modules); } catch (e) { console.error(e); }
  };

  const fetchUsers = async () => {
    try { const r = await api.get('/api/admin/users'); setUsers(r.data.users); } catch (e) { console.error(e); }
  };

  const fetchPermissions = async () => {
    try { const r = await api.get('/api/admin/permissions'); setPermissions(r.data.permissions); } catch (e) { console.error(e); }
  };

  const openSecurityModal = (user) => {
    setSelectedUser(user);
    setSecurityForm({ security_question: user.security_question || '', security_answer: '' });
    setShowSecurityModal(true);
  };

  const handleSecuritySubmit = async (e) => {
    e.preventDefault();
    if (!securityForm.security_question || !securityForm.security_answer) {
      alert('Please fill in both fields');
      return;
    }
    try {
      await api.put(`/api/admin/users/${selectedUser.id || selectedUser.username}/security?security_question=${encodeURIComponent(securityForm.security_question)}&security_answer=${encodeURIComponent(securityForm.security_answer)}`);
      alert('Security question updated successfully');
      setShowSecurityModal(false);
      fetchUsers();
    } catch (e) { alert(e.response?.data?.detail || 'Error updating security question'); }
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setPasswordForm({ new_password: '', confirm_password: '' });
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      alert('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    try {
      await api.put(`/api/admin/users/${selectedUser.id || selectedUser.username}/reset-password?new_password=${encodeURIComponent(passwordForm.new_password)}`);
      alert('Password reset successfully');
      setShowPasswordModal(false);
    } catch (e) { alert(e.response?.data?.detail || 'Error resetting password'); }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditItem(item);
    if (type === 'role') {
      setFormData(item ? { name: item.name, description: item.description, permissions: item.permissions || [] } : { name: '', description: '', permissions: [] });
    } else if (type === 'category') {
      setFormData(item ? { name: item.name, code: item.code, type: item.type, color: item.color, is_active: item.is_active } : { name: '', code: '', type: 'material', color: '#6366f1', is_active: true });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalType === 'role') {
        if (editItem) await api.put(`/api/admin/roles/${editItem.id}`, formData);
        else await api.post('/api/admin/roles', formData);
        fetchRoles();
      } else if (modalType === 'category') {
        if (editItem) await api.put(`/api/admin/categories/${editItem.id}`, formData);
        else await api.post('/api/admin/categories', formData);
        fetchCategories();
      }
      setShowModal(false);
    } catch (e) { alert(e.response?.data?.detail || 'Error saving'); }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try {
      if (type === 'role') { await api.delete(`/api/admin/roles/${id}`); fetchRoles(); }
      else if (type === 'category') { await api.delete(`/api/admin/categories/${id}`); fetchCategories(); }
    } catch (e) { alert(e.response?.data?.detail || 'Cannot delete'); }
  };

  const toggleModule = async (moduleId, enabled) => {
    try {
      await api.put(`/api/admin/modules/${moduleId}`, { enabled: !enabled });
      fetchModules();
    } catch (e) { console.error(e); }
  };

  const handleUserRoleChange = async (userId, roleCode) => {
    try {
      await api.put(`/api/admin/users/${userId}/role?role_code=${roleCode}`);
      fetchUsers();
    } catch (e) { alert('Error updating role'); }
  };

  const togglePermission = (permId) => {
    const perms = formData.permissions || [];
    if (perms.includes(permId)) {
      setFormData({ ...formData, permissions: perms.filter(p => p !== permId) });
    } else {
      setFormData({ ...formData, permissions: [...perms, permId] });
    }
  };

  const filteredCategories = selectedCategoryType === 'all' 
    ? categories 
    : categories.filter(c => c.type === selectedCategoryType);

  return (
    <div className="space-y-6" data-testid="admin-tools-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
          <Settings className="w-6 h-6 text-purple-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Admin Tools</h2>
          <p className="text-sm text-gray-500">Manage roles, categories, and system settings</p>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-4">
        {[
          { id: 'roles', label: 'Roles & Permissions', icon: Shield },
          { id: 'users', label: 'User Management', icon: Users },
          { id: 'categories', label: 'Categories', icon: Tag },
          { id: 'modules', label: 'Modules', icon: ToggleLeft },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${activeSection === tab.id ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Roles Section */}
      {activeSection === 'roles' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Roles & Permissions</h3>
            <button onClick={() => openModal('role')} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Role
            </button>
          </div>
          <div className="grid gap-4">
            {roles.map(role => (
              <div key={role.id} className="card">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${role.is_system ? 'bg-purple-100' : 'bg-gray-100'}`}>
                      <Shield className={`w-5 h-5 ${role.is_system ? 'text-purple-600' : 'text-gray-600'}`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 flex items-center gap-2">
                        {role.name}
                        {role.is_system && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">System</span>}
                      </h4>
                      <p className="text-sm text-gray-500">{role.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openModal('role', role)} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg">
                      <Edit className="w-4 h-4" />
                    </button>
                    {!role.is_system && (
                      <button onClick={() => handleDelete('role', role.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {(role.permissions || []).map(perm => (
                    <span key={perm} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{perm}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Users Section */}
      {activeSection === 'users' && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">User Management</h3>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">User</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Current Role</th>
                  <th className="table-header">Security Question</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(user => (
                  <tr key={user.id || user.username} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{user.username}</td>
                    <td className="table-cell text-gray-500">{user.email}</td>
                    <td className="table-cell">
                      <select 
                        className="select-field text-sm py-1 w-28" 
                        value={user.role || 'user'}
                        onChange={(e) => handleUserRoleChange(user.id || user.username, e.target.value)}
                      >
                        {roles.map(r => <option key={r.id} value={r.code}>{r.name}</option>)}
                      </select>
                    </td>
                    <td className="table-cell">
                      {user.has_security_question ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs"><CheckCircle className="w-3 h-3" /> Set</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs"><AlertCircle className="w-3 h-3" /> Not Set</span>
                      )}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openSecurityModal(user)} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg" title="Set Security Question" data-testid={`set-security-${user.username}`}>
                          <Shield className="w-4 h-4" />
                        </button>
                        <button onClick={() => openPasswordModal(user)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Reset Password" data-testid={`reset-password-${user.username}`}>
                          <Key className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Categories Section */}
      {activeSection === 'categories' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <select className="select-field" value={selectedCategoryType} onChange={(e) => setSelectedCategoryType(e.target.value)}>
                {categoryTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <button onClick={() => openModal('category')} className="btn-primary flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Category
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCategories.map(cat => (
              <div key={cat.id} className="card flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: cat.color + '20' }}>
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }}></div>
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{cat.name}</p>
                    <p className="text-xs text-gray-500">{categoryTypes.find(t => t.id === cat.type)?.name || cat.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openModal('category', cat)} className="p-1.5 text-gray-400 hover:text-purple-600 rounded">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete('category', cat.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modules Section */}
      {activeSection === 'modules' && (
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Enable/Disable Modules</h3>
          <p className="text-sm text-gray-500">Choose which modules appear in the sidebar for all users.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {modules.map(mod => (
              <div key={mod.id} className={`card flex items-center justify-between ${!mod.enabled ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${mod.enabled ? 'bg-purple-100' : 'bg-gray-100'}`}>
                    <Clipboard className={`w-5 h-5 ${mod.enabled ? 'text-purple-600' : 'text-gray-400'}`} />
                  </div>
                  <span className="font-medium text-gray-900">{mod.name}</span>
                </div>
                <button
                  onClick={() => toggleModule(mod.id, mod.enabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${mod.enabled ? 'bg-purple-600' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${mod.enabled ? 'translate-x-7' : 'translate-x-1'}`}></div>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? `Edit ${modalType === 'role' ? 'Role' : 'Category'}` : `Add ${modalType === 'role' ? 'Role' : 'Category'}`}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {modalType === 'role' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                <input type="text" className="input-field" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea className="input-field" rows="2" value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {permissions.map(perm => (
                    <label key={perm.id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(formData.permissions || []).includes(perm.id)}
                        onChange={() => togglePermission(perm.id)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm">{perm.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
          {modalType === 'category' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
                <input type="text" className="input-field" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category Type *</label>
                <select className="select-field" value={formData.type || 'material'} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                  {categoryTypes.filter(t => t.id !== 'all').map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={formData.color || '#6366f1'} onChange={(e) => setFormData({ ...formData, color: e.target.value })} className="w-12 h-10 rounded cursor-pointer" />
                  <input type="text" className="input-field flex-1" value={formData.color || '#6366f1'} onChange={(e) => setFormData({ ...formData, color: e.target.value })} />
                </div>
              </div>
            </>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{editItem ? 'Update' : 'Create'}</button>
          </div>
        </form>
      </Modal>

      {/* Security Question Modal */}
      <Modal isOpen={showSecurityModal} onClose={() => setShowSecurityModal(false)} title={`Set Security Question - ${selectedUser?.username}`}>
        <form onSubmit={handleSecuritySubmit} className="space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700 flex items-start gap-2">
            <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <p>Set a security question for this user so they can recover their password if forgotten.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Security Question *</label>
            <select className="select-field" value={securityForm.security_question} onChange={(e) => setSecurityForm({ ...securityForm, security_question: e.target.value })} required data-testid="security-question-select">
              <option value="">Select a question...</option>
              {securityQuestions.map((q, i) => <option key={i} value={q}>{q}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Answer *</label>
            <input type="text" className="input-field" value={securityForm.security_answer} onChange={(e) => setSecurityForm({ ...securityForm, security_answer: e.target.value })} required placeholder="Enter the answer" data-testid="security-answer-input" />
            <p className="text-xs text-gray-500 mt-1">The answer is case-insensitive</p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowSecurityModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Save Security Question</button>
          </div>
        </form>
      </Modal>

      {/* Password Reset Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title={`Reset Password - ${selectedUser?.username}`}>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-700 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <p>This will reset the user's password. Make sure to share the new password with them securely.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password *</label>
            <input type="password" className="input-field" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} required minLength={6} data-testid="new-password-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
            <input type="password" className="input-field" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} required data-testid="confirm-password-input" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={() => setShowPasswordModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Reset Password</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Public Invite Page Component
const PublicInvite = () => {
  const [invite, setInvite] = useState(null);
  const [organization, setOrganization] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', address: '', city: '', state: '', zip_code: '', birthday: '', gender: '', notes: ''
  });

  useEffect(() => {
    const hash = window.location.hash;
    const inviteMatch = hash.match(/#\/invite\/(.+)/);
    const inviteCode = inviteMatch ? inviteMatch[1] : null;
    
    if (inviteCode) {
      fetchInvite(inviteCode);
    } else {
      setError('Invalid invite link');
      setLoading(false);
    }
  }, []);

  const fetchInvite = async (code) => {
    try {
      const r = await axios.get(`${API_URL}/api/public/invite/${code}`);
      setInvite(r.data.invite);
      setOrganization(r.data.organization);
      // Pre-fill form with invite data
      setFormData(prev => ({
        ...prev,
        first_name: r.data.invite.first_name || '',
        last_name: r.data.invite.last_name || '',
        email: r.data.invite.email || ''
      }));
    } catch (e) {
      setError(e.response?.data?.detail || 'Invite not found or has expired');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/public/invite/${invite.code}/accept`, formData);
      setSubmitted(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error submitting registration');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-indigo-800 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-indigo-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invite Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a href="/" className="btn-primary inline-block">Go to Homepage</a>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-indigo-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to {organization.name}!</h2>
          <p className="text-gray-600 mb-6">Your registration has been submitted successfully. We're excited to have you join our community!</p>
          <div className="bg-purple-50 p-4 rounded-lg text-sm text-purple-700">
            <p>The church administrator will be notified of your registration.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-indigo-800">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Church className="w-10 h-10 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{organization.name || 'Our Church'}</h1>
          {organization.address && <p className="text-purple-200">{organization.address}</p>}
        </div>

        {/* Invite Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 text-white">
            <div className="flex items-center gap-3">
              <UserPlus className="w-8 h-8" />
              <div>
                <h2 className="text-xl font-bold">You're Invited!</h2>
                <p className="text-purple-100">Complete your registration to join our community</p>
              </div>
            </div>
          </div>

          {invite.message && (
            <div className="bg-purple-50 p-4 border-b">
              <p className="text-purple-700 italic">"{invite.message}"</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input type="text" className="input-field" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input type="text" className="input-field" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" className="input-field" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" className="input-field" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input type="text" className="input-field" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input type="text" className="input-field" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input type="text" className="input-field" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zip</label>
                <input type="text" className="input-field" value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birthday</label>
                <input type="date" className="input-field" value={formData.birthday} onChange={(e) => setFormData({ ...formData, birthday: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select className="select-field" value={formData.gender} onChange={(e) => setFormData({ ...formData, gender: e.target.value })}>
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Anything you'd like us to know?</label>
              <textarea className="input-field" rows="3" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Prayer requests, how you heard about us, etc." />
            </div>

            <button type="submit" className="btn-primary w-full py-3 flex items-center justify-center gap-2 text-lg">
              <UserPlus className="w-5 h-5" /> Complete Registration
            </button>
          </form>

          <div className="bg-gray-50 p-4 text-center text-sm text-gray-500">
            <p>Invite expires: {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : 'N/A'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Owner Business Card Component
const OwnerBusinessCard = () => {
  const [copied, setCopied] = useState(false);
  
  const ownerInfo = {
    name: "Bishop Derrick A. Raybon",
    title: "Founding Pastor",
    company: "Christ The King Church And Ministry",
    tagline: "Holy Light Ministry Soul Center Headquarters",
    email: "derrickraybon1981@gmail.com",
    phone: "(770) 638-3444",
    website: "https://churccrm.info",
    address: "805 S 1st St., Slaton, TX 79364",
    ministry: "Holy Light Ministry Soul Center Headquarters",
    photo: "https://customer-assets.emergentagent.com/job_holy-light-crm/artifacts/n2202x01_Untitled%20design%20%282%29.png"
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadVCard = () => {
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${ownerInfo.name}
TITLE:${ownerInfo.title}
ORG:${ownerInfo.company}
EMAIL:${ownerInfo.email}
TEL:${ownerInfo.phone}
URL:${ownerInfo.website}
ADR:;;${ownerInfo.address};;;
NOTE:${ownerInfo.tagline}
END:VCARD`;
    
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Derrick_Raybon_ChurchCRM.vcf';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-500" />
      </div>
      
      <div className="relative z-10 w-full max-w-md">
        {/* Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 p-8 text-center relative">
            <div className="absolute inset-0 bg-black/10" />
            <div className="relative">
              {/* Profile Image */}
              <div className="w-28 h-28 mx-auto mb-4 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white/30 flex items-center justify-center shadow-xl overflow-hidden">
                <img src={ownerInfo.photo} alt={ownerInfo.name} className="w-full h-full object-cover" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">{ownerInfo.name}</h1>
              <p className="text-purple-200 font-medium">{ownerInfo.title}</p>
              <div className="mt-3 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full">
                <Church className="w-4 h-4 text-white" />
                <span className="text-white text-sm font-medium">{ownerInfo.company}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-center text-purple-200/80 text-sm italic">"{ownerInfo.tagline}"</p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href={`mailto:${ownerInfo.email}`} className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all group">
                <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-purple-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-purple-300/60 uppercase tracking-wider">Email</p>
                  <p className="text-white truncate group-hover:text-purple-300 transition-colors">{ownerInfo.email}</p>
                </div>
              </a>

              <a href={`tel:${ownerInfo.phone}`} className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all group">
                <div className="w-10 h-10 rounded-full bg-green-500/30 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-green-300" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-purple-300/60 uppercase tracking-wider">Phone</p>
                  <p className="text-white group-hover:text-green-300 transition-colors">{ownerInfo.phone}</p>
                </div>
              </a>

              <a href={ownerInfo.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all group">
                <div className="w-10 h-10 rounded-full bg-blue-500/30 flex items-center justify-center">
                  <ExternalLink className="w-5 h-5 text-blue-300" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-purple-300/60 uppercase tracking-wider">Website</p>
                  <p className="text-white group-hover:text-blue-300 transition-colors">churccrm.info</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-3 rounded-xl bg-white/5">
                <div className="w-10 h-10 rounded-full bg-red-500/30 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-red-300" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-purple-300/60 uppercase tracking-wider">Location</p>
                  <p className="text-white text-sm">{ownerInfo.address}</p>
                </div>
              </div>
            </div>

            {/* Ministry Badge */}
            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl p-4 border border-amber-500/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/30 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-amber-300" />
                </div>
                <div>
                  <p className="text-xs text-amber-300/60 uppercase tracking-wider">Ministry</p>
                  <p className="text-amber-100 font-medium text-sm">{ownerInfo.ministry}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <button 
                onClick={downloadVCard}
                className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-xl font-medium transition-all shadow-lg hover:shadow-purple-500/25"
              >
                <Download className="w-4 h-4" />
                Save Contact
              </button>
              <button 
                onClick={() => copyToClipboard(ownerInfo.website)}
                className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white py-3 px-4 rounded-xl font-medium transition-all border border-white/20"
              >
                {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Share'}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-white/5 border-t border-white/10 text-center">
            <p className="text-purple-300/60 text-xs">
              Powered by <span className="text-purple-300">ChurchCRM</span> • Built with ❤️ for Churches
            </p>
          </div>
        </div>

        {/* QR Code hint */}
        <div className="text-center mt-6">
          <p className="text-purple-300/40 text-sm">Scan • Save • Connect</p>
        </div>
      </div>
    </div>
  );
};

// Public Careers Page Component
const PublicCareers = () => {
  const [jobs, setJobs] = useState([]);
  const [organization, setOrganization] = useState({});
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicationForm, setApplicationForm] = useState({ first_name: '', last_name: '', email: '', phone: '', cover_letter: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchJobs();
    // Check if URL has job ID
    const hash = window.location.hash;
    const jobMatch = hash.match(/#\/careers\/(.+)/);
    if (jobMatch) {
      fetchSingleJob(jobMatch[1]);
    }
  }, []);

  const fetchJobs = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/public/careers`);
      setJobs(r.data.job_postings);
      setOrganization(r.data.organization || {});
    } catch (e) { console.error(e); }
  };

  const fetchSingleJob = async (jobId) => {
    try {
      const r = await axios.get(`${API_URL}/api/public/careers/${jobId}`);
      setSelectedJob(r.data.posting);
      setOrganization(r.data.organization || {});
      setShowApplicationForm(true);
    } catch (e) { console.error(e); }
  };

  const handleApply = (job) => {
    setSelectedJob(job);
    setShowApplicationForm(true);
    setSubmitted(false);
    setApplicationForm({ first_name: '', last_name: '', email: '', phone: '', cover_letter: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/careers/${selectedJob.id}/apply`, {
        first_name: applicationForm.first_name,
        last_name: applicationForm.last_name,
        email: applicationForm.email,
        phone: applicationForm.phone,
        cover_letter: applicationForm.cover_letter
      });
      setSubmitted(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Error submitting application');
    }
    setSubmitting(false);
  };

  const copyJobLink = (jobId) => {
    const link = `${window.location.origin}/#/careers/${jobId}`;
    navigator.clipboard.writeText(link);
    alert('Link copied to clipboard!');
  };

  const orgName = organization?.organization_name || 'Our Organization';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Church className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{orgName}</h1>
              <p className="text-purple-200 text-sm">Career Opportunities</p>
            </div>
          </div>
          {organization?.address && (
            <div className="text-right text-purple-200 text-sm hidden md:block">
              <p>{organization.address}</p>
              <p>{organization.city}, {organization.state} {organization.zip_code}</p>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {!showApplicationForm ? (
          <>
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">Join Our Team</h2>
              <p className="text-purple-200 text-lg max-w-2xl mx-auto">
                We're looking for passionate individuals to join our ministry. Explore our open positions below.
              </p>
            </div>

            {jobs.length === 0 ? (
              <div className="text-center py-16 bg-white/10 rounded-2xl backdrop-blur-sm">
                <Briefcase className="w-16 h-16 text-purple-300 mx-auto mb-4" />
                <h3 className="text-xl text-white font-semibold mb-2">No Open Positions</h3>
                <p className="text-purple-200">Check back later for new opportunities!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {jobs.map((job) => (
                  <div key={job.id} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                          <p className="text-purple-600 font-medium">{job.department}</p>
                        </div>
                        <span className={`badge ${job.employment_type === 'full_time' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                          {job.employment_type?.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-4 line-clamp-3">{job.description}</p>
                      {job.salary_range && (
                        <p className="text-sm text-gray-500 mb-4"><DollarSign className="w-4 h-4 inline" /> {job.salary_range}</p>
                      )}
                      <div className="flex gap-3">
                        <button onClick={() => handleApply(job)} className="btn-primary flex-1 flex items-center justify-center gap-2">
                          <UserPlus className="w-4 h-4" /> Apply Now
                        </button>
                        <button onClick={() => copyJobLink(job.id)} className="btn-secondary p-2" title="Copy link">
                          <Link className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="max-w-2xl mx-auto">
            <button onClick={() => { setShowApplicationForm(false); setSelectedJob(null); }} className="text-purple-200 hover:text-white mb-6 flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" /> Back to all positions
            </button>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {submitted ? (
                <div className="p-12 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
                  <p className="text-gray-600 mb-6">Thank you for your interest in joining {orgName}. We will review your application and contact you soon.</p>
                  <button onClick={() => { setShowApplicationForm(false); setSelectedJob(null); setSubmitted(false); }} className="btn-primary">
                    View Other Positions
                  </button>
                </div>
              ) : (
                <>
                  <div className="bg-purple-600 p-6 text-white">
                    <h3 className="text-2xl font-bold">{selectedJob?.title}</h3>
                    <p className="text-purple-200">{selectedJob?.department} • {selectedJob?.employment_type?.replace('_', ' ')}</p>
                  </div>
                  <div className="p-6">
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 mb-2">About this position</h4>
                      <p className="text-gray-600">{selectedJob?.description}</p>
                      {selectedJob?.requirements && (
                        <div className="mt-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Requirements</h4>
                          <p className="text-gray-600">{selectedJob?.requirements}</p>
                        </div>
                      )}
                      {selectedJob?.salary_range && (
                        <p className="mt-4 text-gray-600"><strong>Salary Range:</strong> {selectedJob?.salary_range}</p>
                      )}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4 border-t pt-6">
                      <h4 className="font-semibold text-gray-900">Apply for this position</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                          <input type="text" required className="input-field" value={applicationForm.first_name} onChange={(e) => setApplicationForm({...applicationForm, first_name: e.target.value})} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                          <input type="text" required className="input-field" value={applicationForm.last_name} onChange={(e) => setApplicationForm({...applicationForm, last_name: e.target.value})} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                          <input type="email" required className="input-field" value={applicationForm.email} onChange={(e) => setApplicationForm({...applicationForm, email: e.target.value})} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                          <input type="tel" required className="input-field" value={applicationForm.phone} onChange={(e) => setApplicationForm({...applicationForm, phone: e.target.value})} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter</label>
                        <textarea rows="4" className="input-field" placeholder="Tell us why you'd be a great fit..." value={applicationForm.cover_letter} onChange={(e) => setApplicationForm({...applicationForm, cover_letter: e.target.value})} />
                      </div>
                      <button type="submit" disabled={submitting} className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                        {submitting ? <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Submitting...</> : <><UserPlus className="w-4 h-4" /> Submit Application</>}
                      </button>
                    </form>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>

      <footer className="bg-black/20 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-purple-200 text-sm">
          <p>&copy; {new Date().getFullYear()} {orgName}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// Public Form Fill Component for HR documents
const PublicFormFill = () => {
  const [formData, setFormData] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [signatureData, setSignatureData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Get token from URL
  const getToken = () => {
    const hash = window.location.hash;
    const match = hash.match(/#\/form\/([^/]+)/);
    return match ? match[1] : null;
  };
  
  const token = getToken();
  
  useEffect(() => {
    if (token) {
      fetchFormData();
    } else {
      setError('Invalid form link');
      setLoading(false);
    }
  }, [token]);
  
  const fetchFormData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/form/${token}`);
      if (response.data.completed) {
        setError('This form has already been completed.');
        setSuccess(true);
      } else {
        setFormData(response.data);
        // Pre-fill form values
        const prefilled = {};
        response.data.form_fields.forEach(field => {
          if (response.data.prefill_data[field.name]) {
            prefilled[field.name] = response.data.prefill_data[field.name];
          } else if (field.type === 'date' && field.name === 'date') {
            prefilled[field.name] = new Date().toISOString().split('T')[0];
          } else {
            prefilled[field.name] = field.type === 'checkbox' ? false : '';
          }
        });
        setFormValues(prefilled);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Form not found or link expired');
    } finally {
      setLoading(false);
    }
  };
  
  // Signature canvas functions
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };
  
  useEffect(() => {
    if (formData && canvasRef.current) {
      initCanvas();
    }
  }, [formData]);
  
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };
  
  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoords(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCanvasCoords(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };
  
  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };
  
  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };
  
  const handleInputChange = (name, value) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = formData.form_fields
      .filter(f => f.required && f.type !== 'signature' && !formValues[f.name])
      .map(f => f.label);
    
    if (missingFields.length > 0) {
      alert(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    // Check signature
    const hasSignatureField = formData.form_fields.some(f => f.type === 'signature' && f.required);
    if (hasSignatureField && !signatureData) {
      alert('Please provide your signature');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/public/form/${token}/submit`, {
        form_data: formValues,
        signature_data: signatureData
      });
      setSuccess(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error submitting form');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }
  
  if (error && !success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Form Unavailable</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Form Submitted!</h2>
          <p className="text-gray-600 mb-4">Thank you for completing this form. HR has been notified and will process your submission.</p>
          <p className="text-sm text-gray-500">You can close this window now.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white rounded-t-2xl p-6 text-center">
          <Church className="w-12 h-12 mx-auto mb-3" />
          <h1 className="text-xl font-bold">{formData.organization?.name}</h1>
          <p className="text-purple-200 text-sm mt-1">{formData.organization?.address}</p>
        </div>
        
        {/* Document Info */}
        <div className="bg-purple-700 text-white px-6 py-4">
          <h2 className="text-lg font-semibold">{formData.document_name}</h2>
          {formData.employee?.name && (
            <p className="text-purple-200 text-sm">For: {formData.employee.name} {formData.employee.position && `• ${formData.employee.position}`}</p>
          )}
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-b-2xl shadow-xl">
          <div className="p-6 space-y-5">
            {formData.form_fields.map((field, index) => (
              <div key={field.name} className="space-y-1">
                {field.type !== 'signature' && (
                  <>
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    
                    {field.type === 'text' && (
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        value={formValues[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        maxLength={field.maxLength}
                        required={field.required}
                      />
                    )}
                    
                    {field.type === 'password' && (
                      <input
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                        value={formValues[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                    )}
                    
                    {field.type === 'email' && (
                      <input
                        type="email"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        value={formValues[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        required={field.required}
                      />
                    )}
                    
                    {field.type === 'tel' && (
                      <input
                        type="tel"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        value={formValues[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        required={field.required}
                      />
                    )}
                    
                    {field.type === 'number' && (
                      <input
                        type="number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        value={formValues[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        required={field.required}
                      />
                    )}
                    
                    {field.type === 'date' && (
                      <input
                        type="date"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        value={formValues[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        required={field.required}
                      />
                    )}
                    
                    {field.type === 'select' && (
                      <select
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        value={formValues[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        required={field.required}
                      >
                        <option value="">Select...</option>
                        {field.options?.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    )}
                    
                    {field.type === 'textarea' && (
                      <textarea
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        rows="3"
                        value={formValues[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        required={field.required}
                      />
                    )}
                    
                    {field.type === 'checkbox' && (
                      <label className="flex items-start gap-3 cursor-pointer p-3 bg-gray-50 rounded-lg">
                        <input
                          type="checkbox"
                          className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-0.5"
                          checked={formValues[field.name] || false}
                          onChange={(e) => handleInputChange(field.name, e.target.checked)}
                          required={field.required}
                        />
                        <span className="text-gray-700">{field.label}</span>
                      </label>
                    )}
                  </>
                )}
                
                {field.type === 'signature' && (
                  <div className="border-t pt-5 mt-5">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <p className="text-sm text-gray-500 mb-3">Draw your signature below using your mouse or finger</p>
                    <div className="border-2 border-gray-300 rounded-lg p-2 bg-white">
                      <canvas
                        ref={canvasRef}
                        width={500}
                        height={150}
                        className="w-full h-32 cursor-crosshair touch-none"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="mt-2 text-sm text-purple-600 hover:text-purple-800"
                    >
                      Clear Signature
                    </button>
                    {signatureData && (
                      <p className="text-sm text-green-600 mt-1">✓ Signature captured</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Submit Button */}
          <div className="border-t p-6">
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-4 rounded-xl font-semibold text-lg hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Submit Form
                </>
              )}
            </button>
            <p className="text-center text-xs text-gray-500 mt-3">
              By submitting this form, you certify that all information provided is accurate.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

// Public Giving Impact Page - Shows where tithes and offerings go
const PublicGivingImpact = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const r = await api.get('/api/public/giving-impact');
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-blue-500';
    if (percentage >= 25) return 'bg-yellow-500';
    return 'bg-purple-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 flex items-center justify-center">
        <div className="text-center">
          <Church className="w-16 h-16 text-white mx-auto mb-4 animate-pulse" />
          <p className="text-white/80">Loading...</p>
        </div>
      </div>
    );
  }

  const givingLink = `${window.location.origin}/#/giving`;
  
  const handleGiveNowClick = () => {
    // Navigate and force reload to ensure proper routing
    window.location.href = givingLink;
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
              <Church className="w-7 h-7 text-purple-600" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">{data?.organization || 'Holy Light Ministry'}</h1>
              <p className="text-white/70 text-sm">Where Your Giving Goes</p>
            </div>
          </div>
          <button 
            onClick={handleGiveNowClick}
            className="bg-white text-purple-700 px-6 py-2 rounded-full font-semibold hover:bg-purple-50 transition-all flex items-center gap-2 shadow-lg"
          >
            <Heart className="w-4 h-4" /> Give Now
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Your Generosity Makes a Difference</h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Every tithe and offering helps us serve our community, support our ministries, and share God's love.
          </p>
        </div>

        {/* Stats */}
        {data?.stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <DollarSign className="w-10 h-10 text-green-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">${data.stats.total_giving_this_year?.toLocaleString()}</p>
              <p className="text-white/70">Total Giving in {data.stats.year}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <Users className="w-10 h-10 text-blue-400 mx-auto mb-2" />
              <p className="text-3xl font-bold text-white">{data.stats.donors_count || 0}</p>
              <p className="text-white/70">Faithful Givers</p>
            </div>
          </div>
        )}

        {/* Giving Goals with Progress */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <TrendingUp className="w-6 h-6" /> Our Giving Goals
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data?.goals?.map((goal) => {
              const percentage = Math.min(100, Math.round((goal.current_amount / goal.target_amount) * 100));
              return (
                <div key={goal.id} className="bg-white rounded-2xl p-6 shadow-xl">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{goal.name}</h4>
                      <p className="text-gray-600 text-sm mt-1">{goal.description}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${goal.category === 'benevolence' ? 'bg-pink-100' : 'bg-purple-100'}`}>
                      {goal.category === 'benevolence' ? (
                        <Heart className="w-6 h-6 text-pink-600" />
                      ) : (
                        <Church className="w-6 h-6 text-purple-600" />
                      )}
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">${goal.current_amount?.toLocaleString()} raised</span>
                      <span className="font-semibold text-gray-900">{percentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-1000 ${getProgressColor(percentage)}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <p className="text-right text-sm text-gray-500 mt-1">Goal: ${goal.target_amount?.toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Fund Allocation */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Tithes Allocation */}
          <div className="bg-white rounded-2xl p-6 shadow-xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Church className="w-5 h-5 text-purple-600" /> Where Your Tithes Go
            </h3>
            <div className="space-y-4">
              {data?.allocations?.tithes?.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium text-gray-800">{item.name}</span>
                    <span className="text-purple-600 font-bold">{item.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-full rounded-full"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Benevolence Allocation */}
          <div className="bg-white rounded-2xl p-6 shadow-xl">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-600" /> Where Benevolence Helps
            </h3>
            <div className="space-y-4">
              {data?.allocations?.benevolence?.map((item, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium text-gray-800">{item.name}</span>
                    <span className="text-pink-600 font-bold">{item.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div 
                      className="bg-pink-500 h-full rounded-full"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-white rounded-2xl p-8 shadow-xl text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready to Make a Difference?</h3>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            Your generous giving helps us continue serving our community, supporting families in need, and spreading the Gospel.
          </p>
          <button 
            onClick={handleGiveNowClick}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg"
          >
            <Gift className="w-5 h-5" /> Give Online Now
          </button>
          <p className="text-sm text-gray-500 mt-4">Secure online giving powered by Stripe</p>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pb-8">
          <p className="text-white/60 text-sm">{data?.organization}</p>
          <p className="text-white/40 text-xs mt-1">Thank you for your faithfulness</p>
        </div>
      </div>
    </div>
  );
};

// Standalone Kiosk Component for direct access
const StandaloneKiosk = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => { fetchTodaysEvents(); }, []);

  const fetchTodaysEvents = async () => { try { const r = await api.get('/api/kiosk/events/today'); setEvents(r.data.events); if (r.data.events.length > 0) setSelectedEvent(r.data.events[0]); } catch (e) { console.error(e); } };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (term.length >= 2) { try { const r = await api.get(`/api/kiosk/search?query=${term}`); setSearchResults(r.data.results); } catch (e) { console.error(e); } }
    else setSearchResults([]);
  };

  const handleCheckin = async (memberId) => {
    if (!selectedEvent) { alert('No event selected'); return; }
    try { await api.post('/api/kiosk/checkins', { member_id: memberId, event_id: selectedEvent.id, checkin_time: new Date().toISOString() }); setSearchTerm(''); setSearchResults([]); alert('✓ Check-in successful!'); } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 p-4 md:p-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Church className="w-16 h-16 text-white mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Welcome!</h1>
          <p className="text-purple-200">Please check in</p>
        </div>
        {selectedEvent && <div className="bg-white/20 backdrop-blur rounded-xl p-4 mb-6 text-center"><p className="text-white text-lg">{selectedEvent.name}</p></div>}
        {events.length > 1 && <div className="mb-4"><select className="w-full p-3 rounded-xl bg-white/20 text-white border-0" value={selectedEvent?.id || ''} onChange={(e) => setSelectedEvent(events.find(ev => ev.id === e.target.value))}>{events.map(ev => <option key={ev.id} value={ev.id} className="text-gray-900">{ev.name}</option>)}</select></div>}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
            <input type="text" placeholder="Search by name or phone..." className="w-full pl-14 pr-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-purple-500" value={searchTerm} onChange={(e) => handleSearch(e.target.value)} autoFocus />
          </div>
          {searchResults.length > 0 && <div className="space-y-3">{searchResults.map((m) => (
            <button key={m.id} onClick={() => handleCheckin(m.id)} className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 active:bg-purple-100 transition-all">
              {m.photo ? <img src={m.photo} alt="" className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover" /> : <div className="w-14 h-14 md:w-16 md:h-16 bg-purple-100 rounded-full flex items-center justify-center"><User className="w-7 h-7 md:w-8 md:h-8 text-purple-600" /></div>}
              <div className="text-left flex-1"><p className="text-lg md:text-xl font-semibold">{m.first_name} {m.last_name}</p><p className="text-gray-500">{m.phone}</p></div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </button>
          ))}</div>}
          {searchTerm.length >= 2 && searchResults.length === 0 && <div className="text-center py-8 text-gray-500">No members found</div>}
        </div>
      </div>
    </div>
  );
};

// Main App
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [quickAction, setQuickAction] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // Check if accessing special public routes
  const isKioskRoute = window.location.hash === '#/kiosk' || window.location.pathname === '/kiosk';
  const isCareersRoute = window.location.hash.startsWith('#/careers') || window.location.pathname.startsWith('/careers');
  const isInviteRoute = window.location.hash.startsWith('#/invite') || window.location.pathname.startsWith('/invite');
  const isOwnerRoute = window.location.hash === '#/owner' || window.location.pathname === '/owner';
  const isFormRoute = window.location.hash.startsWith('#/form/') || window.location.pathname.startsWith('/form/');
  const isVerifyRoute = window.location.hash.startsWith('#/verify/') || window.location.pathname.startsWith('/verify/');
  const isGivingImpactRoute = window.location.hash === '#/giving-impact' || window.location.pathname === '/giving-impact';
  const isPublicRoute = isKioskRoute || isCareersRoute || isInviteRoute || isOwnerRoute || isFormRoute || isVerifyRoute || isGivingImpactRoute;
  // Check if returning from giving
  const isGivingReturn = window.location.pathname.includes('/giving/success');

  useEffect(() => { if (!isPublicRoute) checkAuth(); else setLoading(false); }, [isPublicRoute]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) { 
      try { 
        const r = await api.get('/api/auth/me'); 
        setUser(r.data); 
        setIsAuthenticated(true);
        // Fetch detailed permissions
        try {
          const permRes = await api.get('/api/auth/permissions');
          setPermissions(permRes.data);
        } catch (e) { 
          // Default permissions if endpoint fails
          setPermissions({ is_admin: r.data.role === 'admin', can_edit_own_profile: true });
        }
        // Check if tour should be shown
        try {
          const tourStatus = await api.get('/api/user/tour-status');
          if (!tourStatus.data.tour_completed) setShowTour(true);
        } catch (e) { console.log('Tour status check failed'); }
      } catch (e) { localStorage.removeItem('token'); } 
    }
    setLoading(false);
    // If returning from giving, go to giving tab
    if (isGivingReturn) setActiveTab('giving');
  };

  const handleLogin = () => { checkAuth(); };
  const handleLogout = () => { localStorage.removeItem('token'); setIsAuthenticated(false); setUser(null); setPermissions(null); };

  // Scroll page to top when active tab changes
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTop = 0;
    }
  }, [activeTab]);

  // Helper function to check if user can edit a module
  const canEdit = (module = null) => {
    if (!permissions) return false;
    if (permissions.is_admin) return true;
    if (module && permissions.modules && permissions.modules[module]) {
      return permissions.modules[module].edit;
    }
    return false;
  };

  // Helper function to check if user can delete
  const canDelete = (module = null) => {
    if (!permissions) return false;
    if (permissions.is_admin) return true;
    if (module && permissions.modules && permissions.modules[module]) {
      return permissions.modules[module].delete;
    }
    return false;
  };

  const completeTour = async () => {
    try { await api.post('/api/user/complete-tour'); } catch (e) { console.log('Failed to save tour status'); }
    setShowTour(false);
  };

  const handleQuickAction = (action) => {
    setQuickAction(action);
    switch (action) {
      case 'addMember': setActiveTab('members'); break;
      case 'addDonation': setActiveTab('donations'); break;
      case 'processPayroll': setActiveTab('payroll'); break;
      case 'openKiosk': setActiveTab('kiosk'); break;
      case 'onlineGiving': setActiveTab('giving'); break;
      default: break;
    }
    setTimeout(() => setQuickAction(null), 100);
  };

  const getPageTitle = () => {
    const titles = { dashboard: 'Dashboard', members: 'Members', families: 'Families', employees: 'Employees', hr: 'HR & Hiring', payroll: 'Payroll & W2', attendance: 'Attendance', donations: 'Donations', giving: 'Online Giving', accounting: 'Accounting', kiosk: 'Kiosk', reports: 'Reports' };
    return titles[activeTab] || 'Dashboard';
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <Dashboard setActiveTab={setActiveTab} onQuickAction={handleQuickAction} />;
      case 'mypaperwork': return <MyPaperwork />;
      case 'businesscards': return <DigitalBusinessCards />;
      case 'churchdocs': return <ChurchDocuments />;
      case 'people': return <Members autoOpenModal={quickAction === 'addMember'} />;
      case 'members': return <Members autoOpenModal={quickAction === 'addMember'} />;
      case 'families': return <Families />;
      case 'groups': return <Groups />;
      case 'calendar': return <CalendarView />;
      case 'materials': return <ChurchMaterials />;
      case 'newsletter': return <Newsletter />;
      case 'notifications': return <NotificationsManager />;
      case 'tithe-reminders': return <TitheReminders />;
      case 'tasks': return <TaskAssignments />;
      case 'loans': return <ChurchLoans />;
      case 'employees': return <Employees />;
      case 'hr': return <HRHiring />;
      case 'payroll': return <Payroll autoOpenModal={quickAction === 'processPayroll'} />;
      case 'contractors': return <Contractors />;
      case 'attendance': return <Attendance />;
      case 'donations': return <Donations autoOpenModal={quickAction === 'addDonation'} />;
      case 'giving': return <OnlineGiving />;
      case 'accounting': return <Accounting />;
      case 'documents': return <HRDocuments />;
      case 'kiosk': return <Kiosk autoLaunchKiosk={quickAction === 'openKiosk'} />;
      case 'admin': return <AdminTools />;
      default: return <Dashboard setActiveTab={setActiveTab} onQuickAction={handleQuickAction} />;
    }
  };

  // Show standalone kiosk if accessing via kiosk route
  if (isKioskRoute) return <StandaloneKiosk />;
  
  // Show public careers page
  if (isCareersRoute) return <PublicCareers />;
  
  // Show public invite page
  if (isInviteRoute) return <PublicInvite />;
  
  // Show owner business card
  if (isOwnerRoute) return <OwnerBusinessCard />;
  
  // Show public form filling page
  if (isFormRoute) return <PublicFormFill />;
  
  // Show ID card verification page
  if (isVerifyRoute) return <PublicVerifyCard />;
  
  // Show public giving impact page
  if (isGivingImpactRoute) return <PublicGivingImpact />;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><div className="text-center"><div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse"><Church className="w-8 h-8 text-purple-600" /></div><p className="text-gray-500">Loading...</p></div></div>;

  if (!isAuthenticated) return <Login onLogin={handleLogin} />;

  // Mobile navigation items - key sections for members
  const mobileNavItems = [
    { id: 'dashboard', label: 'Home', icon: Home },
    { id: 'calendar', label: 'Events', icon: Calendar },
    { id: 'giving', label: 'Give', icon: Heart },
    { id: 'materials', label: 'Resources', icon: BookOpen },
    { id: 'more', label: 'More', icon: Menu },
  ];

  return (
    <AuthContext.Provider value={{ user, logout: handleLogout, permissions, canEdit, canDelete }}>
      {showTour && <OnboardingTour onComplete={completeTour} />}
      <div className="flex h-screen bg-gray-100">
        {/* Desktop Sidebar - hidden on mobile */}
        <div className="hidden md:block">
          <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        </div>
        
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header setSidebarOpen={setSidebarOpen} title={getPageTitle()} setActiveTab={setActiveTab} />
          <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-20 md:pb-6">{renderContent()}</main>
        </div>
        
        {/* Mobile Bottom Navigation */}
        <nav className="mobile-nav md:hidden">
          {mobileNavItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.id === 'more') {
                  setShowMoreMenu(!showMoreMenu);
                } else {
                  setActiveTab(item.id);
                  setShowMoreMenu(false);
                }
              }}
              className={`mobile-nav-item ${activeTab === item.id ? 'active' : ''}`}
              data-testid={`mobile-nav-${item.id}`}
            >
              <item.icon className="w-6 h-6" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        
        {/* Mobile "More" Menu Overlay */}
        {showMoreMenu && (
          <div className="fixed inset-0 z-40 md:hidden" onClick={() => setShowMoreMenu(false)}>
            <div className="absolute inset-0 bg-black bg-opacity-50" />
            <div className="absolute bottom-16 left-0 right-0 bg-white rounded-t-2xl p-4 pb-8 animate-slideUp" onClick={(e) => e.stopPropagation()}>
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-4">More Options</h3>
              <div className="grid grid-cols-4 gap-4">
                {[
                  { id: 'people', label: 'People', icon: Users },
                  { id: 'families', label: 'Families', icon: Building },
                  { id: 'groups', label: 'Groups', icon: UsersRound },
                  { id: 'donations', label: 'Donations', icon: Heart },
                  { id: 'tasks', label: 'Tasks', icon: ListTodo },
                  { id: 'attendance', label: 'Check-in', icon: UserCheck },
                  ...(permissions?.is_admin ? [
                    { id: 'payroll', label: 'Payroll', icon: DollarSign },
                    { id: 'accounting', label: 'Accounting', icon: Calculator },
                    { id: 'hr', label: 'HR', icon: Briefcase },
                    { id: 'admin', label: 'Admin', icon: Settings },
                  ] : []),
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      setShowMoreMenu(false);
                    }}
                    className="flex flex-col items-center p-3 rounded-xl hover:bg-purple-50 text-gray-600"
                  >
                    <item.icon className="w-6 h-6 mb-1" />
                    <span className="text-xs">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </AuthContext.Provider>
  );
}

export default App;
